// ============================================
// KLY GROUPE - ADMIN SAV INDUSTRIEL V2 PRO
// Version connectée au backend réel - Améliorée
// ============================================

import React, { useState, useEffect, useMemo, createContext, useContext, useRef, useCallback } from 'react';
// Image générée (Figma AI, 07/10/2026) : installateur, pompe à chaleur, jardin ensoleillé.
import photoAccueil from './assets/accueil-jardin.jpg';
import {
  LayoutDashboard, Ticket as TicketIcon, Users, Settings, Bell, Search, Plus, Eye, Edit3,
  Trash2, CheckCircle, Clock, AlertTriangle, ChevronRight, ChevronDown,
  Calendar, Wrench, FileText, BarChart3,
  Building2, Phone, Mail, User, LogOut, Menu, X, RefreshCw, Download, Upload,
  MessageSquare, Send, Paperclip, Zap, Filter,
  Timer, ArrowUpRight, Image,
  Shield, Cog, Save,
  Lock, Eye as EyeIcon, EyeOff, UserPlus, Sparkles, Brain,
  File, FolderOpen, History,
  Workflow, AlertCircle, Activity, Target, Maximize2, Minimize2,
  Headphones, ExternalLink,
  Loader2, UserCircle, UserCog, ChevronLeft, Hash, GitMerge, Link2, MailX, Archive, ArchiveRestore } from 'lucide-react';

// Import des services API réels
import { AdminApi, TokenStorage, ApiError, type DossierRecherche, type ResultatRecherche } from './services/api';
import { adminSocketService } from './services/socket';
import { changerMonMotDePasse } from './features/auth/changementMotDePasse';
import {
  Ticket, TicketMessage, User as UserType, TicketStatus, TicketPriority,
  UserRole, Notification as NotificationType, TicketStats, Attachment, Brand,
  TicketHistory, UpdateTicketPayload
} from './types';
import { estAssignable, agentsPourAutoAssignation, repartirSurLeMoinsCharge, estCompteTechnique, ROLE_COMPTE_TECHNIQUE } from './features/tickets/affectation';
import { libelleAction, decrireChangement } from './features/tickets/historique';
import { lireBrouillon, ecrireBrouillon, effacerBrouillon } from './features/tickets/brouillon';
import AssistantIAPanel, { prechargerAssistantIA } from './features/ia/AssistantIAPanel';
import { estMessageAssistant, libellePauseAssistant } from './features/ia/libelles';
import SlaSettingsTab from './features/settings/SlaSettingsTab';
import GestionReponsesTypes from './features/tickets/GestionReponsesTypes';
import { getStatusRail,
  formatDateTime, formatTime, formatFileSize, getInitials,
  getPriorityColor, COULEUR_STATUT, COULEUR_PRIORITE
} from './shared/format';
import {
  formatDate as formatDateParis, formatDateHeure, formatJour, jourParis, decalerJour, PERIODES_PREDEFINIES,
} from './shared/dates';
import {
  STATUS_LABELS, PRIORITY_LABELS, ISSUE_TYPE_LABELS, ROLE_CONFIG
} from './shared/labels';
import {
  TRIGGER_LABELS, ACTION_LABELS, CATALOGUE_PAR_DEFAUT, lireCatalogue, libelleCatalogue, regleHorsCatalogue, modelesDisponibles,
  type CatalogueAutomatisation, type ModeleAutomatisation
} from './features/automation/constants';
import {
  contexteDepuisDossier, substituerVariables, detecterCommandeSlash, filtrerReponsesTypes, insererReponse,
  type ReponseType, type CommandeSlash
} from './features/tickets/reponsesTypes';
import {
  FILTRES_PAR_DEFAUT, TRIS_DOSSIERS, construireFiltresListe, compterFiltresActifs,
  type FiltresListe, type TriDossiers
} from './features/tickets/listeDossiers';
import { chargerNonAssignes, chargerDerniersDossiers, useTousLesDossiers, libelleNombre } from './features/tickets/chargementDossiers';
import {
  DELAI_REGROUPEMENT_MS, creerRegroupeur, decisionDossierModifie, creerLimiteur, DELAI_NON_ASSIGNES_MS, ajouterMessageSansDoublon, messageDepuisSocket, fusionnerChampDossier
} from './features/temps-reel/tempsReel';
import { transfererDossier } from './features/tickets/transfertFusion';
import FicheEquipement from './features/tickets/FicheEquipement';
import FusionDossierModal from './features/tickets/FusionDossierModal';
import NouveauDossierModal from './features/tickets/NouveauDossierModal';
import RattacherClientModal from './features/tickets/RattacherClientModal';
import AutresClientsBoutique from './features/tickets/AutresClientsBoutique';
import { BadgeSpamPossible, BandeauSpamPossible } from './features/tickets/SpamPossible';
import { BadgeDomaineAConfirmer, BandeauDomaineAConfirmer, MentionDomaineConfirme } from './features/tickets/RattachementDomaine';
import { BoutonHorsSav } from './features/tickets/BoutonHorsSav';
import { FenetreArchivage, FenetreDesarchivage } from './features/tickets/Archivage';
import ArchivesView from './features/tickets/ArchivesView';
import { CartesGestionDossier } from './features/tickets/CartesGestion';
import { LienEmailComplet } from './features/tickets/EmailComplet';
import {
  AVIS_DESARCHIVAGE, CHARGE_DESARCHIVAGE, VUE_ARCHIVES, avisArchivage, chargeArchivage, type EtatArchives,
} from './features/tickets/archives';
import { sansCompteClient } from './features/tickets/rattachement';
import { compteDossiersOuverts, estEncadrement } from './features/tickets/visibilite';
import { surRepriseApresMotDePasse, oublierChangementMotDePasse, CODE_MOT_DE_PASSE_A_CHANGER } from './features/auth/motDePasseObligatoire';
import { Link, Routes, Route, useLocation, useNavigate, matchPath } from 'react-router-dom';
import PageDossier, { toucheVisePageDossier } from './features/tickets/PageDossier';
import { defilementARetablir, etatInitialListe, type EtatListeMemorise } from './features/tickets/memoireListe';
import {
  Badge, Button, Card, Input, Select, Modal, Tabs, Wordmark,
  CIBLE_TACTILE, ZONE_TACTILE, FENETRE_FOND_MOBILE, FENETRE_MOBILE, Fenetre } from './shared/ui';
import { useEcranEtroit, useTelephone, DefilementHorizontal } from './shared/responsive';
import { BadgeStatut, IconeStatut, couleurStatut } from './shared/ui/BadgeStatut';
import { actionsStatut } from './shared/statuts';
import { ParcoursStatut } from './features/tickets/ParcoursStatut';
import { PucesStatut, LegendeStatuts } from './features/tickets/PucesStatut';
import { useHauteurAuto } from './features/tickets/zoneSaisie';
import {
  KPICard, DonutChart, AreaLineChart, VerticalBarChart, HorizontalBarChart, Sparkline
} from './shared/charts';
import ConnecteursView from './features/connecteurs/ConnecteursView';
import { TelephonieView } from './features/telephonie/TelephonieView';
import ParametresView from './features/settings/ParametresView';
import CourrierEcarteView from './features/courrier/CourrierEcarteView';
import { useCompteurCourrierEcarte } from './features/courrier/useCompteurCourrierEcarte';
import { compteurLisible } from './features/courrier/courrier';

// ============================================
// TYPES ADDITIONNELS V2
// ============================================

/** Entrée de navigation de la sidebar. `badge` est optionnel : sans ce type
 *  explicite, TypeScript infère une union où les entrées sans badge ne
 *  déclarent pas la propriété, et toute lecture de `item.badge` échoue. */
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  permission: string | null;
  badge?: number;
  /** Nombre affiché sans pastille (texte discret), ex. courrier écarté. */
  compteurDiscret?: number | null;
}

interface NavSection {
  title: string;
  permission?: string;
  items: NavItem[];
}

interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}




// ============================================
// SERVICE IA - RÉPONSES AUTOMATIQUES
// ============================================

// Formatage et couleurs → shared/format.ts

// ============================================
// CONTEXTES
// ============================================

interface AuthContextType {
  user: UserType | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  refreshUser: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | null>(null);
const useAuth = () => { const c = useContext(AuthContext); if (!c) throw new Error('useAuth error'); return c; };

// Types pour les paramètres d'application
type AppLanguage = 'fr' | 'en';
type AppTheme = 'classic' | 'blue' | 'green';

// Système de traduction
const TRANSLATIONS: Record<AppLanguage, Record<string, string>> = {
  fr: {
    // Sidebar
    'sidebar.dashboard': 'Vue d\'ensemble',
    'sidebar.home': 'Accueil',
    'sidebar.tickets': 'Dossiers',
    'sidebar.allTickets': 'Tous les tickets',
    'sidebar.openTickets': 'Tickets ouverts',
    'sidebar.archives': 'Archives',
    'sidebar.clients': 'Clients',
    'sidebar.clientsList': 'Liste des clients',
    'sidebar.admin': 'Administration',
    'sidebar.users': 'Utilisateurs',
    'sidebar.automation': 'Automatisation',
    'sidebar.analytics': 'Analytics',
    'sidebar.settings': 'Préférences et SLA',
    'sidebar.brands': 'Marques',
    // Dashboard
    'dashboard.title': 'Tableau de bord',
    'dashboard.welcome': 'Bienvenue',
    'dashboard.totalTickets': 'Total tickets',
    'dashboard.openTickets': 'Tickets ouverts',
    'dashboard.resolvedTickets': 'Tickets résolus',
    'dashboard.avgResolution': 'Temps moyen',
    // Tickets
    'tickets.title': 'Gestion des tickets',
    'tickets.new': 'Nouveau ticket',
    'tickets.search': 'Rechercher...',
    'tickets.status': 'Statut',
    'tickets.priority': 'Priorité',
    'tickets.assignedTo': 'Assigné à',
    'tickets.created': 'Créé le',
    'tickets.updated': 'Mis à jour',
    // Settings
    'settings.title': 'Paramètres',
    'settings.general': 'Général',
    'settings.language': 'Langue de l\'interface',
    'settings.languageDesc': 'Sélectionnez la langue d\'affichage',
    'settings.theme': 'Thème de couleur',
    'settings.themeDesc': 'Personnalisez l\'apparence de la sidebar',
    'settings.company': 'Informations entreprise',
    'settings.companyName': 'Nom de l\'entreprise',
    // Common
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.close': 'Fermer',
    'common.loading': 'Chargement...',
    'common.noData': 'Aucune donnée',
  },
  en: {
    // Sidebar
    'sidebar.dashboard': 'Overview',
    'sidebar.home': 'Home',
    'sidebar.tickets': 'Dossiers',
    'sidebar.allTickets': 'All tickets',
    'sidebar.openTickets': 'Open tickets',
    'sidebar.archives': 'Archives',
    'sidebar.clients': 'Clients',
    'sidebar.clientsList': 'Client list',
    'sidebar.admin': 'Administration',
    'sidebar.users': 'Users',
    'sidebar.automation': 'Automation',
    'sidebar.analytics': 'Analytics',
    'sidebar.settings': 'Preferences & SLA',
    'sidebar.brands': 'Brands',
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome',
    'dashboard.totalTickets': 'Total tickets',
    'dashboard.openTickets': 'Open tickets',
    'dashboard.resolvedTickets': 'Resolved tickets',
    'dashboard.avgResolution': 'Avg time',
    // Tickets
    'tickets.title': 'Ticket management',
    'tickets.new': 'New ticket',
    'tickets.search': 'Search...',
    'tickets.status': 'Status',
    'tickets.priority': 'Priority',
    'tickets.assignedTo': 'Assigned to',
    'tickets.created': 'Created',
    'tickets.updated': 'Updated',
    // Settings
    'settings.title': 'Settings',
    'settings.general': 'General',
    'settings.language': 'Interface language',
    'settings.languageDesc': 'Select display language',
    'settings.theme': 'Color theme',
    'settings.themeDesc': 'Customize sidebar appearance',
    'settings.company': 'Company info',
    'settings.companyName': 'Company name',
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.loading': 'Loading...',
    'common.noData': 'No data',
  },
};

// Theme styles with CSS values for runtime application - Couleurs KLY
const THEME_STYLES: Record<AppTheme, {
  name: string;
  description: string;
  sidebarBg: string;
  logoBg: string;
  activeBg: string;
  activeText: string;
  accentColor: string;
  accentHover: string;
  borderColor: string;
  hoverBg: string;
  textMuted: string;
  isLight?: boolean; // Pour les thèmes clairs (texte sombre)
}> = {
  classic: {
    name: 'KLY clair',
    description: 'Barre latérale blanche, accents sapin',
    sidebarBg: '#ffffff',
    logoBg: 'var(--kly-petrol-600)',
    activeBg: 'var(--kly-sapin-50)',
    activeText: 'var(--kly-sapin-900)',
    accentColor: 'var(--kly-sapin-700)',
    accentHover: 'var(--kly-sapin-900)',
    borderColor: 'var(--kly-trait)',
    hoverBg: 'var(--kly-brume)',
    textMuted: 'var(--kly-graphite)',
    isLight: true,
  },
  blue: {
    name: 'KLY pétrole',
    description: 'Barre latérale bleu pétrole du logo, actif en sapin',
    sidebarBg: 'var(--kly-petrol-700)',
    logoBg: 'var(--kly-sapin-600)',
    activeBg: 'rgba(255, 255, 255, 0.10)',
    activeText: '#ffffff',
    accentColor: '#93C5FD',
    accentHover: '#BFDBFE',
    borderColor: 'rgba(255, 255, 255, 0.10)',
    hoverBg: 'rgba(255, 255, 255, 0.06)',
    textMuted: 'rgba(207, 225, 232, 0.65)',
  },
  green: {
    name: 'KLY forêt',
    description: 'Barre latérale vert sapin profond',
    sidebarBg: 'var(--kly-sapin-900)',
    logoBg: 'var(--kly-petrol-600)',
    activeBg: 'rgba(255, 255, 255, 0.12)',
    activeText: '#ffffff',
    accentColor: '#93C5FD',
    accentHover: '#DBEAFE',
    borderColor: 'rgba(255, 255, 255, 0.10)',
    hoverBg: 'rgba(255, 255, 255, 0.06)',
    textMuted: 'rgba(216, 235, 217, 0.6)',
  },
};

interface TableauDeBord {
  /** Dossiers nouveaux ou réouverts sans agent. */
  nonAssignes: Ticket[];
  /** La lecture des dossiers sans agent a atteint sa borne : le compte est un minimum. */
  nonAssignesTronque: boolean;
  /** Derniers dossiers actifs reçus. */
  derniers: Ticket[];
  charge: boolean;
}

interface AdminContextType {
  currentView: string;
  setCurrentView: (v: string) => void;
  /** Ouvre la fiche d'un dossier sur sa page (/dossiers/:id). */
  ouvrirDossier: (id: string) => void;
  /** État de la liste quittée pour une fiche, repris au retour (filtres, recherche, page). */
  memoireListe: React.MutableRefObject<EtatListeMemorise | null>;
  sidebarOpen: boolean;
  setSidebarOpen: (o: boolean) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (c: boolean) => void;
  notifications: NotificationType[];
  unreadNotifications: number;
  unreadByTicket: Map<string, number>;
  hasUnreadForTicket: (ticketId: string) => boolean;
  markNotificationsAsRead: (ids: string[]) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  humanTakeoverAlert: { ticketId: string; ticketNumber: string; customerName: string } | null;
  clearHumanTakeoverAlert: () => void;
  showAIAssistant: boolean;
  setShowAIAssistant: (s: boolean) => void;
  /** Données propres du tableau de bord (indépendantes de la page affichée dans la liste). */
  tableauDeBord: TableauDeBord;
  /** Incrémenté à chaque changement signalé par le serveur : la liste recharge sa page. */
  versionDossiers: number;
  /** Ouvre le formulaire « Nouveau dossier ». */
  ouvrirCreation: () => void;
  /** Personnel (admins, superviseurs, agents), actifs ou non : noms, écran Équipe. */
  users: UserType[];
  /** Personnel affectable (GET /admin/agents, sans comptes techniques) : listes d'affectation et de transfert. */
  agents: UserType[];
  /** Filtre de statut demandé à la liste des dossiers (barre latérale) ; null = rien en attente. */
  pendingTicketFilter: string | null;
  setPendingTicketFilter: (s: string | null) => void;
  stats: TicketStats | null;
  refreshData: () => Promise<void>;
  isLoading: boolean;
  autoAssignTickets: () => Promise<void>;
  // App settings
  appTheme: AppTheme;
  setAppTheme: (t: AppTheme) => void;
  appLanguage: AppLanguage;
  setAppLanguage: (l: AppLanguage) => void;
  // Translation function
  t: (key: string) => string;
}
const AdminContext = createContext<AdminContextType | null>(null);
const useAdmin = () => { const c = useContext(AdminContext); if (!c) throw new Error('useAdmin error'); return c; };

// Primitives UI  -> shared/ui
// Graphiques     -> shared/charts

// ============================================
// LOGIN PAGE
// ============================================

const LoginPageV2: React.FC<{ onLogin: (email: string, password: string) => Promise<boolean> }> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await onLogin(email, password);
      if (!success) setError('Identifiants invalides');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Erreur de connexion');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      {/* Volet gauche : l'identité, calme, en bleu pétrole du logo. */}
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-petrol-800 text-white p-12">
        <img src={photoAccueil} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover anim-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-br from-petrol-900/85 via-petrol-800/60 to-petrol-700/30" />
        <div className="relative anim-fade-up"><Wordmark inverse /></div>
        <div className="relative max-w-md anim-fade-up anim-delay-2">
          <p className="text-3xl font-extrabold tracking-tight leading-tight">Le service après-vente des installateurs KLY.</p>
          <p className="mt-4 text-petrol-100/80 leading-relaxed">Dossiers, garanties constructeur, échanges avec les clients : tout ce qui arrive sur sav@ et assistance@ passe par ici.</p>
        </div>
        <p className="relative text-sm text-petrol-100/70 anim-fade-up anim-delay-3">KLY Groupe · Gennevilliers</p>
      </section>
      {/* Volet droit : le formulaire, seul. */}
      <section className="flex items-center justify-center p-6 sm:p-12 bg-papier">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10"><Wordmark /></div>
          <h1 className="text-2xl font-extrabold tracking-tight text-encre">Connexion</h1>
          <p className="mt-1 text-graphite">Espace réservé à l'équipe SAV.</p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {error && (
              <div role="alert" className="p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" /><span>{error}</span>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-encre mb-1.5">Adresse e-mail</label>
              <Input type="email" placeholder="prenom@klygroupe.com" value={email} onChange={e => setEmail(e.target.value)} icon={<Mail className="w-5 h-5" />} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-encre mb-1.5">Mot de passe</label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} placeholder="Votre mot de passe" value={password} onChange={e => setPassword(e.target.value)} icon={<Lock className="w-5 h-5" />} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'} className="absolute right-2 top-1/2 -translate-y-1/2 text-graphite hover:text-encre p-1.5 rounded-md">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="-mt-2 text-right">
              <Link to="/mot-de-passe-oublie" className="text-sm font-semibold text-sapin-700 hover:text-sapin-900">Mot de passe oublié ?</Link>
            </div>
            <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading}>
              {isLoading ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> : null}
              {isLoading ? 'Connexion en cours' : 'Se connecter'}
            </Button>
          </form>
          <p className="mt-8 text-xs text-graphite">Accès réservé au personnel autorisé. Les connexions sont journalisées.</p>
        </div>
      </section>
    </div>
  );
};

// ============================================
// SIDEBAR
// ============================================

const Sidebar: React.FC = () => {
  const { currentView, setCurrentView, sidebarOpen, setSidebarOpen, sidebarCollapsed: repliChoisi, setSidebarCollapsed, stats, appTheme, t, setPendingTicketFilter, tableauDeBord } = useAdmin();
  const { user, hasPermission } = useAuth();
  const theme = THEME_STYLES[appTheme];
  // Sous 1024 px, la barre latérale est un tiroir : toujours dépliée (libellés
  // visibles), même si elle a été repliée sur ordinateur.
  const etroit = useEcranEtroit();
  const sidebarCollapsed = repliChoisi && !etroit;

  // Tiroir ouvert : Échap le ferme.
  useEffect(() => {
    if (!sidebarOpen) return;
    const surTouche = (e: KeyboardEvent) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [sidebarOpen, setSidebarOpen]);

  // Check permissions
  const isAdmin = user?.role === UserRole.ADMIN || user?.role === 'ADMIN';
  const canAccess = (permission: string | null) => !permission || isAdmin || hasPermission(permission);

  // Dossiers nouveaux ou réouverts, d'après les statistiques du serveur ; pour
  // un agent (statistiques limitées à ses dossiers), plus ceux sans conseiller,
  // que la liste lui montre aussi.
  const openTicketsCount = compteDossiersOuverts(stats, tableauDeBord.nonAssignes.length, user?.role);
  // E-mails écartés par le tri (notifications, avis de l'IA) : compteur discret.
  const courrierEcarte = useCompteurCourrierEcarte(!!user && canAccess('tickets'));

  // Menu sections structure with translations
  const menuSections: NavSection[] = [
    {
      title: t('sidebar.home'),
      items: [
        { id: 'dashboard', label: t('sidebar.dashboard'), icon: <LayoutDashboard className="w-5 h-5" />, permission: null },
      ]
    },
    {
      title: t('sidebar.tickets'),
      permission: 'tickets',
      items: [
        { id: 'tickets', label: t('sidebar.allTickets'), icon: <TicketIcon className="w-5 h-5" />, permission: 'tickets' },
        // Archives (19/10/2026) : les dossiers fermés. Remplace « Tickets fermés ».
        { id: VUE_ARCHIVES, label: t('sidebar.archives'), icon: <Archive className="w-5 h-5" />, permission: 'tickets' },
        { id: 'tickets-open', label: t('sidebar.openTickets'), icon: <Clock className="w-5 h-5" />, permission: 'tickets', badge: openTicketsCount },
        { id: 'courrier-ecarte', label: 'Courrier écarté', icon: <MailX className="w-5 h-5" />, permission: 'tickets', compteurDiscret: courrierEcarte },
      ]
    },
    {
      title: t('sidebar.clients'),
      permission: 'tickets',
      items: [
        { id: 'clients', label: t('sidebar.clientsList'), icon: <UserCircle className="w-5 h-5" />, permission: 'tickets' },
      ]
    },
    {
      title: t('sidebar.admin'),
      permission: 'team',
      items: [
        { id: 'team', label: t('sidebar.users'), icon: <Users className="w-5 h-5" />, permission: 'team' },
        { id: 'automation', label: t('sidebar.automation'), icon: <Workflow className="w-5 h-5" />, permission: 'automation' },
        { id: 'analytics', label: t('sidebar.analytics'), icon: <BarChart3 className="w-5 h-5" />, permission: 'reports' },
        { id: 'brands', label: t('sidebar.brands'), icon: <FolderOpen className="w-5 h-5" />, permission: 'settings' },
        { id: 'connecteurs', label: 'Boîtes mail', icon: <Mail className="w-5 h-5" />, permission: 'settings' },
        { id: 'telephonie', label: 'Téléphonie', icon: <Phone className="w-5 h-5" />, permission: 'settings' },
        { id: 'parametres', label: 'Paramètres', icon: <Cog className="w-5 h-5" />, permission: 'settings' },
        // Deux écrans distincts : « Paramètres » (réglages de la plateforme) et
        // « Préférences et SLA » (profil, sécurité, langue, délais SLA, réponses types).
        { id: 'settings', label: t('sidebar.settings'), icon: <UserCog className="w-5 h-5" />, permission: 'settings' },
      ]
    },
  ];

  const handleMenuClick = (itemId: string) => {
    // Raccourcis de la liste des dossiers : le filtre passe par l'état partagé,
    // lu par la liste à son montage (un événement émis avant que la liste
    // n'écoute était perdu).
    if (itemId === 'tickets-open') {
      setPendingTicketFilter('OPEN');
      setCurrentView('tickets');
    } else {
      if (itemId === 'tickets') setPendingTicketFilter('');
      setCurrentView(itemId);
    }
    setSidebarOpen(false);
  };

  // Couleurs de texte selon le thème (clair ou sombre)
  const textPrimary = theme.isLight ? 'var(--kly-encre)' : '#ffffff';
  const textSecondary = theme.isLight ? 'var(--kly-graphite)' : 'rgba(255,255,255,0.78)';
  const textHover = theme.isLight ? 'var(--kly-encre)' : '#ffffff';

  return (
    <>
      {sidebarOpen && <div className="fixed inset-0 bg-encre/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 transform transition-all duration-300 ${sidebarCollapsed ? 'w-20' : 'w-64'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} ${theme.isLight ? 'shadow-flottant' : ''}`}
        style={{ background: theme.sidebarBg }}
      >
        {/* Logo */}
        <div
          className={`flex items-center h-16 px-4 border-b ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}
          style={{ borderColor: theme.borderColor }}
        >
          <div className={`flex items-center ${sidebarCollapsed ? '' : 'gap-3'}`}>
            <Wordmark inverse={!theme.isLight} compact={sidebarCollapsed} />
          </div>
          <button
            className="lg:hidden transition-colors p-2.5 -mr-2 rounded-lg"
            style={{ color: theme.accentColor }}
            onClick={() => setSidebarOpen(false)}
            aria-label="Fermer le menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User info */}
        {user && (
          <div
            className={`py-3 border-b ${sidebarCollapsed ? 'px-2' : 'px-4'}`}
            style={{ borderColor: theme.borderColor }}
          >
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`} title={sidebarCollapsed ? `${user.displayName} - ${ROLE_CONFIG[user.role]?.label || user.role}` : undefined}>
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center border"
                style={{ background: theme.isLight ? '#e2e8f0' : theme.hoverBg, borderColor: theme.borderColor }}
              >
                <span className="text-sm font-semibold" style={{ color: theme.accentColor }}>{getInitials(user.displayName, user.email)}</span>
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: textPrimary }}>{user.displayName}</p>
                  <p className="text-xs" style={{ color: theme.textMuted }}>{ROLE_CONFIG[user.role]?.label || user.role}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className={`py-4 overflow-y-auto overscroll-contain h-[calc(100vh-10rem)] supports-[height:100dvh]:h-[calc(100dvh-10rem)] space-y-6 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
          {menuSections.map((section) => {
            // Check if user has access to this section
            if (section.permission && !canAccess(section.permission)) return null;

            // Filter items by permission
            const visibleItems = section.items.filter(item => canAccess(item.permission));
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.title}>
                {!sidebarCollapsed && (
                  <h3 className="px-3 mb-1.5 text-xs font-semibold" style={{ color: theme.textMuted }}>
                    {section.title}
                  </h3>
                )}
                {sidebarCollapsed && (
                  <div className="w-8 h-px mx-auto mb-2" style={{ backgroundColor: theme.borderColor }} />
                )}
                <div className="space-y-1">
                  {visibleItems.map(item => {
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleMenuClick(item.id)}
                        title={sidebarCollapsed ? item.label : undefined}
                        aria-current={currentView === item.id ? 'page' : undefined}
                        className={`w-full flex items-center rounded-lg text-sm font-medium group relative select-none outline-none focus:outline-none
                          ${sidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'}
                          ${currentView === item.id ? 'font-semibold' : ''}`}
                        style={{
                          background: currentView === item.id ? theme.activeBg : 'transparent',
                          color: currentView === item.id ? theme.activeText : textSecondary,
                          transition: 'all 0.15s ease',
                          WebkitUserSelect: 'none',
                          userSelect: 'none',
                        }}
                        onMouseEnter={(e) => {
                          if (currentView !== item.id) {
                            e.currentTarget.style.background = theme.hoverBg;
                            e.currentTarget.style.color = textHover;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentView !== item.id) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = textSecondary;
                          }
                        }}
                      >
                        <span style={{ color: currentView === item.id ? theme.activeText : theme.accentColor }} className="transition-colors">
                          {item.icon}
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span className="flex-1 text-left">{item.label}</span>
                            {item.badge !== undefined && item.badge > 0 && (
                              <span
                                className="px-2 py-0.5 text-xs font-semibold rounded-full"
                                style={currentView === item.id
                                  ? { backgroundColor: theme.isLight ? '#ffffff' : 'rgba(255,255,255,0.15)', color: theme.activeText }
                                  : { backgroundColor: theme.isLight ? '#e2e8f0' : theme.hoverBg, color: theme.accentColor }
                                }
                              >
                                {item.badge}
                              </span>
                            )}
                            {compteurLisible(item.compteurDiscret) && (
                              <span
                                className="text-xs font-medium tnum"
                                style={{ color: currentView === item.id ? theme.activeText : theme.textMuted }}
                                aria-label={`${item.compteurDiscret} e-mails écartés`}
                              >
                                {compteurLisible(item.compteurDiscret)}
                              </span>
                            )}
                          </>
                        )}
                        {sidebarCollapsed && item.badge !== undefined && item.badge > 0 && (
                          <span
                            className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-[10px] font-bold rounded-full text-white"
                            style={{ background: theme.logoBg }}
                          >
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer with collapse toggle */}
        <div className="absolute bottom-0 left-0 right-0 border-t" style={{ borderColor: theme.borderColor }}>
          {/* Collapse toggle button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex w-full items-center justify-center p-3 transition-colors"
            style={{ color: theme.accentColor }}
            onMouseEnter={(e) => { e.currentTarget.style.background = theme.hoverBg; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            title={sidebarCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
          {!sidebarCollapsed && (
            <div className="flex items-center justify-center gap-2 text-xs pb-3" style={{ color: theme.textMuted }}>
              <span>KLY Groupe · Service après-vente</span>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

// ============================================
// RECHERCHE GLOBALE
// ============================================
// Ce qu'on fait en premier au téléphone : retrouver un dossier par sa
// référence, un numéro de série, un client, une commande, une adresse.

// Sur téléphone (`pleinEcran`), la recherche s'ouvre par-dessus tout l'écran,
// résultats en liste sous le champ ; Échap ou la croix la referment.
const RechercheGlobale: React.FC<{ pleinEcran?: boolean; onFermer?: () => void }> = ({ pleinEcran = false, onFermer }) => {
  const { ouvrirDossier, setCurrentView } = useAdmin();
  const [q, setQ] = useState('');
  const [resultat, setResultat] = useState<ResultatRecherche | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const zone = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResultat(null); return; }
    const t = setTimeout(async () => {
      setEnCours(true);
      try { setResultat(await AdminApi.recherche(q.trim())); setOuvert(true); }
      catch { setResultat(null); }
      finally { setEnCours(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const fermer = (e: MouseEvent) => { if (zone.current && !zone.current.contains(e.target as Node)) setOuvert(false); };
    document.addEventListener('mousedown', fermer);
    return () => document.removeEventListener('mousedown', fermer);
  }, []);

  // En plein écran, Échap ferme même quand le champ n'a plus le focus.
  useEffect(() => {
    if (!pleinEcran) return;
    const touche = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer?.(); };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [pleinEcran, onFermer]);

  // La page du dossier le charge elle-même (et dit s'il n'est plus accessible).
  const ouvrir = (id: string) => {
    setOuvert(false); setQ('');
    ouvrirDossier(id);
    onFermer?.();
  };

  const rien = resultat && resultat.dossiers.length === 0 && resultat.clients.length === 0;
  const champ = (
    <Input placeholder="Référence, n° de série, client, commande…" icon={<Search className="w-5 h-5" />} className={pleinEcran ? '' : 'w-96 max-lg:w-64'}
      autoFocus={pleinEcran} aria-label={pleinEcran ? 'Rechercher un dossier ou un client' : undefined}
      value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => {
        if (e.key === 'Escape') { setOuvert(false); if (pleinEcran) onFermer?.(); }
        if (e.key === 'Enter' && resultat?.dossiers[0]) ouvrir(resultat.dossiers[0].id);
      }} />
  );
  const resultats = (
    <>
      {enCours && !resultat && <p className="p-4 text-sm text-graphite">Recherche…</p>}
          {rien && <p className="p-4 text-sm text-graphite">Rien pour « {q} ». Essayez une référence (26-0012), un numéro de série ou un nom de client.</p>}
          {resultat && resultat.dossiers.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-xs font-semibold text-graphite">Dossiers</p>
              {resultat.dossiers.map(d => (
                <button key={d.id} onClick={() => ouvrir(d.id)} className={`${getStatusRail(d.status as TicketStatus)} w-full text-left px-4 py-2.5 max-lg:py-3 hover:bg-brume flex items-center gap-3`}>
                  <span className="ref text-sm w-20 shrink-0">{d.ticketRef ?? '—'}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-encre truncate">{d.title}</span>
                    <span className="block text-xs text-graphite truncate">{d.client ?? 'Client inconnu'}{d.serialNumber ? ` · n° ${d.serialNumber}` : ''}</span>
                  </span>
                  <BadgeStatut status={d.status} />
                </button>
              ))}
            </div>
          )}
          {resultat && resultat.clients.length > 0 && (
            <div className="border-t border-trait">
              <p className="px-4 pt-3 pb-1 text-xs font-semibold text-graphite">Clients</p>
              {resultat.clients.map(c => (
                <button key={c.id} onClick={() => { setOuvert(false); setQ(''); setCurrentView('clients'); onFermer?.(); }} className="w-full text-left px-4 py-2.5 max-lg:py-3 hover:bg-brume">
                  <span className="block text-sm text-encre">{c.displayName}{c.customerCode ? ` · ${c.customerCode}` : ''}</span>
                  <span className="block text-xs text-graphite tnum">{c.email ?? ''}{c.dossiers ? ` · ${c.dossiers} dossier${c.dossiers > 1 ? 's' : ''}` : ''}</span>
                </button>
              ))}
            </div>
          )}
    </>
  );

  if (pleinEcran) {
    return (
      <div ref={zone} role="dialog" aria-modal="true" aria-label="Recherche" className="fixed inset-0 z-[60] bg-white flex flex-col animate-fade-in md:hidden">
        <div className="flex items-center gap-2 p-3 border-b border-trait">
          <div className="flex-1 min-w-0">{champ}</div>
          <button onClick={onFermer} aria-label="Fermer la recherche" className="p-2.5 text-graphite hover:text-encre hover:bg-brume rounded-lg transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain" role="listbox" aria-label="Résultats">
          {q.trim().length < 2
            ? <p className="p-4 text-sm text-graphite">Tapez au moins deux caractères : référence (26-0012), numéro de série, client, commande ou adresse.</p>
            : resultats}
        </div>
      </div>
    );
  }

  return (
    <div ref={zone} className="relative">
      {champ}
      {ouvert && (resultat || enCours) && (
        <div className="absolute left-0 top-12 w-[36rem] max-h-[70vh] overflow-y-auto bg-white rounded-card border border-trait shadow-flottant z-50 animate-fade-in" role="listbox">
          {resultats}
        </div>
      )}
    </div>
  );
};

// ============================================
// HEADER
// ============================================

// Titre de l'écran affiché dans l'en-tête sur téléphone (la barre latérale y est repliée en tiroir).
const titreEcran = (vue: string, surPageDossier: boolean, t: (cle: string) => string): string => {
  if (surPageDossier) return 'Dossier';
  const titres: Record<string, string> = {
    dashboard: t('dashboard.title'),
    tickets: t('sidebar.tickets'),
    clients: t('sidebar.clients'),
    team: t('sidebar.users'),
    automation: t('sidebar.automation'),
    analytics: t('sidebar.analytics'),
    brands: t('sidebar.brands'),
    connecteurs: 'Boîtes mail',
    'courrier-ecarte': 'Courrier écarté',
    [VUE_ARCHIVES]: 'Archives',
    telephonie: 'Téléphonie',
    parametres: 'Paramètres',
    settings: t('sidebar.settings'),
  };
  return titres[vue] ?? '';
};

const Header: React.FC = () => {
  const { setSidebarOpen, notifications, unreadNotifications, markNotificationsAsRead, markAllNotificationsAsRead, setShowAIAssistant, refreshData, isLoading, tableauDeBord, autoAssignTickets, setCurrentView, ouvrirDossier, currentView, t } = useAdmin();
  const location = useLocation();
  const titre = titreEcran(currentView, matchPath('/dossiers/:id', location.pathname) !== null, t);
  // Recherche globale en plein écran (téléphone).
  const [rechercheOuverte, setRechercheOuverte] = useState(false);
  // Dossiers déjà connus (tableau de bord) : sert à titrer les notifications sans requête.
  const tickets = useMemo(() => [...tableauDeBord.nonAssignes, ...tableauDeBord.derniers], [tableauDeBord]);
  const { user, logout } = useAuth();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [notifTab, setNotifTab] = useState<'all' | 'messages' | 'sla' | 'system'>('all');
  const [showAllNotifs, setShowAllNotifs] = useState(false); // Afficher toutes les notifications
  const unassignedCount = tableauDeBord.nonAssignes.length;
  const unassignedLabel = libelleNombre(unassignedCount, tableauDeBord.nonAssignesTronque);

  // Filtrer les notifications selon l'onglet actif
  const filteredNotifications = useMemo(() => {
    switch (notifTab) {
      case 'messages':
        return notifications.filter(n => n.type === 'MESSAGE' || n.type === 'MENTION');
      case 'sla':
        return notifications.filter(n => n.type === 'SLA_WARNING' || n.type === 'SLA_BREACH');
      case 'system':
        // Tout ce que les onglets Messages et SLA ne couvrent pas — soit
        // TICKET_UPDATE, qui n'apparaissait jusqu'ici dans aucun onglet.
        // (Les anciens tests sur 'SYSTEM' / 'ASSIGNMENT' visaient des types
        //  absents de l'enum NotificationType : ils étaient toujours faux.)
        return notifications.filter(
          n => !['MESSAGE', 'MENTION', 'SLA_WARNING', 'SLA_BREACH'].includes(n.type)
        );
      default:
        return notifications;
    }
  }, [notifications, notifTab]);

  const handleAutoAssign = async () => {
    if (isAutoAssigning || unassignedCount === 0) return;
    setIsAutoAssigning(true);
    try {
      await autoAssignTickets();
    } finally {
      setIsAutoAssigning(false);
    }
  };

  return (
    <header className="h-16 bg-white border-b border-trait flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        <button className="lg:hidden text-graphite hover:text-encre p-2 -ml-1 sm:-ml-2 rounded-lg hover:bg-brume flex-shrink-0" onClick={() => setSidebarOpen(true)} aria-label="Ouvrir le menu"><Menu className="w-6 h-6" /></button>
        {/* Téléphone : logo réduit au globe et nom de l'écran ; la recherche passe par la loupe.
            Sous 360 px, le globe cède sa place au titre. */}
        <div className="md:hidden flex items-center gap-2 min-w-0">
          <span className="flex-shrink-0 max-[359px]:hidden"><Wordmark compact /></span>
          {titre && <p className="text-sm font-bold text-encre truncate">{titre}</p>}
        </div>
        <div className="hidden md:block">
          <RechercheGlobale />
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        <button className="md:hidden p-2.5 max-sm:p-2 text-graphite hover:text-encre hover:bg-brume rounded-lg transition-colors" onClick={() => setRechercheOuverte(true)} aria-label="Rechercher un dossier ou un client">
          <Search className="w-5 h-5" />
        </button>
        <Button variant="ghost" size="sm" onClick={refreshData} disabled={isLoading} className="hidden sm:flex">
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
        {estEncadrement(user?.role) && <button
          onClick={handleAutoAssign}
          disabled={isAutoAssigning || unassignedCount === 0}
          className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            unassignedCount > 0
              ? 'bg-orange-500 text-white'
              : 'bg-brume text-graphite/70 cursor-not-allowed'
          }`}
          title={unassignedCount > 0 ? `Assigner ${unassignedLabel} ticket(s) automatiquement` : 'Aucun ticket à assigner'}
        >
          <Zap className={`w-4 h-4 ${isAutoAssigning ? '' : ''}`} />
          <span className="max-lg:hidden">Auto-assigner</span>
          {unassignedCount > 0 && (
            <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">{unassignedLabel}</span>
          )}
        </button>}
        <Button variant="ghost" size="sm" onClick={() => setShowAIAssistant(true)} className="text-purple-600 hover:bg-purple-50 max-sm:hidden" aria-label="Assistant IA">
          <Sparkles className="w-4 h-4 lg:mr-2" /><span className="hidden lg:inline">Assistant IA</span>
        </Button>
        <button className="relative p-2.5 max-sm:p-2 text-graphite hover:text-encre hover:bg-brume rounded-lg transition-colors" aria-label={unreadNotifications > 0 ? `Notifications (${unreadNotifications} non lues)` : 'Notifications'} aria-expanded={showNotifs} onClick={() => { setShowNotifs(!showNotifs); if (showNotifs) setShowAllNotifs(false); }}>
          <Bell className="w-5 h-5" />
          {unreadNotifications > 0 && <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">{unreadNotifications > 9 ? '9+' : unreadNotifications}</span>}
        </button>

        <div className="relative">
          <button className="flex items-center gap-3 sm:pl-3 sm:border-l border-trait sm:ml-2" onClick={() => setShowUserMenu(!showUserMenu)} aria-label="Mon compte" aria-expanded={showUserMenu}>
            <div className="text-right hidden lg:block">
              <p className="text-sm font-medium text-encre">{user?.displayName}</p>
              <p className="text-xs text-graphite">{ROLE_CONFIG[user?.role || '']?.label}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-blue-600">{user ? getInitials(user.displayName, user.email) : ''}</span>
            </div>
          </button>
          {showUserMenu && (
            <div className="absolute right-0 top-14 w-56 bg-white rounded-lg shadow-flottant border border-trait py-2 z-50 animate-fade-in">
              <div className="px-4 py-2 border-b border-trait">
                <p className="text-sm font-medium text-encre">{user?.displayName}</p>
                <p className="text-xs text-graphite">{user?.email}</p>
              </div>
              {/* Téléphone : l'Assistant IA, retiré de l'en-tête faute de place. */}
              <button
                onClick={() => { setShowAIAssistant(true); setShowUserMenu(false); }}
                className="sm:hidden w-full px-4 py-2.5 text-left text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-3"
              >
                <Sparkles className="w-4 h-4" />Assistant IA
              </button>
              <button
                onClick={() => { setCurrentView('settings'); setShowUserMenu(false); }}
                className="w-full px-4 py-2.5 text-left text-sm text-encre hover:bg-brume flex items-center gap-3"
              >
                <User className="w-4 h-4 text-graphite/70" />Mon profil
              </button>
              <button
                onClick={() => { setCurrentView('settings'); setShowUserMenu(false); }}
                className="w-full px-4 py-2.5 text-left text-sm text-encre hover:bg-brume flex items-center gap-3"
              >
                <Settings className="w-4 h-4 text-graphite/70" />Préférences
              </button>
              <hr className="my-1" />
              <button onClick={logout} className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3">
                <LogOut className="w-4 h-4" />Déconnexion
              </button>
            </div>
          )}
        </div>
      </div>

      {showNotifs && (
        <div className="absolute right-2 sm:right-4 top-16 w-[calc(100vw-1rem)] sm:w-[420px] max-w-[420px] bg-white rounded-lg shadow-flottant border border-trait z-50 animate-fade-in">
          <div className="p-4 border-b border-trait flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-encre">Notifications</h3>
              {unreadNotifications > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">{unreadNotifications} nouvelle{unreadNotifications > 1 ? 's' : ''}</span>}
            </div>
            {unreadNotifications > 0 && <button onClick={() => markAllNotificationsAsRead()} className="text-xs text-blue-600 hover:text-blue-700 font-medium max-lg:min-h-10 max-lg:px-2 max-lg:-mr-2">Tout marquer lu</button>}
          </div>

          {/* Tabs for notification types */}
          <div className="flex border-b border-trait">
            <button
              onClick={() => { setNotifTab('all'); setShowAllNotifs(false); }}
              className={`flex-1 px-4 py-2 max-lg:py-3 max-sm:px-2 text-sm font-medium transition-colors ${notifTab === 'all' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-graphite hover:text-encre'}`}
            >
              Toutes
            </button>
            <button
              onClick={() => { setNotifTab('messages'); setShowAllNotifs(false); }}
              className={`flex-1 px-4 py-2 max-lg:py-3 max-sm:px-2 text-sm font-medium transition-colors ${notifTab === 'messages' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-graphite hover:text-encre'}`}
            >
              Messages
            </button>
            <button
              onClick={() => { setNotifTab('sla'); setShowAllNotifs(false); }}
              className={`flex-1 px-4 py-2 max-lg:py-3 max-sm:px-2 text-sm font-medium transition-colors ${notifTab === 'sla' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-graphite hover:text-encre'}`}
            >
              SLA
            </button>
            <button
              onClick={() => { setNotifTab('system'); setShowAllNotifs(false); }}
              className={`flex-1 px-4 py-2 max-lg:py-3 max-sm:px-2 text-sm font-medium transition-colors ${notifTab === 'system' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-graphite hover:text-encre'}`}
            >
              Système
            </button>
          </div>

          <div className={`${showAllNotifs ? 'max-h-[70vh]' : 'max-h-[400px]'} overflow-y-auto`}>
            {filteredNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-trait mx-auto mb-2" />
                <p className="text-sm text-graphite">
                  {notifTab === 'all' ? 'Aucune notification' :
                   notifTab === 'messages' ? 'Aucun message' :
                   notifTab === 'sla' ? 'Aucune alerte SLA' :
                   'Aucune notification système'}
                </p>
              </div>
            ) : (
              <>
                {(showAllNotifs ? filteredNotifications : filteredNotifications.slice(0, 10)).map(n => {
                  // Trouver le ticket associé pour afficher son numéro
                  const relatedTicket = n.ticketId ? tickets.find(t => t.id === n.ticketId) : null;
                  // Utiliser ticketNumber de la notification, du payload, ou du ticket trouvé
                  const ticketNumber = n.ticketNumber || (n.payload?.ticketNumber as number) || relatedTicket?.ticketNumber;
                  const ticketTitleDisplay = n.ticketTitle || (n.payload?.ticketTitle as string) || relatedTicket?.title;

                  const getNotifIcon = () => {
                    switch(n.type) {
                      case 'MESSAGE': return <MessageSquare className="w-4 h-4 text-blue-500" />;
                      case 'SLA_WARNING': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
                      case 'SLA_BREACH': return <AlertCircle className="w-4 h-4 text-red-500" />;
                      case 'TICKET_UPDATE': return <TicketIcon className="w-4 h-4 text-green-500" />;
                      case 'MENTION': return <User className="w-4 h-4 text-purple-500" />;
                      default: return <Bell className="w-4 h-4 text-graphite/70" />;
                    }
                  };
                  const getNotifLabel = () => {
                    switch(n.type) {
                      case 'MESSAGE': return 'Nouveau message';
                      case 'SLA_WARNING': return 'Alerte SLA';
                      case 'SLA_BREACH': return 'SLA dépassé';
                      case 'TICKET_UPDATE': return 'Ticket mis à jour';
                      case 'MENTION': return 'Vous avez été mentionné';
                      default: return 'Notification';
                    }
                  };
                  const getNotifColor = () => {
                    switch(n.type) {
                      case 'MESSAGE': return 'bg-blue-50 border-blue-200';
                      case 'SLA_WARNING': return 'bg-yellow-50 border-yellow-200';
                      case 'SLA_BREACH': return 'bg-red-50 border-red-200';
                      case 'TICKET_UPDATE': return 'bg-green-50 border-green-200';
                      case 'MENTION': return 'bg-purple-50 border-purple-200';
                      default: return 'bg-brume border-trait';
                    }
                  };
                  const handleNotifClick = () => {
                    // Mark as read
                    if (!n.isRead) {
                      markNotificationsAsRead([n.id]);
                    }
                    // Dossier lié : sa page le charge par son identifiant.
                    if (n.ticketId) {
                      ouvrirDossier(n.ticketId);
                    }
                    setShowNotifs(false);
                  };

                  // Construire le texte d'affichage du ticket
                  const getTicketDisplay = () => {
                    if (ticketNumber) {
                      return `Ticket #${ticketNumber}`;
                    } else if (n.ticketId) {
                      return `Ticket #${n.ticketId.slice(0, 8)}`;
                    }
                    return null;
                  };

                  return (
                    <div key={n.id} onClick={handleNotifClick} className={`p-4 border-b border-trait hover:bg-brume cursor-pointer transition-colors ${!n.isRead ? 'bg-blue-50/30' : ''}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${getNotifColor()}`}>
                          {getNotifIcon()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-encre">{getNotifLabel()}</p>
                            {!n.isRead && <span className="w-2 h-2 bg-blue-500 rounded-full" />}
                          </div>
                          {n.title && <p className="text-xs text-encre truncate">{n.title}</p>}
                          {n.body && <p className="text-xs text-graphite truncate mt-0.5">{n.body}</p>}
                          {n.ticketId && !n.title && (
                            <p className="text-xs text-blue-600 truncate font-medium">
                              {getTicketDisplay()}
                              {ticketTitleDisplay && <span className="text-graphite font-normal ml-1">- {ticketTitleDisplay.slice(0, 30)}{ticketTitleDisplay.length > 30 ? '...' : ''}</span>}
                            </p>
                          )}
                          <p className="text-xs text-graphite/70 mt-1">{formatDateTime(n.createdAt)}</p>
                        </div>
                        {!n.isRead && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markNotificationsAsRead([n.id]); }}
                            className={`p-1.5 text-graphite/70 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex items-center justify-center flex-shrink-0 ${CIBLE_TACTILE}`}
                            title="Marquer comme lu"
                            aria-label="Marquer comme lu"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {filteredNotifications.length > 10 && (
            <div className="p-3 border-t border-trait text-center">
              <button
                onClick={() => setShowAllNotifs(!showAllNotifs)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {showAllNotifs
                  ? 'Afficher moins'
                  : `Voir toutes les notifications (${filteredNotifications.length})`
                }
              </button>
            </div>
          )}
        </div>
      )}

      {rechercheOuverte && <RechercheGlobale pleinEcran onFermer={() => setRechercheOuverte(false)} />}
    </header>
  );
};

// ============================================
// AI ASSISTANT
// ============================================

const FILTRES_ASSISTANT = { excludeStatus: [TicketStatus.RESOLVED, TicketStatus.CLOSED] };

const AIAssistant: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { stats } = useAdmin();
  // Ses propres données : tous les dossiers actifs, chargés à l'ouverture de
  // l'assistant (pas à chaque événement temps réel : lecture trop coûteuse).
  const { dossiers: tickets } = useTousLesDossiers(isOpen, FILTRES_ASSISTANT);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Générer le message d'accueil basé sur les vraies données
  const generateWelcomeMessage = useCallback(() => {
    if (!stats) return 'Bonjour ! Je suis votre assistant IA SAV. Chargement des données...';

    const openTickets = stats.byStatus?.OPEN || 0;
    const inProgressTickets = stats.byStatus?.IN_PROGRESS || 0;
    const urgentTickets = stats.byPriority?.URGENT || 0;
    const slaBreached = stats.slaBreached || 0;

    let welcome = `Bonjour ! Voici l'état actuel de votre SAV:\n\n`;
    welcome += `📊 **${stats.total} tickets** au total\n`;
    welcome += `🆕 ${openTickets} ouvert${openTickets > 1 ? 's' : ''} • ⚙️ ${inProgressTickets} en cours\n`;

    if (urgentTickets > 0) {
      welcome += `\n🔴 **${urgentTickets} ticket${urgentTickets > 1 ? 's' : ''} urgent${urgentTickets > 1 ? 's' : ''}** à traiter\n`;
    }
    if (slaBreached > 0) {
      welcome += `⚠️ **${slaBreached} SLA dépassé${slaBreached > 1 ? 's' : ''}**\n`;
    }

    welcome += `\nQue souhaitez-vous analyser ?`;
    return welcome;
  }, [stats]);

  // Initialiser avec le message d'accueil
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: '1',
        role: 'assistant',
        content: generateWelcomeMessage(),
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, stats, generateWelcomeMessage, messages.length]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Analyser les tickets urgents
  const analyzeUrgentTickets = () => {
    const urgentTickets = tickets.filter(t => t.priority === 'URGENT' && t.status !== 'CLOSED' && t.status !== 'RESOLVED');
    if (urgentTickets.length === 0) {
      return '✅ Aucun ticket urgent en attente. Tout est sous contrôle !';
    }

    let response = `🔴 **${urgentTickets.length} ticket${urgentTickets.length > 1 ? 's' : ''} urgent${urgentTickets.length > 1 ? 's' : ''}:**\n\n`;
    urgentTickets.slice(0, 5).forEach(t => {
      response += `• **#${t.ticketNumber}** - ${t.title.slice(0, 40)}${t.title.length > 40 ? '...' : ''}\n`;
      response += `  ${t.assignedTo ? `👤 ${t.assignedTo.displayName}` : '⚠️ Non assigné'} • ${STATUS_LABELS[t.status as TicketStatus]}\n`;
    });
    if (urgentTickets.length > 5) {
      response += `\n... et ${urgentTickets.length - 5} autre${urgentTickets.length - 5 > 1 ? 's' : ''}`;
    }
    return response;
  };

  // Analyser les SLA
  const analyzeSLA = () => {
    const breachedTickets = tickets.filter(t => t.slaBreached && t.status !== 'CLOSED' && t.status !== 'RESOLVED');
    const atRiskTickets = tickets.filter(t => {
      if (!t.slaDeadline || t.slaBreached) return false;
      const deadline = new Date(t.slaDeadline);
      const now = new Date();
      const hoursLeft = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60);
      return hoursLeft > 0 && hoursLeft < 4;
    });

    let response = `📈 **Analyse SLA:**\n\n`;

    if (breachedTickets.length > 0) {
      response += `🔴 **${breachedTickets.length} SLA dépassé${breachedTickets.length > 1 ? 's' : ''}:**\n`;
      breachedTickets.slice(0, 3).forEach(t => {
        response += `• #${t.ticketNumber} - ${t.title.slice(0, 30)}...\n`;
      });
      response += '\n';
    }

    if (atRiskTickets.length > 0) {
      response += `🟡 **${atRiskTickets.length} ticket${atRiskTickets.length > 1 ? 's' : ''} proche${atRiskTickets.length > 1 ? 's' : ''} du SLA:**\n`;
      atRiskTickets.slice(0, 3).forEach(t => {
        response += `• #${t.ticketNumber} - ${t.title.slice(0, 30)}...\n`;
      });
    }

    if (breachedTickets.length === 0 && atRiskTickets.length === 0) {
      response += '✅ Tous les SLA sont respectés. Excellent travail !';
    }

    return response;
  };

  // Analyser la charge de travail
  const analyzeWorkload = () => {
    const openTickets = tickets.filter(t => t.status === 'OPEN' || t.status === 'REOPENED');
    const unassigned = openTickets.filter(t => !t.assignedTo);
    const byType = {
      TECHNICAL: tickets.filter(t => t.issueType === 'TECHNICAL' && t.status !== 'CLOSED').length,
      DELIVERY: tickets.filter(t => t.issueType === 'DELIVERY' && t.status !== 'CLOSED').length,
      BILLING: tickets.filter(t => t.issueType === 'BILLING' && t.status !== 'CLOSED').length,
      OTHER: tickets.filter(t => t.issueType === 'OTHER' && t.status !== 'CLOSED').length,
    };

    let response = `📊 **Analyse de la charge:**\n\n`;
    response += `🆕 **${openTickets.length} ticket${openTickets.length > 1 ? 's' : ''} en attente**\n`;

    if (unassigned.length > 0) {
      response += `⚠️ ${unassigned.length} non assigné${unassigned.length > 1 ? 's' : ''}\n`;
    }

    response += `\n**Par type:**\n`;
    response += `🔧 Technique: ${byType.TECHNICAL}\n`;
    response += `🚚 Livraison: ${byType.DELIVERY}\n`;
    response += `💳 Facturation: ${byType.BILLING}\n`;
    response += `📋 Autre: ${byType.OTHER}\n`;

    if (unassigned.length > 0) {
      response += `\n💡 **Recommandation:** ${unassigned.length} ticket${unassigned.length > 1 ? 's' : ''} à assigner en priorité.`;
    }

    return response;
  };

  // Chercher un ticket spécifique
  const searchTicket = (query: string) => {
    const searchTerm = query.toLowerCase();
    const found = tickets.filter(t =>
      t.title.toLowerCase().includes(searchTerm) ||
      t.ticketNumber?.toString().includes(searchTerm) ||
      t.customer?.displayName?.toLowerCase().includes(searchTerm) ||
      t.contactName?.toLowerCase().includes(searchTerm) ||
      t.companyName?.toLowerCase().includes(searchTerm)
    );

    if (found.length === 0) {
      return `❌ Aucun ticket trouvé pour "${query}".\n\nEssayez avec un numéro de ticket, un nom de client ou un mot-clé.`;
    }

    let response = `🔍 **${found.length} résultat${found.length > 1 ? 's' : ''} pour "${query}":**\n\n`;
    found.slice(0, 5).forEach(t => {
      const clientName = t.customer?.displayName || t.contactName || t.companyName || 'Client';
      response += `• **#${t.ticketNumber}** - ${t.title.slice(0, 35)}${t.title.length > 35 ? '...' : ''}\n`;
      response += `  ${clientName} • ${STATUS_LABELS[t.status as TicketStatus]}\n`;
    });

    return response;
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const userInput = input.trim();
    const newUserMessage: AIMessage = { id: Date.now().toString(), role: 'user', content: userInput, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, newUserMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Préparer l'historique pour l'API (exclure le message d'accueil initial)
      const conversationHistory = messages
        .filter(m => m.id !== '1') // Exclure le message d'accueil
        .map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }));

      // Appel à l'API backend
      const result = await AdminApi.chatWithAI(userInput, conversationHistory);

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: result.message,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Erreur AI chat:', error);
      // Fallback sur le traitement local en cas d'erreur
      let response: string;
      const inputLower = userInput.toLowerCase();

      if (inputLower.includes('urgent') || inputLower.includes('priorit')) {
        response = analyzeUrgentTickets();
      } else if (inputLower.includes('sla') || inputLower.includes('délai') || inputLower.includes('retard')) {
        response = analyzeSLA();
      } else if (inputLower.includes('charge') || inputLower.includes('workload') || inputLower.includes('répartition') || inputLower.includes('analyse') || inputLower.includes('stat')) {
        response = analyzeWorkload();
      } else if (inputLower.includes('cherch') || inputLower.includes('trouv') || inputLower.includes('search') || inputLower.startsWith('#')) {
        const searchQuery = userInput.replace(/^(cherche|trouve|search|#)\s*/i, '');
        response = searchTicket(searchQuery);
      } else {
        response = `Désolé, une erreur s'est produite. Réessayez avec:\n• "tickets urgents"\n• "analyse SLA"\n• "charge de travail"`;
      }

      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 w-full sm:w-[420px] h-screen max-sm:supports-[height:100dvh]:h-dvh sm:h-[560px] bg-white sm:rounded-card shadow-flottant border max-sm:border-0 border-trait flex flex-col z-50 animate-in fade-in slide-in-from-bottom-4">
      <div className="flex items-center justify-between p-4 border-b bg-purple-600 sm:rounded-t-2xl">
        <div className="flex items-center gap-3 text-white">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5" />
          </div>
          <div>
            <span className="font-semibold">Assistant IA</span>
            <p className="text-xs text-white/70">Analyse en temps réel</p>
          </div>
        </div>
        <button onClick={onClose} aria-label="Fermer l'assistant" className={`p-2 inline-flex items-center justify-center ${CIBLE_TACTILE} text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors`}><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-4 rounded-card ${m.role === 'user' ? 'bg-blue-600 text-white rounded-br-md' : 'bg-brume text-encre rounded-bl-md'}`}>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">
                {m.content.split('**').map((part, i) =>
                  i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
                )}
              </div>
              <p className={`text-[10px] mt-2 ${m.role === 'user' ? 'text-white/60' : 'text-graphite/70'}`}>{formatTime(m.timestamp)}</p>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-brume p-4 rounded-card rounded-bl-md">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="p-4 border-t bg-brume sm:rounded-b-2xl">
        <div className="flex gap-2">
          <Input placeholder="Posez votre question..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} className="flex-1 bg-white" />
          <Button variant="primary" onClick={handleSend} className="px-4" aria-label="Envoyer la question"><Send className="w-4 h-4" /></Button>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button onClick={() => { setInput('tickets urgents'); setTimeout(handleSend, 100); }} className="text-xs px-3 py-1.5 max-lg:py-2.5 bg-white border border-trait text-graphite rounded-full hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors">🔴 Urgents</button>
          <button onClick={() => { setInput('analyse SLA'); setTimeout(handleSend, 100); }} className="text-xs px-3 py-1.5 max-lg:py-2.5 bg-white border border-trait text-graphite rounded-full hover:bg-yellow-50 hover:border-yellow-200 hover:text-yellow-700 transition-colors">⏱️ SLA</button>
          <button onClick={() => { setInput('charge de travail'); setTimeout(handleSend, 100); }} className="text-xs px-3 py-1.5 max-lg:py-2.5 bg-white border border-trait text-graphite rounded-full hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-colors">📊 Charge</button>
        </div>
      </div>
    </div>
  );
};

// La barre « Actions rapides » a été retirée (19/10/2026) : elle doublait
// les boutons de l'en-tête de la fiche. « Auto-assigner » est passé à côté de
// « Prendre en charge ».

// Couleurs de la conversation : une par auteur, lue à la fois par la légende
// et par les bulles. L'IA est violette (hex : la classe violet-* est
// redirigée vers le bleu de la marque, tailwind.config.js), l'opérateur
// (un collègue) vert, le client ambre, vous en bleu.
const COULEURS_AUTEUR = {
  ia: { libelle: 'Assistant IA', pastille: 'bg-[#7C3AED]' },
  client: { libelle: 'Client', pastille: 'bg-amber-400' },
  vous: { libelle: 'Vous', pastille: 'bg-blue-500' },
  operateur: { libelle: 'Opérateur (collègue)', pastille: 'bg-emerald-600' },
} as const;

// ============================================
// CHAT PANEL AMÉLIORÉ
// ============================================

const ChatPanel: React.FC<{
  ticketId: string;
  ticket?: Ticket;
  /** Change quand le dossier a été modifié ailleurs : les messages sont relus (le brouillon reste). */
  versionRelecture?: number;
}> = ({ ticketId, ticket, versionRelecture = 0 }) => {
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  // Le brouillon (texte et mode) est gardé par dossier dans sessionStorage :
  // il survit au changement d'onglet et à une fermeture accidentelle.
  const [newMessage, setNewMessage] = useState(() => lireBrouillon(ticketId)?.texte ?? '');
  const [isInternal, setIsInternal] = useState(() => lireBrouillon(ticketId)?.interne ?? false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showQuickResponses, setShowQuickResponses] = useState(false);
  const [quickResponseSearch, setQuickResponseSearch] = useState('');
  // Réponses types de la base (GET /admin/canned-responses).
  const [reponsesTypes, setReponsesTypes] = useState<ReponseType[]>([]);
  const [reponsesEtat, setReponsesEtat] = useState<'chargement' | 'pret' | 'erreur'>('chargement');
  // Commande « / » en cours dans la zone de saisie, et suggestion surlignée.
  const [commande, setCommande] = useState<CommandeSlash | null>(null);
  const [indexSuggestion, setIndexSuggestion] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  // L'assistant automatique rédige (événement « ai:typing » de la salle du dossier).
  const [isAITyping, setIsAITyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const quickResponsesRef = useRef<HTMLDivElement>(null);
  const zoneSaisieRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  // Zone de réponse : 8 lignes au moins sur ordinateur (5 sur téléphone),
  // grandit avec le texte jusqu'à la moitié de l'écran ; « Agrandir » la passe en grand.
  const telephone = useTelephone();
  const [zoneAgrandie, setZoneAgrandie] = useState(false);
  const { noterRedimensionnement } = useHauteurAuto(zoneSaisieRef, newMessage, { telephone, agrandie: zoneAgrandie, pret: !isLoading });

  useEffect(() => {
    let vivant = true;
    AdminApi.getCannedResponses()
      .then(r => { if (vivant) { setReponsesTypes(r); setReponsesEtat('pret'); } })
      .catch(err => { console.error('Réponses types :', err); if (vivant) setReponsesEtat('erreur'); });
    return () => { vivant = false; };
  }, []);

  const variables = useMemo(() => contexteDepuisDossier(ticket, user), [ticket, user]);

  // Liste du bouton « Réponses types » (recherche libre).
  const filteredQuickResponses = useMemo(
    () => filtrerReponsesTypes(reponsesTypes, quickResponseSearch),
    [reponsesTypes, quickResponseSearch],
  );
  // Suggestions de la commande « / » (huit au plus).
  const suggestions = useMemo(
    () => (commande ? filtrerReponsesTypes(reponsesTypes, commande.requete).slice(0, 8) : []),
    [commande, reponsesTypes],
  );

  // Sauvegarde du brouillon à chaque frappe (un texte vide l'efface).
  useEffect(() => {
    ecrireBrouillon(ticketId, { texte: newMessage, interne: isInternal });
  }, [ticketId, newMessage, isInternal]);

  useEffect(() => { loadMessages(); }, [ticketId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, isAITyping]);

  // Relecture demandée par la fiche (dossier modifié ailleurs) : sans écran de
  // chargement, le brouillon n'est pas touché.
  const premiereRelecture = useRef(true);
  useEffect(() => {
    if (premiereRelecture.current) { premiereRelecture.current = false; return; }
    loadMessages(true);
  }, [versionRelecture]);

  // Messages poussés dans la salle du dossier : ajoutés sans doublon.
  const clientIdRef = useRef(ticket?.customerId);
  clientIdRef.current = ticket?.customerId;
  useEffect(() => {
    const surMessage = (data: unknown) => {
      const m = messageDepuisSocket(data, ticketId, clientIdRef.current);
      if (m) setMessages(prev => ajouterMessageSansDoublon(prev, m));
    };
    adminSocketService.on('message:new', surMessage);
    return () => adminSocketService.off('message:new', surMessage);
  }, [ticketId]);

  // Indicateur « l'assistant rédige » : diffusé par le serveur pendant la
  // génération d'une réponse automatique (ai/parole.ts, broadcastAITyping).
  useEffect(() => {
    setIsAITyping(false);
    let arret: ReturnType<typeof setTimeout> | undefined;
    const surFrappe = (data: unknown) => {
      const d = data as { ticketId?: unknown; isTyping?: unknown } | null;
      if (!d || d.ticketId !== ticketId) return;
      setIsAITyping(d.isTyping === true);
      // Filet : un « fin de frappe » perdu n'immobilise pas l'indicateur.
      if (arret) clearTimeout(arret);
      if (d.isTyping === true) arret = setTimeout(() => setIsAITyping(false), 60_000);
    };
    adminSocketService.on('ai:typing', surFrappe);
    return () => {
      if (arret) clearTimeout(arret);
      adminSocketService.off('ai:typing', surFrappe);
    };
  }, [ticketId]);

  const loadMessages = async (silencieux = false) => {
    try {
      if (!silencieux) setIsLoading(true);
      const data = await AdminApi.getTicketMessages(ticketId);
      setMessages(data);

      // Plus de message automatique déclenché depuis l'écran : le serveur
      // applique la politique de parole (paramètres, plafond, pause).
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    } finally {
      if (!silencieux) setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && files.length === 0) || isSending) return;
    setIsSending(true);
    setSendError(null);
    try {
      let attachmentIds: string[] = [];
      if (files.length > 0) {
        const uploaded = await AdminApi.uploadFiles(files);
        attachmentIds = uploaded.map(a => a.id);
      }

      const message = await AdminApi.sendMessage(ticketId, newMessage, isInternal, attachmentIds);
      // Le socket peut avoir déjà livré ce message : pas de doublon.
      setMessages(prev => ajouterMessageSansDoublon(prev, message));
      setNewMessage('');
      setFiles([]);
      // Envoi réussi : le brouillon n'a plus lieu d'être.
      effacerBrouillon(ticketId);
    } catch (error) {
      console.error('Erreur envoi message:', error);
      // Le brouillon est conservé : rien n'est perdu.
      setSendError(error instanceof Error ? error.message : "L'envoi a échoué");
    } finally {
      setIsSending(false);
    }
  };

  // Bouton « Réponses types » : le texte choisi remplace la saisie vide, ou
  // s'ajoute à la suite d'un texte déjà commencé.
  const handleQuickResponse = (content: string) => {
    const texte = substituerVariables(content, variables);
    setNewMessage(prev => (prev.trim() ? `${prev.replace(/\s+$/, '')}\n\n${texte}` : texte));
    setShowQuickResponses(false);
    setQuickResponseSearch('');
  };

  // Commande « / » : la ligne « /… » est remplacée par la réponse choisie.
  const choisirSuggestion = (r: ReponseType) => {
    if (!commande) return;
    const { texte, curseur } = insererReponse(newMessage, commande, substituerVariables(r.content, variables));
    setNewMessage(texte);
    setCommande(null);
    requestAnimationFrame(() => {
      const zone = zoneSaisieRef.current;
      if (zone) { zone.focus(); zone.setSelectionRange(curseur, curseur); }
    });
  };

  // Relit la commande « / » à la position du curseur.
  const suivreCurseur = (zone: HTMLTextAreaElement) => {
    const c = detecterCommandeSlash(zone.value, zone.selectionStart ?? zone.value.length);
    setCommande(prev => {
      if (!c) return null;
      if (!prev || prev.requete !== c.requete) setIndexSuggestion(0);
      return c;
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)].slice(0, 5));
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Messages triés ; ceux de l'assistant automatique sont repérés comme tels.
  const allMessages = useMemo(
    () => [...messages]
      .map(m => ({ ...m, isAI: estMessageAssistant(m) }))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [messages],
  );
  // Pourquoi l'assistant automatique ne parle plus au client sur ce dossier.
  const pauseAssistant = libellePauseAssistant(ticket);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-graphite">Chargement des messages...</p>
        </div>
      </div>
    );
  }

  return (
    // Sous 1024 px, la conversation prend presque tout l'écran visible (en-tête
    // de l'admin déduit) : on la fait défiler jusqu'à elle, puis on lit et on
    // écrit sans que la zone de saisie n'écrase les messages.
    // Depuis l'agrandissement de la zone de réponse (19/10/2026), la conversation
    // prend aussi presque tout l'écran sur ordinateur : messages et réponse
    // tiennent ensemble une fois la page défilée jusqu'à elle.
    <div className="flex flex-col h-[calc(100vh-6rem)] supports-[height:100dvh]:h-[calc(100dvh-6rem)] min-h-[560px] max-lg:min-h-[420px] bg-brume rounded-lg overflow-hidden">
      {/* L'interrupteur IA local et le compteur de réponses IA ont été retirés :
          ils n'agissaient sur rien. La parole de l'IA se règle dans Paramètres. */}
      {pauseAssistant && (
        <div role="status" className="px-4 py-2 bg-white border-b border-trait text-xs text-graphite flex items-center gap-2">
          <Brain className="w-3.5 h-3.5 text-[#7C3AED] shrink-0" aria-hidden="true" />
          <span>{pauseAssistant}</span>
        </div>
      )}

      {/* Légende des couleurs : mêmes pastilles que les avatars des bulles. */}
      <div aria-label="Légende des couleurs" className="px-4 py-2 bg-brume border-b border-trait flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px]">
        {(Object.keys(COULEURS_AUTEUR) as Array<keyof typeof COULEURS_AUTEUR>).map(cle => (
          <div key={cle} data-auteur={cle} className="flex items-center gap-1.5">
            <div aria-hidden="true" className={`w-3 h-3 rounded-full ${COULEURS_AUTEUR[cle].pastille}`} />
            <span className="text-graphite">{COULEURS_AUTEUR[cle].libelle}</span>
          </div>
        ))}
        <div data-auteur="note" className="flex items-center gap-1.5">
          <div aria-hidden="true" className="w-3 h-3 rounded border-2 border-dashed border-slate-400" />
          <span className="text-graphite">Note interne</span>
        </div>
      </div>

      {/* Messages area : garde au moins 8 rem quand la zone de réponse grandit. */}
      <div className="flex-1 min-h-[8rem] overflow-y-auto p-4 space-y-4">
        {allMessages.length === 0 && !isAITyping ? (
          <div className="flex flex-col items-center justify-center h-full text-graphite/70">
            <MessageSquare className="w-12 h-12 mb-3" />
            <p className="text-sm">Aucun message pour l'instant</p>
            <p className="text-xs">Écrivez au client ou ajoutez une note interne ci-dessous.</p>
          </div>
        ) : (
          <>
            {allMessages.map(m => {
              const isAIMessage = m.isAI;
              const isOwnMessage = !isAIMessage && m.authorId === user?.id;
              const isInternalNote = !isAIMessage && (m as TicketMessage).isInternal;
              const authorRole = !isAIMessage ? (m as TicketMessage).author?.role : null;
              const isCustomerMessage = !isAIMessage && authorRole === 'CUSTOMER';
              const isOperatorMessage = !isAIMessage && !isOwnMessage && !isCustomerMessage && !isAIMessage;

              // Définir les styles selon le type de message
              const getMessageStyles = () => {
                if (isAIMessage) return {
                  auteur: 'ia',
                  bubble: 'bg-[#F5F0FF] border-2 border-[#C4B5FD]',
                  avatar: COULEURS_AUTEUR.ia.pastille,
                  avatarIcon: <Brain className="w-4 h-4 text-white" />,
                  headerColor: 'text-[#5B21B6]',
                  timeColor: 'text-[#7C3AED]',
                  textColor: 'text-[#2E1065]',
                  label: 'Assistant IA',
                  labelIcon: <Sparkles className="w-3.5 h-3.5" />,
                };
                if (isInternalNote) return {
                  auteur: 'note',
                  bubble: 'bg-brume border-2 border-slate-400 border-dashed',
                  avatar: 'bg-slate-200',
                  avatarIcon: <Lock className="w-4 h-4 text-graphite" />,
                  headerColor: 'text-encre',
                  timeColor: 'text-graphite',
                  textColor: 'text-encre',
                  label: 'NOTE INTERNE',
                  labelIcon: <Lock className="w-3.5 h-3.5" />,
                };
                if (isCustomerMessage) return {
                  auteur: 'client',
                  bubble: 'bg-amber-50 border-2 border-amber-300',
                  avatar: COULEURS_AUTEUR.client.pastille,
                  avatarIcon: <User className="w-4 h-4 text-white" />,
                  headerColor: 'text-amber-700',
                  timeColor: 'text-amber-600',
                  textColor: 'text-amber-900',
                  label: 'Client',
                  labelIcon: <User className="w-3.5 h-3.5" />,
                };
                if (isOwnMessage) return {
                  auteur: 'vous',
                  bubble: `${COULEURS_AUTEUR.vous.pastille} text-white`,
                  avatar: 'bg-blue-200',
                  avatarIcon: null,
                  headerColor: 'text-white/90',
                  timeColor: 'text-white/70',
                  textColor: 'text-white',
                  label: 'Vous',
                  labelIcon: null,
                };
                // Autre opérateur
                return {
                  auteur: 'operateur',
                  bubble: 'bg-emerald-50 border-2 border-emerald-300',
                  avatar: COULEURS_AUTEUR.operateur.pastille,
                  avatarIcon: <Headphones className="w-4 h-4 text-white" />,
                  headerColor: 'text-emerald-700',
                  timeColor: 'text-emerald-600',
                  textColor: 'text-emerald-900',
                  label: 'Opérateur',
                  labelIcon: <Headphones className="w-3.5 h-3.5" />,
                };
              };

              const styles = getMessageStyles();

              return (
                <div key={m.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                  {/* Avatar */}
                  {!isOwnMessage && (
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center mr-2 flex-shrink-0 ${styles.avatar}`}>
                      {styles.avatarIcon || (
                        <span className="text-xs font-bold text-encre">
                          {getInitials((m as TicketMessage).author?.displayName, (m as TicketMessage).author?.email)}
                        </span>
                      )}
                    </div>
                  )}

                  <div data-auteur={styles.auteur} className={`max-w-[75%] max-sm:max-w-[85%] min-w-0 break-words ${styles.bubble} rounded-card ${isOwnMessage ? 'rounded-br-sm' : 'rounded-bl-sm'} p-4`}>

                    {/* Badge type de message */}
                    {(isAIMessage || isInternalNote || isCustomerMessage || isOperatorMessage) && (
                      <div className={`flex items-center gap-1.5 text-xs font-semibold ${styles.headerColor} mb-2 pb-2 border-b ${
                        isAIMessage ? 'border-[#DDD6FE]' :
                        isInternalNote ? 'border-trait' :
                        isCustomerMessage ? 'border-amber-200' :
                        'border-emerald-200'
                      }`}>
                        {styles.labelIcon}
                        <span>{isAIMessage ? 'Assistant IA' : isCustomerMessage ? 'Client' : isInternalNote ? 'NOTE INTERNE' : (m as TicketMessage).author?.displayName || 'Opérateur'}</span>
                        {isAIMessage && <span className="ml-auto font-normal text-[#7C3AED]">message automatique</span>}
                      </div>
                    )}

                    {/* Header pour ses propres messages */}
                    {isOwnMessage && (
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-semibold ${styles.headerColor}`}>Vous</span>
                        <span className={`text-[10px] ${styles.timeColor}`}>
                          {formatTime(m.createdAt)}
                        </span>
                      </div>
                    )}

                    {/* Timestamp pour les autres messages */}
                    {!isOwnMessage && (
                      <div className="flex justify-end mb-1">
                        <span className={`text-[10px] ${styles.timeColor}`}>
                          {formatTime(m.createdAt)}
                        </span>
                      </div>
                    )}

                    {/* Content */}
                    <p className={`text-sm whitespace-pre-wrap leading-relaxed ${styles.textColor}`}>
                      {m.content}
                    </p>

                    {/* Réponse par e-mail : historique cité retiré, l'e-mail complet reste consultable */}
                    {(m as TicketMessage).emailComplet && (m as TicketMessage).ticketId && (
                      <LienEmailComplet ticketId={(m as TicketMessage).ticketId} messageId={m.id} />
                    )}

                    {/* Attachments */}
                    {(m as TicketMessage).attachments && (m as TicketMessage).attachments!.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {(m as TicketMessage).attachments!.map(att => (
                          <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer"
                            className={`flex items-center gap-2 p-2 rounded-lg text-xs ${isOwnMessage ? 'bg-white/10 hover:bg-white/20' : 'bg-brume hover:bg-brume'} transition-colors`}>
                            <FileText className="w-4 h-4" />
                            <span className="truncate flex-1">{att.fileName}</span>
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Avatar pour ses propres messages */}
                  {isOwnMessage && !isInternalNote && (
                    <div className="w-9 h-9 rounded-full bg-blue-400 flex items-center justify-center ml-2 flex-shrink-0">
                      <span className="text-xs font-bold text-white">{getInitials(user?.displayName, user?.email)}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* AI Typing indicator */}
            {isAITyping && (
              <div className="flex justify-start">
                <div className={`w-8 h-8 rounded-full ${COULEURS_AUTEUR.ia.pastille} flex items-center justify-center mr-2 flex-shrink-0`}>
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <div className="bg-[#F5F0FF] border border-[#DDD6FE] rounded-card rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#A78BFA] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#A78BFA] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#A78BFA] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-[#6D28D9]">L'assistant automatique rédige une réponse au client…</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick responses dropdown amélioré */}
      {showQuickResponses && (
        <div
          ref={quickResponsesRef}
          className="absolute bottom-24 left-4 right-4 bg-white rounded-lg shadow-flottant border border-trait z-20 overflow-hidden"
          style={{ maxHeight: '400px' }}
        >
          {/* Header avec recherche */}
          <div className="p-3 bg-blue-50 border-b border-trait">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-encre flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-600" />
                Réponses types
              </h4>
              <button
                onClick={() => {
                  setShowQuickResponses(false);
                  setQuickResponseSearch('');
                }}
                className="p-1 hover:bg-brume rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-graphite" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-graphite/70" />
              <input
                type="text"
                placeholder="Rechercher par titre, étiquette ou contenu"
                value={quickResponseSearch}
                onChange={e => setQuickResponseSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-trait rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Liste des réponses */}
          <div className="overflow-y-auto" style={{ maxHeight: '300px' }}>
            {reponsesEtat === 'chargement' ? (
              <div className="p-6 text-center text-graphite text-sm">Chargement des réponses types…</div>
            ) : reponsesEtat === 'erreur' ? (
              <div role="alert" className="p-6 text-center text-red-700 text-sm">Les réponses types n'ont pas pu être chargées.</div>
            ) : filteredQuickResponses.length === 0 ? (
              <div className="p-6 text-center text-graphite/70">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{reponsesTypes.length === 0 ? 'Aucune réponse type en base : ajoutez-en dans Paramètres > Réponses types.' : 'Aucune réponse trouvée'}</p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredQuickResponses.map(qr => (
                  <button
                    key={qr.id}
                    onClick={() => handleQuickResponse(qr.content)}
                    className="w-full text-left p-3 hover:bg-brume rounded-lg transition-colors group border border-transparent hover:border-trait"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-encre truncate mb-1">{qr.title}</p>
                        <p className="text-xs text-graphite line-clamp-2 leading-relaxed">
                          {substituerVariables(qr.content, variables).substring(0, 140)}
                        </p>
                      </div>
                      {qr.tags.length > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap bg-brume text-graphite">
                          {qr.tags[0]}
                        </span>
                      )}
                    </div>
                    {/* Aperçu au survol, variables remplacées */}
                    <div className="hidden group-hover:block mt-2 p-2 bg-brume rounded-lg border border-trait text-xs text-graphite whitespace-pre-wrap max-h-24 overflow-y-auto">
                      {substituerVariables(qr.content, variables)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pied : aide */}
          <div className="p-2 bg-brume border-t border-trait text-center">
            <p className="text-[10px] text-graphite">
              Astuce : tapez <span className="tnum bg-white px-1 rounded">/</span> en début de ligne dans la zone de saisie • {'{{ticketRef}}'}, {'{{customerName}}'} et {'{{agentName}}'} sont remplacées automatiquement
            </p>
          </div>
        </div>
      )}

      {/* File preview */}
      {files.length > 0 && (
        <div className="px-4 py-2 bg-white border-t border-trait flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 bg-brume rounded-lg px-3 py-1.5 text-xs">
              <Paperclip className="w-3.5 h-3.5 text-graphite/70" />
              <span className="truncate max-w-[120px]">{f.name}</span>
              <button onClick={() => removeFile(i)} className="text-graphite/70 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="p-4 max-sm:p-3 bg-white border-t border-trait">
        {/* Actions row */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 max-sm:mb-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsInternal(!isInternal)}
              aria-pressed={isInternal}
              className="flex items-center gap-2 cursor-pointer group max-lg:min-h-10"
            >
              <div className={`relative w-10 h-5 rounded-full transition-colors ${isInternal ? 'bg-indigo-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isInternal ? 'translate-x-5' : ''}`} />
              </div>
              <Lock className={`w-4 h-4 ${isInternal ? 'text-indigo-600' : 'text-graphite/70'}`} />
              <span className={`text-sm ${isInternal ? 'text-indigo-600 font-medium' : 'text-graphite'}`}>Note interne</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => setShowQuickResponses(!showQuickResponses)}
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 px-3 py-1.5 max-lg:min-h-10 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200">
              <Zap className="w-3.5 h-3.5" />Réponses types
            </button>
            {/* Passe la zone de réponse en grand (60 % de l'écran), et retour. */}
            <button type="button" onClick={() => setZoneAgrandie(a => !a)} aria-pressed={zoneAgrandie}
              title={zoneAgrandie ? 'Revenir à la taille automatique' : 'Agrandir la zone de réponse'}
              className="flex items-center gap-1.5 text-xs text-graphite hover:text-encre px-3 py-1.5 max-lg:min-h-10 hover:bg-brume rounded-lg transition-colors border border-trait">
              {zoneAgrandie ? <Minimize2 className="w-3.5 h-3.5" aria-hidden="true" /> : <Maximize2 className="w-3.5 h-3.5" aria-hidden="true" />}
              {zoneAgrandie ? 'Réduire' : 'Agrandir'}
            </button>
          </div>
        </div>

        {/* Input + buttons */}
        <div className="flex gap-2">
          <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" multiple accept="image/*,.pdf,.doc,.docx" />
          <button onClick={() => fileInputRef.current?.click()} aria-label="Joindre un fichier" className="p-2.5 text-graphite/70 hover:text-graphite hover:bg-brume rounded-lg transition-colors flex-shrink-0">
            <Paperclip className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0 relative">
            {/* Suggestions de la commande « / » */}
            {commande && (
              <div role="listbox" aria-label="Réponses types" className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-flottant border border-trait z-20 overflow-hidden">
                <div className="px-3 py-2 bg-brume border-b border-trait text-[11px] text-graphite">
                  Réponses types{commande.requete ? <> contenant « <span className="font-semibold text-encre">{commande.requete}</span> »</> : ''} · ↑ ↓ pour choisir, Entrée pour insérer, Échap pour fermer
                </div>
                {reponsesEtat === 'chargement' ? (
                  <p className="px-3 py-3 text-sm text-graphite">Chargement…</p>
                ) : reponsesEtat === 'erreur' ? (
                  <p role="alert" className="px-3 py-3 text-sm text-red-700">Les réponses types n'ont pas pu être chargées.</p>
                ) : suggestions.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-graphite">Aucune réponse type ne correspond.</p>
                ) : (
                  <ul className="max-h-64 overflow-y-auto py-1">
                    {suggestions.map((r, i) => (
                      <li key={r.id} role="option" aria-selected={i === indexSuggestion}>
                        <button
                          type="button"
                          onMouseDown={e => { e.preventDefault(); choisirSuggestion(r); }}
                          onMouseEnter={() => setIndexSuggestion(i)}
                          className={`w-full text-left px-3 py-2 ${i === indexSuggestion ? 'bg-sapin-50' : 'hover:bg-brume'}`}
                        >
                          <p className="text-sm font-medium text-encre truncate">{r.title}</p>
                          <p className="text-xs text-graphite truncate">{substituerVariables(r.content, variables).replace(/\s+/g, ' ').slice(0, 110)}</p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <textarea
              ref={zoneSaisieRef}
              aria-label={isInternal ? 'Note interne' : 'Message au client'}
              placeholder={isInternal ? "Note interne, visible uniquement par l'équipe (Ctrl+Entrée pour enregistrer)" : 'Message au client (Ctrl+Entrée pour envoyer, « / » en début de ligne pour une réponse type)'}
              value={newMessage}
              onChange={e => { setNewMessage(e.target.value); suivreCurseur(e.target); }}
              onClick={e => suivreCurseur(e.currentTarget)}
              onKeyUp={e => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') suivreCurseur(e.currentTarget); }}
              onBlur={() => setCommande(null)}
              onKeyDown={e => {
                // Liste « / » ouverte : flèches pour choisir, Entrée pour insérer, Échap pour fermer.
                if (commande && !e.ctrlKey && !e.metaKey) {
                  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setCommande(null); return; }
                  if (suggestions.length > 0) {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setIndexSuggestion(i => (i + 1) % suggestions.length); return; }
                    if (e.key === 'ArrowUp') { e.preventDefault(); setIndexSuggestion(i => (i - 1 + suggestions.length) % suggestions.length); return; }
                    if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choisirSuggestion(suggestions[Math.min(indexSuggestion, suggestions.length - 1)]); return; }
                  }
                }
                // Entrée seule = retour à la ligne. L'envoi demande Ctrl+Entrée
                // (Cmd+Entrée sur Mac) : un message au client part sans retour.
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  if (newMessage.trim() || files.length > 0) {
                    handleSend();
                  }
                }
              }}
              // Hauteur gérée par useHauteurAuto (features/tickets/zoneSaisie.ts) :
              // 8 lignes au moins (5 sur téléphone), jusqu'à la moitié de l'écran,
              // puis défilement. La poignée en bas à droite l'agrandit à la main.
              rows={telephone ? 5 : 8}
              onMouseUp={noterRedimensionnement}
              data-agrandie={zoneAgrandie || undefined}
              style={{ maxHeight: '85vh' }}
              className={`block w-full rounded-lg border border-trait bg-white py-2.5 px-3.5 text-sm leading-5 max-sm:text-base max-sm:leading-6 text-encre placeholder:text-graphite/70 focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none resize-y transition-colors duration-150 ${isInternal ? 'bg-indigo-50 border-indigo-200 focus:border-indigo-400' : ''}`}
            />
            <p className="mt-1 text-[11px] text-graphite">
              {isInternal ? 'Note interne' : 'Visible par le client'}<span className="max-lg:hidden"> · Ctrl+Entrée (Cmd+Entrée sur Mac) pour envoyer</span> · brouillon conservé pour ce dossier
            </p>
            {sendError && (
              <p role="alert" className="mt-1 text-xs text-red-700">Envoi impossible : {sendError}. Le texte est conservé.</p>
            )}
          </div>
          <Button variant={isInternal ? 'secondary' : 'primary'} onClick={handleSend} disabled={isSending || (!newMessage.trim() && files.length === 0)} aria-label={isInternal ? 'Enregistrer la note' : 'Envoyer le message'} className={`self-end max-lg:min-w-11 max-lg:min-h-11 ${isInternal ? 'bg-indigo-600 text-white hover:bg-indigo-700' : ''}`}>
            {isSending ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// DOCUMENTS PANEL
// ============================================

const DocumentsPanel: React.FC<{ ticketId: string; attachments: Attachment[] }> = ({ ticketId, attachments }) => {
  const [documents, setDocuments] = useState<Attachment[]>(attachments);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Charger tous les documents (ticket + messages)
  useEffect(() => {
    const loadAllDocuments = async () => {
      try {
        setIsLoading(true);
        // Récupérer les messages du ticket pour avoir leurs pièces jointes
        const messages = await AdminApi.getTicketMessages(ticketId);

        // Collecter toutes les pièces jointes
        const messageAttachments = messages.flatMap(m => m.attachments || []);
        const allDocs = [...attachments, ...messageAttachments];

        // Dédupliquer par ID
        const uniqueDocs = allDocs.filter((doc, index, self) =>
          index === self.findIndex(d => d.id === doc.id)
        );

        setDocuments(uniqueDocs);
      } catch (error) {
        console.error('Erreur chargement documents:', error);
        setDocuments(attachments);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllDocuments();
  }, [ticketId, attachments]);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image className="w-8 h-8 text-green-500" />;
    if (mimeType.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="w-8 h-8 text-blue-500" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileText className="w-8 h-8 text-green-600" />;
    return <File className="w-8 h-8 text-graphite" />;
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = async (files: File[]) => {
    setIsUploading(true);
    setErreur(null);
    try {
      // Rattachés au dossier : ils restent visibles au prochain chargement.
      const uploaded = await AdminApi.uploadFiles(files, { ticketId });
      setDocuments(prev => [...prev, ...uploaded]);
    } catch (error) {
      console.error('Erreur upload:', error);
      setErreur(`Envoi impossible : ${error instanceof Error ? error.message : 'erreur inconnue'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (doc: Attachment) => {
    if (!window.confirm(`Supprimer définitivement « ${doc.fileName} » de ce dossier ?`)) return;
    setErreur(null);
    try {
      await AdminApi.deleteAttachment(doc.id);
      // Retiré de l'écran seulement une fois la suppression confirmée par le serveur.
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Erreur suppression:', error);
      setErreur(`Suppression impossible : ${error instanceof Error ? error.message : 'erreur inconnue'}. Le document est conservé.`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-graphite">Chargement des documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {erreur && (
        <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{erreur}</span>
          <button onClick={() => setErreur(null)} aria-label="Masquer le message" className="text-red-700 hover:text-red-900"><X className="w-4 h-4" /></button>
        </div>
      )}
      {/* Zone de drop */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg p-8 max-sm:p-5 text-center cursor-pointer transition-all duration-200 ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-trait hover:border-gray-400 hover:bg-brume'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        />
        <div className="flex flex-col items-center">
          {isUploading ? (
            <>
              <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-3" />
              <p className="text-sm text-blue-600 font-medium">Upload en cours...</p>
            </>
          ) : (
            <>
              <Upload className="w-12 h-12 text-graphite/70 mb-3" />
              <p className="text-sm font-medium text-encre">
                Glissez-déposez vos fichiers ici
              </p>
              <p className="text-xs text-graphite mt-1">
                ou cliquez pour sélectionner (Images, PDF, Word, Excel)
              </p>
            </>
          )}
        </div>
      </div>

      {/* Liste des documents */}
      {documents.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-graphite flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Documents ({documents.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {documents.map(doc => (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-4 bg-white border border-trait rounded-lg transition-all group"
              >
                <div className="flex-shrink-0">
                  {doc.mimeType.startsWith('image/') ? (
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-brume">
                      <img src={doc.url} alt={doc.fileName} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-brume flex items-center justify-center">
                      {getFileIcon(doc.mimeType)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-encre truncate">{doc.fileName}</p>
                  <p className="text-xs text-graphite">{formatFileSize(doc.sizeBytes)}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 max-lg:opacity-100 transition-opacity">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 max-lg:p-3 text-graphite/70 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Voir"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <a
                    href={doc.url}
                    download={doc.fileName}
                    className="p-2 max-lg:p-3 text-graphite/70 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Télécharger"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-2 max-lg:p-3 text-graphite/70 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-graphite/70">
          <FolderOpen className="w-12 h-12 mx-auto mb-3" />
          <p className="text-sm">Aucun document</p>
          <p className="text-xs mt-1">Ajoutez des documents en les déposant ci-dessus</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// TICKET DETAIL MODAL
// ============================================

// Onglet « Assistant IA » : features/ia/AssistantIAPanel.tsx (16/10/2026).

// Composant SLA Timer
const SLATimer: React.FC<{ deadline: string | Date | null; breached: boolean; compact?: boolean }> = ({ deadline, breached, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [urgencyLevel, setUrgencyLevel] = useState<'safe' | 'warning' | 'critical' | 'breached'>('safe');

  useEffect(() => {
    if (!deadline) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const diff = deadlineDate.getTime() - now.getTime();

      if (breached || diff < 0) {
        setUrgencyLevel('breached');
        const absDiff = Math.abs(diff);
        const hours = Math.floor(absDiff / (1000 * 60 * 60));
        const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeLeft(`-${hours}h${compact ? '' : ` ${minutes}m`}`);
      } else {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (hours < 1) setUrgencyLevel('critical');
        else if (hours < 4) setUrgencyLevel('warning');
        else setUrgencyLevel('safe');

        setTimeLeft(`${hours}h${compact ? '' : ` ${minutes}m`}`);
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(interval);
  }, [deadline, breached, compact]);

  if (!deadline) return null;

  const colors = {
    safe: 'bg-green-100 text-green-700 border-green-300',
    warning: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    critical: 'bg-orange-100 text-orange-700 border-orange-300',
    breached: 'bg-red-100 text-red-700 border-red-300',
  };

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${colors[urgencyLevel]}`}>
        <Timer className="w-3 h-3" />
        {timeLeft}
      </span>
    );
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${colors[urgencyLevel]}`}>
      <Timer className="w-4 h-4" />
      <span className="text-sm font-bold">{timeLeft}</span>
      <span className="text-xs opacity-75">{urgencyLevel === 'breached' ? 'Dépassé' : 'restant'}</span>
    </div>
  );
};

// Les autres dossiers de la même machine : c'est ce qui décide entre réparer
// encore et remplacer. Ne s'affiche que s'il y a un numéro de série et au
// moins un autre dossier.
const Recidive: React.FC<{ ticketId: string }> = ({ ticketId }) => {
  const { ouvrirDossier } = useAdmin();
  const [etat, setEtat] = useState<{ serialNumber: string | null; dossiers: DossierRecherche[] } | null>(null);
  useEffect(() => { let vivant = true; AdminApi.memesSerie(ticketId).then(r => { if (vivant) setEtat(r); }).catch(() => {}); return () => { vivant = false; }; }, [ticketId]);
  if (!etat || !etat.serialNumber || etat.dossiers.length === 0) return null;
  const ouvrir = (id: string) => ouvrirDossier(id);
  return (
    <div className="rounded-lg border border-[#F0D9AE] bg-[#FBF1E1] p-4">
      <p className="text-sm font-bold text-[#7A4A00]">Cette machine a déjà un historique SAV</p>
      <p className="text-xs text-[#7A4A00] mt-0.5">Numéro de série <span className="tnum font-semibold">{etat.serialNumber}</span> · {etat.dossiers.length} autre{etat.dossiers.length > 1 ? 's' : ''} dossier{etat.dossiers.length > 1 ? 's' : ''}. Au troisième passage, la question du remplacement se pose.</p>
      <ul className="mt-3 space-y-1.5">
        {etat.dossiers.map(d => (
          <li key={d.id}>
            <button onClick={() => ouvrir(d.id)} className="w-full text-left flex items-center gap-3 max-sm:gap-2 rounded-md px-2 py-1.5 max-lg:py-2.5 hover:bg-white/70">
              <span className="ref text-sm w-20 shrink-0">{d.ticketRef ?? '—'}</span>
              <span className="flex-1 min-w-0 text-sm text-encre truncate">{d.title}</span>
              <span className="tnum text-xs text-graphite max-sm:hidden">{formatDateParis(d.updatedAt)}</span>
              <BadgeStatut status={d.status} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Fiche d'un dossier : rendue sur sa page (/dossiers/:id) par PageDossier,
// qui charge le dossier et lui transmet chaque mise à jour.
const FicheDossier: React.FC<{
  ticket: Ticket;
  onUpdate: (t: Ticket) => void;
  /** Dossier complet (avec l'historique) déjà lu par la page : pas de seconde lecture à l'ouverture. */
  dejaCharge?: boolean;
}> = ({ ticket, onUpdate, dejaCharge = false }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [editedTicket, setEditedTicket] = useState(ticket);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [documentsCount, setDocumentsCount] = useState(ticket.attachments?.length || 0);
  const [history, setHistory] = useState<TicketHistory[] | null>(dejaCharge ? (ticket.history || []) : null);
  const [historyError, setHistoryError] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferReason, setTransferReason] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [transfertEnCours, setTransfertEnCours] = useState(false);
  const [transfertErreur, setTransfertErreur] = useState<string | null>(null);
  const [transfertAvis, setTransfertAvis] = useState<string | null>(null);
  const [showFusion, setShowFusion] = useState(false);
  const [showRattacher, setShowRattacher] = useState(false);
  const [rattachementAvis, setRattachementAvis] = useState<string | null>(null);
  // Décision sur un dossier « spam possible » (pas un spam / spam).
  const [spamAvis, setSpamAvis] = useState<string | null>(null);
  // Décision sur un rattachement par le domaine (confirmer / refuser la société).
  const [domaineAvis, setDomaineAvis] = useState<string | null>(null);
  // « Pas une demande SAV » : dossier clos sans rien envoyer au client.
  const [horsSavAvis, setHorsSavAvis] = useState<string | null>(null);
  // Archiver (passer en « Fermé ») et désarchiver (« Réouvert ») : fenêtres de confirmation.
  const [archivageOuvert, setArchivageOuvert] = useState(false);
  const [desarchivageOuvert, setDesarchivageOuvert] = useState(false);
  const [archiveAvis, setArchiveAvis] = useState<string | null>(null);
  // Incrémenté quand le dossier a changé ailleurs : la conversation se relit.
  const [versionMessages, setVersionMessages] = useState(0);
  const { users, agents, ouvrirDossier, setCurrentView } = useAdmin();
  const { user } = useAuth();

  // La fiche suit le dossier transmis par sa page (mise à jour sans rechargement).
  useEffect(() => { setEditedTicket(ticket); }, [ticket]);
  // Synthèse IA préparée dès l'ouverture : l'onglet « Assistant IA » s'affiche sans attente.
  useEffect(() => { prechargerAssistantIA(ticket.id); }, [ticket.id]);

  // Dernières valeurs, lues par les gestionnaires du socket (posés une fois).
  const editedRef = useRef(editedTicket);
  editedRef.current = editedTicket;
  // Une fois la page quittée, une réponse tardive ne doit plus rien mettre à jour.
  const monteRef = useRef(true);
  useEffect(() => { monteRef.current = true; return () => { monteRef.current = false; }; }, []);
  const onUpdateRef = useRef<(t: Ticket) => void>(onUpdate);
  onUpdateRef.current = (t: Ticket) => { if (monteRef.current) onUpdate(t); };

  // La liste ne contient pas l'historique : on charge le dossier complet
  // (GET /admin/tickets/:id) à l'ouverture, puis après chaque modification.
  // Le dossier relu est fusionné dans la fiche (modification par un collègue).
  const loadHistory = useCallback(async (fusionnerDossier = false) => {
    try {
      const complet = await AdminApi.getTicketById(ticket.id);
      setHistory(complet.history || []);
      setHistoryError(false);
      if (fusionnerDossier) {
        const fusionne = { ...editedRef.current, ...complet };
        setEditedTicket(fusionne);
        onUpdateRef.current(fusionne);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setHistoryError(true);
    }
  }, [ticket.id]);
  // À l'ouverture, seulement si la page n'a pas déjà lu le dossier complet.
  const premiereLectureFaite = useRef(dejaCharge);
  useEffect(() => {
    if (premiereLectureFaite.current) { premiereLectureFaite.current = false; return; }
    loadHistory();
  }, [loadHistory]);

  // Temps réel : la fiche rejoint la salle du dossier et la quitte avec la page.
  useEffect(() => {
    adminSocketService.joinTicketRoom(ticket.id);
    return () => adminSocketService.leaveTicketRoom(ticket.id);
  }, [ticket.id]);

  useEffect(() => {
    // Dossier modifié (par un collègue, un e-mail, une fusion) : relecture du
    // dossier et des messages, regroupée ; le brouillon n'est pas touché.
    const relecture = creerRegroupeur(DELAI_REGROUPEMENT_MS, () => {
      loadHistory(true);
      setVersionMessages(v => v + 1);
    });
    const surDossierModifie = (data: unknown) => {
      if (decisionDossierModifie(data, ticket.id).relireFiche) relecture.declencher();
    };
    // Champ modifié dans la salle : appliqué tout de suite.
    const surChamp = (data: unknown) => {
      const avant = editedRef.current;
      const apres = fusionnerChampDossier(avant, data);
      if (apres !== avant) {
        setEditedTicket(apres);
        onUpdateRef.current(apres);
      }
    };
    adminSocketService.on('dossier:modifie', surDossierModifie);
    adminSocketService.on('ticket:updated', surChamp);
    // Reconnexion après une coupure : ce qui a changé entre-temps est relu.
    const desabonnerReconnexion = adminSocketService.surReconnexion(() => relecture.declencher());
    return () => {
      relecture.annuler();
      desabonnerReconnexion();
      adminSocketService.off('dossier:modifie', surDossierModifie);
      adminSocketService.off('ticket:updated', surChamp);
    };
  }, [ticket.id, loadHistory]);

  // Nom d'un compte à partir de son identifiant (historique, affectation).
  const nomUtilisateur = useCallback((id: string) => {
    const u = users.find(x => x.id === id) || agents.find(x => x.id === id);
    if (u) return u.displayName || u.email;
    if (user && user.id === id) return user.displayName || user.email;
    return undefined;
  }, [users, agents, user]);

  // Agents disponibles pour transfert : personnel actif, hors comptes techniques.
  const availableAgents = agents.filter(
    u => estAssignable(u) && u.id !== editedTicket.assignedToId
  );

  // Vérifier si le ticket m'appartient
  const isMyTicket = editedTicket.assignedToId === user?.id;

  // Charger le compte total des documents (ticket + messages)
  useEffect(() => {
    const loadDocumentsCount = async () => {
      try {
        const messages = await AdminApi.getTicketMessages(ticket.id);
        const messageAttachments = messages.flatMap(m => m.attachments || []);
        const ticketAttachments = ticket.attachments || [];
        const allIds = new Set([
          ...ticketAttachments.map(a => a.id),
          ...messageAttachments.map(a => a.id)
        ]);
        setDocumentsCount(allIds.size);
      } catch (error) {
        console.error('Erreur chargement count documents:', error);
      }
    };
    loadDocumentsCount();
  }, [ticket.id, ticket.attachments]);

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement
        || e.target instanceof HTMLSelectElement || (e.target instanceof HTMLElement && e.target.isContentEditable)) return;
      // L'en-tête, les notifications, l'assistant et les fenêtres restent
      // utilisables au-dessus de la page : une touche frappée là ne vise pas ce dossier.
      if (!toucheVisePageDossier(e.target)) return;

      // Ctrl+1-5 pour les onglets
      if (e.ctrlKey && e.key >= '1' && e.key <= '5') {
        e.preventDefault();
        const tabs = ['details', 'messages', 'ai-assistant', 'documents', 'history'];
        setActiveTab(tabs[parseInt(e.key) - 1]);
      }
      // R pour résoudre, avec confirmation (une touche frappée par erreur ne doit pas résoudre le dossier)
      if (e.key === 'r' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (editedTicket.status !== TicketStatus.RESOLVED && editedTicket.status !== TicketStatus.CLOSED
          && window.confirm('Marquer ce dossier comme résolu ?')) {
          handleUpdate({ status: TicketStatus.RESOLVED });
        }
      }
      // E pour escalader, avec confirmation
      if (e.key === 'e' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (editedTicket.status !== TicketStatus.ESCALATED
          && window.confirm('Escalader ce dossier ?')) {
          handleUpdate({ status: TicketStatus.ESCALATED });
        }
      }
      // Échap ne ferme plus rien : la fiche est une page, on en sort par « Retour aux dossiers ».
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editedTicket.status]);

  // `assignedToId: null` désaffecte (undefined disparaîtrait du JSON envoyé).
  // Enregistre les changements ; lève l'erreur pour l'appelant qui l'affiche lui-même.
  const enregistrer = async (updates: UpdateTicketPayload) => {
    const updated = await AdminApi.updateTicket(ticket.id, updates);
    // La réponse du PUT ne porte ni l'historique ni les compteurs de la liste :
    // on la fusionne dans le dossier affiché plutôt que de le remplacer.
    const fusionne = { ...editedRef.current, ...updated };
    setEditedTicket(fusionne);
    onUpdate(fusionne);
    loadHistory();
  };

  const handleUpdate = async (updates: UpdateTicketPayload) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await enregistrer(updates);
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      setSaveError(error instanceof Error ? error.message : 'Modification impossible');
    } finally {
      setIsSaving(false);
    }
  };

  // Transfert = réaffectation directe, puis note interne si une raison est saisie.
  const handleTransfer = async () => {
    const agent = agents.find(a => a.id === selectedAgent);
    if (!agent) return;
    setTransfertEnCours(true);
    setTransfertErreur(null);
    try {
      const { dossier, noteEnregistree } = await transfererDossier(ticket.id, agent, transferReason);
      const fusionne = { ...editedRef.current, ...dossier };
      setEditedTicket(fusionne);
      onUpdate(fusionne);
      loadHistory();
      setShowTransferModal(false);
      setTransferReason('');
      setSelectedAgent('');
      setTransfertAvis(noteEnregistree === false
        ? `Dossier transféré à ${agent.displayName}, mais la note interne n'a pas pu être enregistrée.`
        : `Dossier transféré à ${agent.displayName}.`);
      setVersionMessages(v => v + 1);
    } catch (error) {
      console.error('Erreur transfert:', error);
      setTransfertErreur(error instanceof Error ? error.message : 'Le transfert a échoué');
    } finally {
      setTransfertEnCours(false);
    }
  };

  // Changement de statut (barre, parcours, carte « Statut ») : archiver
  // (vers « Fermé ») et désarchiver (« Fermé » → « Réouvert ») passent par leur
  // fenêtre de confirmation ; le reste s'enregistre tout de suite.
  const changerStatut = (vers: TicketStatus) => {
    if (vers === editedTicket.status) return;
    if (vers === TicketStatus.CLOSED) { setArchivageOuvert(true); return; }
    if (editedTicket.status === TicketStatus.CLOSED && vers === TicketStatus.REOPENED) { setDesarchivageOuvert(true); return; }
    handleUpdate({ status: vers });
  };

  const handleTakeOver = async () => {
    if (!user) return;
    await handleUpdate({ assignedToId: user.id });
  };

  // Auto-assignation : agent ou superviseur actif le moins chargé, hors comptes techniques.
  const candidatsAuto = agentsPourAutoAssignation(agents);
  const autoAssigner = () => {
    const [choisi] = repartirSurLeMoinsCharge(candidatsAuto, 1);
    if (choisi) handleUpdate({ assignedToId: choisi.id });
  };

  const assignee = editedTicket.assignedToId
    ? (users.find(u => u.id === editedTicket.assignedToId) || agents.find(u => u.id === editedTicket.assignedToId)
      || (editedTicket.assignedTo?.id === editedTicket.assignedToId ? editedTicket.assignedTo : undefined))
    : undefined;
  // Options d'affectation : personnel affectable, plus l'affecté actuel s'il ne l'est plus (inactif…).
  const optionsAffectation = [
    ...agents.filter(estAssignable),
    ...(assignee && !agents.some(a => a.id === assignee.id && estAssignable(a)) ? [assignee] : []),
  ];
  const ticketAge = Math.floor((Date.now() - new Date(ticket.createdAt).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <>
      {/* Header amélioré. Sur téléphone, empilé : référence et pastilles, titre
          sur toute la largeur, puis délai et conseiller en dessous. Les marges
          négatives suivent le retrait de la page (p-3, puis p-5 dès 640 px). */}
      <div className={`-mt-5 -mx-5 px-5 pb-4 max-sm:-mt-3 max-sm:-mx-3 max-sm:px-3 max-sm:pb-3 bg-white border-b border-trait ${getStatusRail(editedTicket.status)}`}>
        <div className="flex items-start justify-between max-sm:flex-col max-sm:items-stretch max-sm:gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="ref text-2xl max-sm:text-xl">{ticket.ticketRef || ticket.ticketNumber}</span>
              <BadgeStatut status={editedTicket.status} taille="md" />
              <Badge className={`${getPriorityColor(editedTicket.priority)} text-sm px-3 py-1`}>{PRIORITY_LABELS[editedTicket.priority]}</Badge>
              <Badge className="bg-brume text-graphite text-sm px-3 py-1">{ISSUE_TYPE_LABELS[editedTicket.issueType]}</Badge>
              <BadgeSpamPossible dossier={editedTicket} className="text-sm px-3 py-1" />
              <BadgeDomaineAConfirmer dossier={editedTicket} className="text-sm px-3 py-1" />
              {ticketAge > 0 && <span className="text-xs text-graphite bg-brume px-2 py-1 rounded">{ticketAge}j</span>}
              {isSaving && <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />}
              {saveError && <span role="alert" className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded">Modification non enregistrée : {saveError}</span>}
            </div>
            <h2 className="text-lg font-semibold text-encre mb-2 break-words">{ticket.title}</h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-graphite">
              {/* Client : compte ou contact */}
              {(editedTicket.customer || editedTicket.contactName || editedTicket.companyName) && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {editedTicket.customer?.displayName || editedTicket.contactName || editedTicket.companyName}
                </span>
              )}
              {editedTicket.companyName && editedTicket.customer && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {editedTicket.companyName}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDateTime(ticket.createdAt)}
              </span>
              <MentionDomaineConfirme dossier={editedTicket} />
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 max-sm:flex-row max-sm:flex-wrap max-sm:items-center max-sm:justify-between">
            {/* SLA Timer */}
            <SLATimer deadline={ticket.slaDeadline || null} breached={editedTicket.slaBreached || false} />

            {/* Agent assigné */}
            {assignee ? (
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-trait">
                <div className="w-7 h-7 bg-petrol-600 rounded-full flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">{getInitials(assignee.displayName, assignee.email)}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-encre">{assignee.displayName}</p>
                  <p className="text-[10px] text-graphite">{ROLE_CONFIG[assignee.role]?.label}</p>
                </div>
              </div>
            ) : activeTab === 'details' ? (
              // Onglet Détails : les boutons sont dans la carte « Assigné à » (pas de doublon).
              <Badge className="bg-[#FBF1E1] text-[#7A4A00] border border-dashed border-[#D99A2B] text-sm px-3 py-1">Non assigné</Badge>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="primary" size="sm" onClick={handleTakeOver}>
                  <User className="w-4 h-4 mr-1" />
                  Prendre en charge
                </Button>
                {/* Ex-barre « Actions rapides » : seule entrée pour confier le
                    dossier au conseiller le moins chargé. */}
                <Button variant="secondary" size="sm" onClick={autoAssigner} disabled={candidatsAuto.length === 0}
                  title={candidatsAuto.length === 0 ? 'Aucun agent actif disponible' : 'Confier le dossier au conseiller actif le moins chargé'}>
                  <Zap className="w-4 h-4 mr-1 text-[#B26A00]" />Auto-assigner
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Parcours du dossier : où il en est, ce que ça veut dire, et les
            étapes vers lesquelles la barre ci-dessous permet déjà d'aller. */}
        <ParcoursStatut status={editedTicket.status} onChanger={changerStatut} occupe={isSaving} />

        {/* Dossier « spam possible » : le personnel tranche avant toute suite. */}
        <BandeauSpamPossible
          dossier={editedTicket}
          surDecision={(maj, avis) => {
            const fusionne = { ...editedRef.current, ...maj } as Ticket;
            setEditedTicket(fusionne);
            onUpdate(fusionne);
            setSpamAvis(avis);
            loadHistory(true);
          }}
        />

        {/* Expéditeur sans compte relié à une société par son domaine : à confirmer.
            Tout le personnel peut refuser ; relier le dossier à un compte client
            en confirmant est réservé à l'encadrement, comme « Rattacher à un compte client ». */}
        <BandeauDomaineAConfirmer
          dossier={editedTicket}
          encadrement={estEncadrement(user?.role)}
          surDecision={(maj, avis) => {
            const fusionne = { ...editedRef.current, ...maj } as Ticket;
            setEditedTicket(fusionne);
            onUpdate(fusionne);
            setDomaineAvis(avis);
            loadHistory(true);
          }}
        />

        {/* Barre d'actions rapides améliorée */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {/* Changements de statut (shared/statuts.ts, actionsStatut : la même
              règle que le parcours). Chaque bouton porte l'icône et la couleur
              du statut vers lequel il mène. */}
          {/* Archiver et Désarchiver ont leurs boutons, plus loin (à côté de « Pas une demande SAV »). */}
          {actionsStatut(editedTicket.status).filter(a => a.vers !== TicketStatus.ESCALATED && a.vers !== TicketStatus.CLOSED
            && !(editedTicket.status === TicketStatus.CLOSED && a.vers === TicketStatus.REOPENED)).map(a => (
            <Button key={a.vers} variant={a.principale ? 'primary' : 'secondary'} size="sm" onClick={() => handleUpdate({ status: a.vers })}
              title={`Passer le dossier en « ${STATUS_LABELS[a.vers]} »`}>
              <IconeStatut status={a.vers} className="w-4 h-4 mr-1" style={a.principale ? undefined : { color: couleurStatut(a.vers) }} />
              {a.libelle}{a.raccourci && <span className="ml-1 text-xs opacity-70 max-lg:hidden">({a.raccourci})</span>}
            </Button>
          ))}

          <div className="h-6 w-px bg-trait mx-1 max-sm:hidden" />

          {/* Actions secondaires */}
          {actionsStatut(editedTicket.status).filter(a => a.vers === TicketStatus.ESCALATED).map(a => (
            <Button key={a.vers} variant="ghost" size="sm" onClick={() => handleUpdate({ status: a.vers })} className="text-[#8A1C16] hover:bg-[#FBE9E7]"
              title="Remonter le dossier à un responsable">
              <IconeStatut status={a.vers} className="w-4 h-4 mr-1" />{a.libelle}{a.raccourci && <span className="ml-1 text-xs opacity-70 max-lg:hidden">({a.raccourci})</span>}
            </Button>
          ))}

          {editedTicket.status !== TicketStatus.CLOSED && (
            <Button variant="ghost" size="sm" title="Fusionner ce dossier dans un autre (même panne envoyée deux fois)" onClick={() => setShowFusion(true)} className="text-graphite">
              <GitMerge className="w-4 h-4 mr-1" />Fusionner
            </Button>
          )}

          {/* Pas une demande SAV : clos sans notification ni relance (confirmation). */}
          <BoutonHorsSav
            dossier={editedTicket}
            surDecision={(maj, avis) => {
              const fusionne = { ...editedRef.current, ...maj } as Ticket;
              setEditedTicket(fusionne);
              onUpdate(fusionne);
              setHorsSavAvis(avis);
              loadHistory(true);
            }}
          />

          {/* Archiver = passer en « Fermé » (le dossier rejoint l'onglet Archives) ;
              Désarchiver = le repasser en « Réouvert ». Confirmation dans les deux cas. */}
          {editedTicket.status !== TicketStatus.CLOSED ? (
            <Button variant="secondary" size="sm" onClick={() => setArchivageOuvert(true)} disabled={isSaving}
              title="Passer le dossier en « Fermé » : il rejoint les Archives" className="max-lg:min-h-11">
              <Archive className="w-4 h-4 mr-1" aria-hidden="true" />Archiver
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setDesarchivageOuvert(true)} disabled={isSaving}
              title="Repasser le dossier en « Réouvert » : il revient dans la liste des dossiers" className="max-lg:min-h-11">
              <ArchiveRestore className="w-4 h-4 mr-1" aria-hidden="true" />Désarchiver
            </Button>
          )}

          {isMyTicket && availableAgents.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowTransferModal(true)} className="text-blue-600 hover:bg-blue-50">
              <ArrowUpRight className="w-4 h-4 mr-1" />Transférer
            </Button>
          )}

          <div className="flex-1 max-sm:hidden" />

          {/* Priorité : libellé écrit à côté de sa pastille (Basse gris,
              Moyenne bleu, Haute ambre, Urgente rouge) ; la valeur choisie est pleine. */}
          <div className="flex items-center gap-1 bg-white rounded-lg border border-trait p-1 max-sm:w-full" role="group" aria-label="Priorité">
            <span className="px-1.5 text-[11px] font-semibold text-graphite" aria-hidden="true">Priorité</span>
            {(['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as TicketPriority[]).map(p => {
              const choisie = editedTicket.priority === p;
              return (
                <button
                  key={p}
                  onClick={() => handleUpdate({ priority: p })}
                  aria-label={`Priorité ${PRIORITY_LABELS[p]}`}
                  aria-pressed={choisie}
                  className={`inline-flex items-center justify-center gap-1.5 px-2 py-1 text-xs font-semibold rounded transition-colors max-sm:flex-1 ${CIBLE_TACTILE} ${
                    choisie
                      ? getPriorityColor(p)
                      : 'text-graphite hover:text-encre hover:bg-brume'
                  }`}
                  title={`Passer en priorité ${PRIORITY_LABELS[p].toLowerCase()}`}
                >
                  <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COULEUR_PRIORITE[p], opacity: choisie ? 1 : 0.55 }} />
                  {PRIORITY_LABELS[p]}
                </button>
              );
            })}
          </div>

          {/* Raccourcis clavier hint */}
          <span className="text-[10px] text-graphite/70 hidden lg:block">
            Ctrl+1-5: onglets • R: résoudre • E: escalader
          </span>
        </div>
      </div>

      {transfertAvis && (
        <div role="status" className="mt-3 p-3 rounded-lg border border-sapin-200 bg-sapin-50 text-sm text-sapin-900 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{transfertAvis}</span>
          <button onClick={() => setTransfertAvis(null)} aria-label="Masquer" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Fusion dans un autre dossier */}
      {showFusion && (
        <FusionDossierModal
          ticketId={ticket.id}
          onClose={() => setShowFusion(false)}
          onFusionne={(r) => {
            setShowFusion(false);
            onUpdate({ ...editedRef.current, status: TicketStatus.CLOSED });
            // Le dossier cible s'ouvre sur sa page (qui dit s'il n'est pas accessible).
            ouvrirDossier(r.cibleId);
          }}
        />
      )}

      {spamAvis && (
        <div role="status" className="mt-3 p-3 rounded-lg border border-sapin-200 bg-sapin-50 text-sm text-sapin-900 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{spamAvis}</span>
          <button onClick={() => setSpamAvis(null)} aria-label="Masquer" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {domaineAvis && (
        <div role="status" className="mt-3 p-3 rounded-lg border border-sapin-200 bg-sapin-50 text-sm text-sapin-900 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{domaineAvis}</span>
          <button onClick={() => setDomaineAvis(null)} aria-label="Masquer" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      <FenetreArchivage
        ouvert={archivageOuvert}
        onFermer={() => setArchivageOuvert(false)}
        onConfirmer={async sansPrevenir => {
          await enregistrer(chargeArchivage(sansPrevenir));
          setArchiveAvis(avisArchivage(sansPrevenir));
        }}
      />
      <FenetreDesarchivage
        ouvert={desarchivageOuvert}
        onFermer={() => setDesarchivageOuvert(false)}
        onConfirmer={async () => {
          await enregistrer(CHARGE_DESARCHIVAGE);
          setArchiveAvis(AVIS_DESARCHIVAGE);
        }}
      />

      {archiveAvis && (
        <div role="status" className="mt-3 p-3 rounded-lg border border-sapin-200 bg-sapin-50 text-sm text-sapin-900 flex items-start gap-2">
          <Archive className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span className="flex-1">{archiveAvis}</span>
          <button onClick={() => setArchiveAvis(null)} aria-label="Masquer" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {horsSavAvis && (
        <div role="status" className="mt-3 p-3 rounded-lg border border-sapin-200 bg-sapin-50 text-sm text-sapin-900 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{horsSavAvis}</span>
          <button onClick={() => setHorsSavAvis(null)} aria-label="Masquer" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {rattachementAvis && (
        <div role="status" className="mt-3 p-3 rounded-lg border border-sapin-200 bg-sapin-50 text-sm text-sapin-900 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{rattachementAvis}</span>
          <button onClick={() => setRattachementAvis(null)} aria-label="Masquer" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Rattachement à un compte client (dossier sans compte) */}
      {showRattacher && (
        <RattacherClientModal
          ticket={editedTicket}
          onClose={() => setShowRattacher(false)}
          onRattache={(dossier) => {
            setShowRattacher(false);
            if (dossier) {
              const fusionne = { ...editedRef.current, ...dossier };
              setEditedTicket(fusionne);
              onUpdateRef.current(fusionne);
              setHistory(dossier.history || []);
              const nom = dossier.customer?.displayName || dossier.customer?.email;
              setRattachementAvis(nom ? `Dossier rattaché au compte de ${nom}.` : 'Dossier rattaché au compte client.');
            } else {
              // Rattachement fait, relecture impossible : on retente.
              loadHistory(true);
              setRattachementAvis('Dossier rattaché au compte client.');
            }
          }}
        />
      )}

      {/* Modal de transfert */}
      {showTransferModal && (
        <Fenetre>
        <div className={`fixed inset-0 z-[100] flex items-center justify-center ${FENETRE_FOND_MOBILE}`}>
          <div className="absolute inset-0 bg-encre/50" onClick={() => setShowTransferModal(false)} />
          <div role="dialog" aria-modal="true" aria-label="Transférer le dossier" className={`relative bg-white rounded-lg shadow-flottant w-full max-w-md p-6 max-sm:p-4 animate-in zoom-in-95 ${FENETRE_MOBILE}`}>
            <h3 className="text-lg font-bold text-encre mb-4 flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-blue-600" />
              Transférer le dossier
            </h3>
            <p className="-mt-2 mb-4 text-sm text-graphite">Le dossier est réaffecté tout de suite. La raison, si vous en donnez une, est gardée en note interne.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-encre mb-2">Transférer à</label>
                <select
                  value={selectedAgent}
                  onChange={e => setSelectedAgent(e.target.value)}
                  className="w-full px-3 py-2 border border-trait rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sélectionner un agent...</option>
                  {availableAgents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.displayName} ({ROLE_CONFIG[agent.role]?.label})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-2">Raison (optionnel)</label>
                <textarea
                  value={transferReason}
                  onChange={e => setTransferReason(e.target.value)}
                  placeholder="Pourquoi transférez-vous ce dossier ?"
                  className="w-full px-3 py-2 border border-trait rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-20 resize-none"
                />
              </div>
              {transfertErreur && <p role="alert" className="text-sm text-red-700">Transfert impossible : {transfertErreur}</p>}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="primary" className="flex-1" onClick={handleTransfer} disabled={!selectedAgent || transfertEnCours}>
                {transfertEnCours ? 'Transfert…' : 'Transférer'}
              </Button>
              <Button variant="secondary" onClick={() => { setShowTransferModal(false); setTransfertErreur(null); }} disabled={transfertEnCours}>
                Annuler
              </Button>
            </div>
          </div>
        </div>
        </Fenetre>
      )}

      <div className="mt-3">
      <Tabs
        tabs={[
          { id: 'details', label: 'Détails', icon: <FileText className="w-4 h-4" /> },
          { id: 'messages', label: 'Conversation', icon: <MessageSquare className="w-4 h-4" /> },
          { id: 'ai-assistant', label: 'Assistant IA', icon: <Brain className="w-4 h-4" /> },
          { id: 'documents', label: 'Documents', icon: <FolderOpen className="w-4 h-4" />, count: documentsCount },
          { id: 'history', label: 'Historique', icon: <History className="w-4 h-4" /> },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />
      </div>

      <div className="mt-5">
        {activeTab === 'details' && (
          // Sous 1024 px, la colonne client / conseiller / dates passe sous le contenu.
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-sm:gap-4">
            <div className="lg:col-span-2 space-y-5 min-w-0">
              <Recidive ticketId={ticket.id} />
              {/* La progression du dossier est affichée sous son titre (ParcoursStatut). */}
              {/* Description */}
              <div>
                <label className="text-xs font-semibold text-graphite">Description du problème</label>
                <div className="mt-2 p-4 bg-white rounded-lg border border-trait">
                  <p className="text-encre whitespace-pre-wrap leading-relaxed">{ticket.description || 'Aucune description fournie.'}</p>
                </div>
              </div>

              {/* Équipement : modifiable en ligne, enregistrement explicite */}
              <FicheEquipement ticket={editedTicket} onEnregistrer={enregistrer} />

              {/* Gestion du dossier : trois cartes colorées (statut, priorité,
                  conseiller), chacune avec sa liste pour changer la valeur. */}
              <CartesGestionDossier
                status={editedTicket.status}
                priority={editedTicket.priority}
                assignee={assignee ? {
                  id: assignee.id, displayName: assignee.displayName, email: assignee.email,
                  role: ROLE_CONFIG[assignee.role]?.label,
                } : undefined}
                optionsAffectation={optionsAffectation.map(u => ({
                  value: u.id,
                  label: `${u.displayName} (${ROLE_CONFIG[u.role]?.label || u.role})`
                }))}
                onStatut={changerStatut}
                onPriorite={p => handleUpdate({ priority: p })}
                onAffectation={id => handleUpdate({ assignedToId: id })}
                onPrendreEnCharge={user ? handleTakeOver : undefined}
                onAutoAssigner={autoAssigner}
                autoAssignerPossible={candidatsAuto.length > 0}
              />
            </div>

            <div className="space-y-4">
              {/* Contact client amélioré */}
              <Card className="p-4 bg-amber-50 border-amber-200">
                <h4 className="text-xs font-semibold text-amber-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client
                </h4>
                <div className="space-y-3">
                  {/* Afficher le compte client si disponible */}
                  {editedTicket.customer && (
                    <div className="flex items-center gap-2 p-2 bg-amber-100/50 rounded-lg">
                      <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{getInitials(editedTicket.customer.displayName, editedTicket.customer.email)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-encre">{editedTicket.customer.displayName}</p>
                        <p className="text-[10px] text-amber-600">Compte client</p>
                      </div>
                    </div>
                  )}
                  {editedTicket.companyName && (
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-encre">{editedTicket.companyName}</span>
                    </div>
                  )}
                  {editedTicket.contactName && !editedTicket.customer && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-encre">{editedTicket.contactName}</span>
                    </div>
                  )}
                  {/* Téléphone : priorité au compte client, puis contact manuel */}
                  {(editedTicket.customer?.phone || editedTicket.contactPhone) && (
                    <a href={`tel:${editedTicket.customer?.phone || editedTicket.contactPhone}`} className="flex items-center gap-2 p-2 bg-white rounded-lg hover:bg-amber-100 transition-colors group">
                      <Phone className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-blue-600 group-hover:underline">{editedTicket.customer?.phone || editedTicket.contactPhone}</span>
                      <span className="ml-auto text-xs text-graphite/70">Appeler</span>
                    </a>
                  )}
                  {/* Email : priorité au compte client, puis contact manuel - un email par ligne */}
                  {(editedTicket.customer?.email || editedTicket.contactEmail) && (
                    <div className="space-y-1">
                      {(editedTicket.customer?.email || editedTicket.contactEmail || '').split(/[;,]/).map((email, idx) => {
                        const trimmedEmail = email.trim();
                        if (!trimmedEmail) return null;
                        return (
                          <a key={idx} href={`mailto:${trimmedEmail}`} className="flex items-center gap-2 p-2 bg-white rounded-lg hover:bg-amber-100 transition-colors group">
                            <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <span className="text-sm text-blue-600 group-hover:underline min-w-0 break-all">{trimmedEmail}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {!editedTicket.customer && !editedTicket.companyName && !editedTicket.contactName && <p className="text-sm text-amber-600">Contact non renseigné</p>}
                  {/* Dossier sans compte client : rattachement manuel (le client le verra dans son espace). */}
                  {sansCompteClient(editedTicket) && estEncadrement(user?.role) && (
                    <Button variant="secondary" size="sm" onClick={() => { setRattachementAvis(null); setShowRattacher(true); }} className="w-full">
                      <Link2 className="w-4 h-4 mr-1" />Rattacher à un compte client
                    </Button>
                  )}
                </div>
              </Card>

              {/* Agent assigné */}
              {assignee && (
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Headphones className="w-4 h-4" />
                    Agent assigné
                  </h4>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-blue-600">{getInitials(assignee.displayName, assignee.email)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{assignee.displayName}</p>
                      <Badge className={`text-[10px] ${ROLE_CONFIG[assignee.role]?.color}`}>{ROLE_CONFIG[assignee.role]?.label}</Badge>
                    </div>
                  </div>
                </Card>
              )}

              <Card className="p-4">
                <h4 className="text-xs font-semibold text-graphite mb-3">Dates</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-graphite">Créé</span>
                    <span className="text-encre">{formatDateTime(ticket.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-graphite">Mis à jour</span>
                    <span className="text-encre">{formatDateTime(ticket.updatedAt)}</span>
                  </div>
                  {ticket.slaDeadline && (
                    <div className={`flex justify-between ${ticket.slaBreached ? 'text-red-600' : ''}`}>
                      <span className="text-graphite">SLA</span>
                      <span>{formatDateTime(ticket.slaDeadline)}</span>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'messages' && <ChatPanel ticketId={ticket.id} ticket={editedTicket} versionRelecture={versionMessages} />}

        {activeTab === 'ai-assistant' && (
          <AssistantIAPanel
            ticket={editedTicket}
            conseiller={user}
            surMiseAJour={enregistrer}
            surOuvrirParametres={user?.role === 'ADMIN' ? () => setCurrentView('parametres') : undefined}
            surUtiliserBrouillon={(texte, interne) => {
              // La proposition devient le brouillon du dossier (message au client
              // ou note interne), sans écraser en silence un texte déjà en cours.
              const enCours = lireBrouillon(ticket.id);
              if (enCours?.texte.trim() && enCours.texte !== texte
                && !window.confirm('Un brouillon est déjà en cours pour ce dossier. Le remplacer par la proposition de l\'assistant ?')) {
                setActiveTab('messages');
                return;
              }
              ecrireBrouillon(ticket.id, { texte, interne });
              setActiveTab('messages');
            }}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentsPanel ticketId={ticket.id} attachments={ticket.attachments || []} />
        )}

        {activeTab === 'history' && (
          <div className="max-w-2xl">
            {history === null && !historyError ? (
              <div className="text-center py-12 text-graphite">
                <RefreshCw className="w-6 h-6 mx-auto mb-3 animate-spin" />
                <p className="text-sm">Chargement de l'historique...</p>
              </div>
            ) : historyError && !history ? (
              <div role="alert" className="text-center py-12 text-red-700">
                <p className="text-sm">L'historique n'a pas pu être chargé.</p>
                <Button variant="secondary" size="sm" className="mt-3" onClick={loadHistory}>Réessayer</Button>
              </div>
            ) : history && history.length > 0 ? (
              <div className="relative pl-8 space-y-6">
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-brume" />
                {history.map(h => {
                  const detail = decrireChangement(h, nomUtilisateur);
                  const auteur = h.actor?.displayName || h.actor?.email || 'Automatique';
                  return (
                    <div key={h.id} className="relative">
                      <div className="absolute -left-5 w-4 h-4 bg-white border-2 border-trait rounded-full" />
                      <div className="bg-brume rounded-lg p-4">
                        <p className="text-sm font-medium text-encre">{libelleAction(h.action)}</p>
                        {detail && <p className="text-xs text-graphite mt-1">{detail}</p>}
                        <p className="text-xs text-graphite/70 mt-2">{formatDateTime(h.createdAt)} · {auteur}</p>
                      </div>
                    </div>
                  );
                })}
                {history.length >= 20 && <p className="text-[11px] text-graphite/70">Seuls les 20 derniers événements sont affichés.</p>}
              </div>
            ) : (
              <div className="text-center py-12 text-graphite/70">
                <History className="w-12 h-12 mx-auto mb-3" />
                <p>Aucun historique disponible</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ============================================
// DASHBOARD VIEW
// ============================================

// ============================================
// AI AUTOBOT PANEL
// ============================================

const AIAutoBotPanel: React.FC = () => {
  const { setCurrentView } = useAdmin();
  const [isEnabled, setIsEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Stats réelles depuis l'API
  const [aiStats, setAiStats] = useState({
    ticketsHandled: 0,
    ticketsResolved: 0,
    avgResponseTime: '< 1 min',
    satisfactionRate: 0,
    currentlyActive: 0,
  });

  // Conversations réelles depuis l'API
  const [recentAIConversations, setRecentAIConversations] = useState<Array<{
    id: string;
    ticketId: string;
    ticketNumber: number;
    ticketTitle: string;
    status: 'resolved' | 'escalated' | 'active';
    messages: number;
    resolvedWithoutHuman: boolean;
    lastActivity: string;
  }>>([]);

  // Charger les données au montage
  useEffect(() => {
    const loadAutoBotData = async () => {
      setIsLoading(true);
      try {
        const [statsData, conversationsData] = await Promise.all([
          AdminApi.getAutoBotStats(),
          AdminApi.getAutoBotConversations(10),
        ]);
        setAiStats(statsData);
        setRecentAIConversations(conversationsData);
      } catch (error) {
        console.error('Erreur chargement données AutoBot:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAutoBotData();
  }, []);

  return (
    <Card className="overflow-hidden">
      <div className="p-4 bg-purple-600 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 max-sm:w-10 max-sm:h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Brain className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg max-sm:text-base font-bold flex items-center gap-2">
                Assistant IA AutoBot
                {isEnabled && <span className="w-2 h-2 bg-green-400 rounded-full" />}
              </h2>
              <p className="text-sm text-white/80">Résolution automatique des tickets</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors inline-flex items-center justify-center ${CIBLE_TACTILE}`}
              title="Paramètres"
              aria-label="Comportement de l'assistant"
              aria-expanded={showSettings}
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsEnabled(!isEnabled)}
              role="switch"
              aria-checked={isEnabled}
              aria-label="Assistant automatique"
              className={`relative w-14 h-7 rounded-full transition-colors ${ZONE_TACTILE} ${isEnabled ? 'bg-green-400' : 'bg-white/30'}`}
            >
              <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-7' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* AI Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-brume border-b">
        {isLoading ? (
          <div className="col-span-5 flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            <span className="ml-2 text-sm text-graphite">Chargement des statistiques...</span>
          </div>
        ) : (
          <>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{aiStats.ticketsHandled}</p>
              <p className="text-xs text-graphite">Tickets traités</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{aiStats.ticketsResolved}</p>
              <p className="text-xs text-graphite">Résolus par IA</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{aiStats.avgResponseTime}</p>
              <p className="text-xs text-graphite">Temps réponse</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{aiStats.satisfactionRate}%</p>
              <p className="text-xs text-graphite">Taux résolution</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600">{aiStats.currentlyActive}</p>
              <p className="text-xs text-graphite">Conv. actives</p>
            </div>
          </>
        )}
      </div>

      {/* Règles de parole : la vérité, pas des réglages factices */}
      {showSettings && (
        <div className="p-4 bg-brume border-b border-trait animate-fade-in">
          <h3 className="font-bold text-encre mb-2 flex items-center gap-2">
            <Cog className="w-4 h-4" />
            Comment l'assistant automatique se comporte
          </h3>
          <ul className="text-sm text-encre space-y-1.5 list-disc pl-5">
            <li>Il n'écrit au client que si « Assistant automatique côté client » est activé dans Paramètres, et qu'une clé API est renseignée. Sans clé, il se tait.</li>
            <li>Il se tait pour de bon dès qu'un conseiller a écrit dans le dossier, ou que le client demande une personne.</li>
            <li>Au plus « Réponses automatiques par dossier » messages (Paramètres), jamais deux en moins de deux minutes.</li>
            <li>Il ne clôt jamais un dossier et ne promet jamais de délai.</li>
          </ul>
          <button onClick={() => setCurrentView('parametres')} className="mt-3 text-sm font-semibold text-sapin-700 hover:text-sapin-900">Ouvrir les paramètres</button>
        </div>
      )}

      {/* Recent AI Conversations */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-encre">Conversations IA récentes</h3>
          <span className="text-xs text-graphite">{recentAIConversations.length} ticket{recentAIConversations.length > 1 ? 's' : ''}</span>
        </div>
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
              <span className="ml-2 text-sm text-graphite">Chargement...</span>
            </div>
          ) : recentAIConversations.length === 0 ? (
            <div className="text-center py-8 text-graphite">
              <Brain className="w-10 h-10 mx-auto mb-2 text-trait" />
              <p className="text-sm">Aucune conversation IA pour le moment</p>
              <p className="text-xs text-graphite/70 mt-1">Les conversations apparaîtront ici lorsque l'IA répondra aux tickets</p>
            </div>
          ) : (
            recentAIConversations.map(conv => (
              <div key={conv.id} className="flex items-center gap-4 max-sm:gap-3 p-3 bg-brume rounded-lg hover:bg-brume transition-colors cursor-pointer">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  conv.status === 'resolved' ? 'bg-green-100' :
                  conv.status === 'escalated' ? 'bg-orange-100' : 'bg-blue-100'
                }`}>
                  {conv.status === 'resolved' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                   conv.status === 'escalated' ? <ArrowUpRight className="w-5 h-5 text-orange-600" /> :
                   <MessageSquare className="w-5 h-5 text-blue-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-encre">Ticket #{conv.ticketNumber}</span>
                    {conv.resolvedWithoutHuman && (
                      <Badge className="bg-green-100 text-green-700 text-[10px]">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Résolu sans humain
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-graphite truncate" title={conv.ticketTitle}>
                    {conv.messages} message{conv.messages > 1 ? 's' : ''} IA • {conv.ticketTitle}
                  </p>
                </div>
                <Badge className={
                  conv.status === 'resolved' ? 'bg-green-100 text-green-700' :
                  conv.status === 'escalated' ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-100 text-blue-700'
                }>
                  {conv.status === 'resolved' ? 'Résolu' :
                   conv.status === 'escalated' ? 'Escaladé' : 'En cours'}
                </Badge>
              </div>
            ))
          )}
        </div>
      </div>

      {/* How it works */}
      <div className="p-4 bg-purple-50 border-t">
        <h4 className="text-sm font-semibold text-encre mb-3">Comment ça fonctionne</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-sm:gap-3">
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">1</div>
            <div>
              <p className="text-xs font-medium text-encre">Nouveau ticket</p>
              <p className="text-xs text-graphite">L'IA analyse la demande</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">2</div>
            <div>
              <p className="text-xs font-medium text-encre">Réponse auto</p>
              <p className="text-xs text-graphite">Propose une solution</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">3</div>
            <div>
              <p className="text-xs font-medium text-encre">Suivi client</p>
              <p className="text-xs text-graphite">Continue l'échange</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">4</div>
            <div>
              <p className="text-xs font-medium text-encre">Escalade si besoin</p>
              <p className="text-xs text-graphite">Transfère à un agent</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const DashboardView: React.FC = () => {
  const { tableauDeBord, stats, setShowAIAssistant, setCurrentView, ouvrirDossier, autoAssignTickets, users, ouvrirCreation } = useAdmin();
  const { user } = useAuth();
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);

  // Administrateur ou superviseur : statistiques globales. Un agent n'a que les siennes.
  const isManagerRole = estEncadrement(user?.role);

  // Données propres du tableau de bord : statistiques, non assignés, derniers dossiers.
  const openCount = stats?.byStatus?.[TicketStatus.OPEN] || 0;
  const inProgressCount = stats?.byStatus?.[TicketStatus.IN_PROGRESS] || 0;
  const breachedCount = stats?.slaBreached || 0;
  const unassignedCount = tableauDeBord.nonAssignes.length;
  const unassignedLabel = libelleNombre(unassignedCount, tableauDeBord.nonAssignesTronque);
  const derniers = tableauDeBord.derniers;

  // Tickets actifs = tous sauf RESOLVED et CLOSED (travail restant)
  const activeTicketsCount = (stats?.total || 0)
    - (stats?.byStatus?.[TicketStatus.RESOLVED] || 0)
    - (stats?.byStatus?.[TicketStatus.CLOSED] || 0);

  const handleAutoAssign = async () => {
    setIsAutoAssigning(true);
    await autoAssignTickets();
    setIsAutoAssigning(false);
  };

  return (
    <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-encre">Tableau de bord</h1>
          <p className="text-graphite">{isManagerRole
            ? "Ce qui attend l'équipe SAV aujourd'hui."
            : "Vos indicateurs, et les dossiers qui attendent encore un conseiller."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {unassignedCount > 0 && isManagerRole && (
            <Button variant="primary" size="sm" onClick={handleAutoAssign} disabled={isAutoAssigning} className="bg-[#B26A00] hover:bg-[#8F5500]">
              {isAutoAssigning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Auto-assigner ({unassignedLabel})
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => setShowAIAssistant(true)}>
            <Sparkles className="w-4 h-4 mr-2" />Assistant IA
          </Button>
          <Button variant="primary" size="sm" onClick={ouvrirCreation}>
            <Plus className="w-4 h-4 mr-2" />Nouveau dossier
          </Button>
        </div>
      </div>

      {/* Alerte tickets non assignés */}
      {unassignedCount > 0 && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-orange-800">{unassignedLabel} ticket{unassignedCount > 1 ? 's' : ''} non assigné{unassignedCount > 1 ? 's' : ''}</p>
              <p className="text-sm text-orange-600">Cliquez sur "Auto-assigner" pour les distribuer automatiquement</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleAutoAssign} disabled={isAutoAssigning} className="text-orange-700 hover:bg-orange-100">
            {isAutoAssigning ? 'Assignation...' : 'Assigner maintenant'}
          </Button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-sm:gap-3">
        {/* Statistiques : globales pour l'encadrement, limitées à ses dossiers pour un agent
            (les dossiers sans conseiller sont comptés à part, dans l'alerte ci-dessus). */}
        <KPICard title="Dossiers actifs" value={activeTicketsCount} icon={<TicketIcon />} color="blue" onClick={() => setCurrentView('tickets')} subtitle={isManagerRole ? "Tout ce qui n'est ni résolu ni clos" : 'Vos dossiers ni résolus ni clos'} />
        <KPICard title="Nouveaux" value={openCount} icon={<Clock />} color="blue" subtitle={isManagerRole ? 'Reçus, pas encore pris en charge' : 'Vos dossiers pas encore démarrés'} />
        <KPICard title="En cours" value={inProgressCount} icon={<Activity />} color="green" subtitle={isManagerRole ? 'Pris en charge par un agent' : 'Vos dossiers en cours'} />
        <KPICard title="SLA dépassés" value={breachedCount} icon={<AlertTriangle />} color={breachedCount > 0 ? 'red' : 'blue'} subtitle={breachedCount > 0 ? 'À traiter en premier' : 'Aucun retard'} />
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tickets récents */}
        <Card className="lg:col-span-2">
          <div className="p-4 border-b border-trait flex items-center justify-between">
            <h2 className="font-bold text-encre">Derniers dossiers</h2>
            <Button variant="ghost" size="xs" onClick={() => setCurrentView('tickets')}>Voir tout<ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
          <div className="divide-y divide-trait">
            {derniers.map(t => (
              <div key={t.id} className={`${getStatusRail(t.status)} p-4 hover:bg-brume transition-colors cursor-pointer`} onClick={() => ouvrirDossier(t.id)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="ref text-sm">{t.ticketRef || t.ticketNumber}</span>
                      <Badge className={getPriorityColor(t.priority)}>{PRIORITY_LABELS[t.priority]}</Badge>
                      {!t.assignedToId && <Badge className="bg-orange-100 text-orange-700 text-[10px]">Non assigné</Badge>}
                    </div>
                    <p className="text-sm font-medium text-encre line-clamp-1">{t.title}</p>
                    <p className="text-xs text-graphite mt-1">{t.customer?.displayName || t.companyName || t.contactName || 'Client'} • {formatDateTime(t.createdAt)}</p>
                  </div>
                  <BadgeStatut status={t.status} />
                </div>
              </div>
            ))}
            {tableauDeBord.charge && derniers.length === 0 && (
              <div className="p-12 text-center">
                <TicketIcon className="w-12 h-12 text-trait mx-auto mb-3" />
                <p className="text-graphite">Aucun dossier pour l'instant. Les e-mails reçus sur sav@ et assistance@ apparaîtront ici.</p>
              </div>
            )}
          </div>
        </Card>

        {/* Stats & Team */}
        <div className="space-y-6">
          {/* Par priorité */}
          <Card className="p-4">
            <h3 className="font-bold text-encre mb-4">Par priorité</h3>
            <div className="space-y-3">
              {Object.entries(PRIORITY_LABELS).map(([priority, label]) => {
                const count = stats?.byPriority?.[priority as TicketPriority] || 0;
                const total = stats?.total || 1;
                const percentage = (count / total) * 100;
                return (
                  <div key={priority}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-graphite">{label}</span>
                      <span className="font-semibold text-encre">{count}</span>
                    </div>
                    <div className="w-full h-2 bg-brume rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${
                        priority === 'URGENT' ? 'bg-statut-urgent' :
                        priority === 'HIGH' ? 'bg-statut-attente' :
                        priority === 'MEDIUM' ? 'bg-statut-nouveau' : 'bg-graphite/40'
                      }`} style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Équipe - Visible uniquement pour ADMIN et SUPERVISOR */}
          {isManagerRole && (
            <Card className="p-4">
              <h3 className="font-bold text-encre mb-4">Équipe</h3>
              <div className="space-y-3">
                {users.filter(u => u.role !== UserRole.CUSTOMER).slice(0, 4).map(u => (
                  <div key={u.id} className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-blue-600">{getInitials(u.displayName, u.email)}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-encre">{u.displayName}</p>
                      <p className="text-xs text-graphite">{u._count?.assignedTickets || 0} tickets</p>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${u.isActive !== false ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* AI AutoBot Panel - Visible uniquement pour ADMIN et SUPERVISOR */}
      {isManagerRole && <AIAutoBotPanel />}
    </div>
  );
};

// ============================================
// PAGINATION COMPONENT
// ============================================

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
}> = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, onItemsPerPageChange }) => {
  const pages = useMemo(() => {
    const result: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) result.push(i);
    } else {
      result.push(1);
      if (currentPage > 3) result.push('ellipsis');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        result.push(i);
      }
      if (currentPage < totalPages - 2) result.push('ellipsis');
      result.push(totalPages);
    }
    return result;
  }, [currentPage, totalPages]);

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  // Téléphone et tablette : précédent / « Page n sur N » / suivant, en cibles de 40 px.
  const etroit = useEcranEtroit();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white border-t border-trait">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-sm text-encre">
          Affichage <span className="font-medium">{startItem}</span> - <span className="font-medium">{endItem}</span> sur <span className="font-medium">{totalItems}</span>
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          aria-label="Dossiers par page"
          className="text-sm border border-trait rounded-lg px-2 py-1 max-lg:min-h-10 max-sm:text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value={10}>10 / page</option>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
      </div>
      {etroit ? (
        <nav className="flex items-center gap-2 max-sm:w-full max-sm:justify-between" aria-label="Pagination">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Page précédente"
            className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-trait text-graphite hover:bg-brume disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-encre tnum" aria-current="page">Page <span className="font-semibold">{currentPage}</span> sur {totalPages}</span>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Page suivante"
            className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-trait text-graphite hover:bg-brume disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </nav>
      ) : (
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Page précédente"
          className="p-2 rounded-lg text-graphite hover:bg-brume disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
        {pages.map((page, i) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${i}`} className="px-2 text-graphite/70">...</span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                currentPage === page
                  ? 'bg-blue-600 text-white'
                  : 'text-encre hover:bg-brume'
              }`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Page suivante"
          className="p-2 rounded-lg text-graphite hover:bg-brume disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      )}
    </div>
  );
};

// ============================================
// DATE RANGE FILTER COMPONENT
// ============================================

const DateRangeFilter: React.FC<{
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
}> = ({ dateFrom, dateTo, onChange }) => {
  // Jours du calendrier de Paris (shared/dates.ts) : l'ancien calcul en UTC
  // donnait la veille entre minuit et 2 h et le dernier jour du mois précédent
  // pour « Ce mois ». Le serveur lit ces jours à minuit, heure de Paris.
  const presets = PERIODES_PREDEFINIES;

  const [showPresets, setShowPresets] = useState(false);

  return (
    // Sur téléphone, le cadre des deux dates prend la largeur de la ligne et les
    // champs se partagent l'espace ; les boutons passent dessous si besoin.
    <div className="relative flex items-center gap-2 max-sm:flex-wrap">
      <div className="flex items-center gap-2 bg-white border border-trait rounded-lg px-3 py-1.5 max-sm:w-full max-lg:min-h-10">
        <Calendar className="w-4 h-4 text-graphite/70 flex-shrink-0" />
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => onChange(e.target.value, dateTo)}
          className="text-sm border-none focus:ring-0 p-0 w-32 max-sm:w-auto max-sm:flex-1 max-sm:min-w-0 max-sm:text-base"
          placeholder="Du"
          aria-label="Du"
        />
        <span className="text-graphite/70">→</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => onChange(dateFrom, e.target.value)}
          className="text-sm border-none focus:ring-0 p-0 w-32 max-sm:w-auto max-sm:flex-1 max-sm:min-w-0 max-sm:text-base"
          placeholder="Au"
          aria-label="Au"
        />
      </div>
      <button
        onClick={() => setShowPresets(!showPresets)}
        className={`p-2 text-graphite hover:bg-brume rounded-lg inline-flex items-center justify-center ${CIBLE_TACTILE}`}
        title="Périodes prédéfinies"
        aria-label="Périodes prédéfinies"
      >
        <Clock className="w-4 h-4" />
      </button>
      {(dateFrom || dateTo) && (
        <button
          onClick={() => onChange('', '')}
          className={`p-2 text-graphite/70 hover:text-red-500 hover:bg-red-50 rounded-lg inline-flex items-center justify-center ${CIBLE_TACTILE}`}
          title="Effacer les dates"
          aria-label="Effacer les dates"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      {showPresets && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-flottant border border-trait py-1 z-50 min-w-[180px]">
          {presets.map((p, i) => (
            <button
              key={i}
              onClick={() => { const [from, to] = p.calculer(); onChange(from, to); setShowPresets(false); }}
              className="w-full px-4 py-2 max-lg:py-3 text-left text-sm hover:bg-brume text-encre"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// TICKETS VIEW
// ============================================

const TicketsView: React.FC = () => {
  const { ouvrirDossier, memoireListe, users, agents, refreshData, autoAssignTickets, unreadByTicket, pendingTicketFilter, setPendingTicketFilter, tableauDeBord, versionDossiers, ouvrirCreation, stats, setCurrentView } = useAdmin();
  const { user: moi } = useAuth();
  // "A_TRAITER" = filtre par défaut qui exclut RESOLVED et CLOSED.
  // Un filtre demandé par la barre latérale avant le montage est repris ici ;
  // sinon, au retour d'une fiche, la liste reprend l'état qu'elle avait.
  const [depart] = useState(() => etatInitialListe(memoireListe.current, pendingTicketFilter));
  const [filters, setFiltersBrut] = useState<FiltresListe>(depart.filters);
  // Saisie de la recherche : envoyée au serveur 300 ms après la dernière frappe.
  const [rechercheSaisie, setRechercheSaisie] = useState(depart.rechercheSaisie);
  const [tri, setTri] = useState<TriDossiers>(depart.tri);
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [currentPage, setCurrentPage] = useState(depart.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(depart.itemsPerPage);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(depart.showAdvancedFilters);

  // L'état courant est gardé en mémoire : le retour depuis une fiche le retrouve.
  const memoriserListe = useCallback(() => {
    memoireListe.current = { filters, rechercheSaisie, tri, currentPage, itemsPerPage, showAdvancedFilters, defilement: memoireListe.current?.defilement };
  }, [memoireListe, filters, rechercheSaisie, tri, currentPage, itemsPerPage, showAdvancedFilters]);
  useEffect(() => { memoriserListe(); }, [memoriserListe]);
  // Position de défilement : notée au fil du défilement, rétablie au retour
  // d'une fiche une fois la page de dossiers affichée.
  const zoneDefilement = useRef<HTMLDivElement>(null);
  const defilementEnAttente = useRef(defilementARetablir(depart));
  const noterDefilement = (e: React.UIEvent<HTMLDivElement>) => {
    if (memoireListe.current) memoireListe.current.defilement = e.currentTarget.scrollTop;
  };
  const [isLoadingFiltered, setIsLoadingFiltered] = useState(true);
  const [erreurListe, setErreurListe] = useState<string | null>(null);
  // Page affichée, telle que rendue par le serveur.
  const [pageDossiers, setPageDossiers] = useState<{ data: Ticket[]; total: number; totalPages: number }>({ data: [], total: 0, totalPages: 0 });
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set());

  // Tout changement de filtre revient à la première page.
  const setFilters = useCallback((maj: React.SetStateAction<FiltresListe>) => {
    setFiltersBrut(maj);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (filters.search === rechercheSaisie) return;
    const minuteur = setTimeout(() => {
      setFiltersBrut(f => ({ ...f, search: rechercheSaisie }));
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(minuteur);
  }, [rechercheSaisie, filters.search]);

  // Filtre demandé par la barre latérale (au montage ou vue déjà ouverte) :
  // appliqué puis consommé.
  useEffect(() => {
    if (pendingTicketFilter === null) return;
    const status = pendingTicketFilter;
    setFilters(f => (f.status === status ? f : { ...f, status }));
    setPendingTicketFilter(null);
  }, [pendingTicketFilter, setPendingTicketFilter, setFilters]);

  // Chargement de la page : filtres, tri et pagination sont appliqués par le
  // serveur. Seule la dernière requête partie est prise en compte.
  const numeroRequete = useRef(0);
  const chargerPage = useCallback(async (silencieux: boolean) => {
    const numero = ++numeroRequete.current;
    if (!silencieux) setIsLoadingFiltered(true);
    try {
      const r = await AdminApi.getTickets(construireFiltresListe(filters, { page: currentPage, limit: itemsPerPage, tri }));
      if (numero !== numeroRequete.current) return;
      setPageDossiers({ data: r.data, total: r.total, totalPages: r.totalPages });
      setErreurListe(null);
      // Page devenue vide (dossiers fermés entre-temps) : on recule.
      if (r.data.length === 0 && currentPage > 1 && r.totalPages > 0 && currentPage > r.totalPages) setCurrentPage(r.totalPages);
    } catch (error) {
      if (numero !== numeroRequete.current) return;
      console.error('Erreur chargement dossiers:', error);
      setErreurListe(error instanceof Error ? error.message : "La liste n'a pas pu être chargée");
    } finally {
      if (numero === numeroRequete.current) setIsLoadingFiltered(false);
    }
  }, [filters, currentPage, itemsPerPage, tri]);

  useEffect(() => { chargerPage(false); }, [chargerPage]);

  useEffect(() => {
    if (isLoadingFiltered || defilementEnAttente.current === 0) return;
    if (zoneDefilement.current) zoneDefilement.current.scrollTop = defilementEnAttente.current;
    defilementEnAttente.current = 0;
  }, [isLoadingFiltered]);

  // Changement signalé par le serveur (dossier:modifie, regroupé) : la page se recharge sans clignoter.
  const premiereVersion = useRef(true);
  useEffect(() => {
    if (premiereVersion.current) { premiereVersion.current = false; return; }
    chargerPage(true);
  }, [versionDossiers]);

  const paginatedTickets = pageDossiers.data;
  const totalPages = pageDossiers.totalPages;

  // Gestion de la sélection
  const toggleTicketSelection = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTicketIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTicketIds.size === paginatedTickets.length) {
      setSelectedTicketIds(new Set());
    } else {
      setSelectedTicketIds(new Set(paginatedTickets.map(t => t.id)));
    }
  };

  const clearSelection = () => setSelectedTicketIds(new Set());

  // Case d'une carte (écran étroit) : bascule la sélection d'un dossier.
  const basculerSelection = (ticketId: string) => {
    setSelectedTicketIds(prev => {
      const suivant = new Set(prev);
      if (suivant.has(ticketId)) suivant.delete(ticketId);
      else suivant.add(ticketId);
      return suivant;
    });
  };

  // États pour les dropdowns d'actions groupées
  const [showBulkAssignDropdown, setShowBulkAssignDropdown] = useState(false);
  const [showBulkStatusDropdown, setShowBulkStatusDropdown] = useState(false);
  const [showBulkPriorityDropdown, setShowBulkPriorityDropdown] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  // Archivage groupé : fenêtre de confirmation (case « Sans prévenir les clients »).
  const [archivageGroupeOuvert, setArchivageGroupeOuvert] = useState(false);

  // Résumé de la dernière action groupée (réussites et échecs).
  const [bulkResult, setBulkResult] = useState<{ libelle: string; ok: number; ko: number; erreur?: string } | null>(null);

  // Actions groupées : chaque dossier est traité séparément ; les échecs sont
  // comptés et restent sélectionnés pour pouvoir réessayer.
  const executerEnMasse = async (libelle: string, payload: UpdateTicketPayload) => {
    const ids = Array.from(selectedTicketIds);
    if (ids.length === 0) return;
    setIsBulkUpdating(true);
    setBulkResult(null);
    try {
      const resultats = await Promise.allSettled(ids.map(id => AdminApi.updateTicket(id, payload)));
      const echecs = ids.filter((_, i) => resultats[i].status === 'rejected');
      const premiereErreur = resultats.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (premiereErreur) console.error(`Erreur action groupée (${libelle}):`, premiereErreur.reason);
      setBulkResult({
        libelle,
        ok: ids.length - echecs.length,
        ko: echecs.length,
        erreur: premiereErreur?.reason instanceof Error ? premiereErreur.reason.message : undefined,
      });
      await refreshData();
      setSelectedTicketIds(new Set(echecs));
    } finally {
      setIsBulkUpdating(false);
      setShowBulkAssignDropdown(false);
      setShowBulkStatusDropdown(false);
      setShowBulkPriorityDropdown(false);
    }
  };

  const handleBulkAssign = async (agentId: string | null) => {
    const agent = agentId ? agents.find(a => a.id === agentId) : null;
    await executerEnMasse(agent ? `Affectation à ${agent.displayName}` : 'Désaffectation', { assignedToId: agentId });
  };

  const handleBulkStatus = async (status: TicketStatus) => {
    const n = selectedTicketIds.size;
    // Résoudre en masse prévient les clients : on demande confirmation. Clore
    // (archiver) passe par sa propre fenêtre (handleBulkArchive).
    if (status === TicketStatus.CLOSED) { setShowBulkStatusDropdown(false); setArchivageGroupeOuvert(true); return; }
    if (status === TicketStatus.RESOLVED
      && !window.confirm(`Passer ${n} dossier${n > 1 ? 's' : ''} au statut « ${STATUS_LABELS[status]} » ?`)) {
      setShowBulkStatusDropdown(false);
      return;
    }
    await executerEnMasse(`Statut « ${STATUS_LABELS[status]} »`, { status });
  };

  // Archiver la sélection : « Fermé », avec ou sans avis aux clients.
  const handleBulkArchive = async (sansPrevenir: boolean) => {
    await executerEnMasse(sansPrevenir ? 'Archivage sans prévenir les clients' : 'Archivage', chargeArchivage(sansPrevenir));
  };

  const handleBulkPriority = async (priority: TicketPriority) => {
    await executerEnMasse(`Priorité « ${PRIORITY_LABELS[priority]} »`, { priority });
  };

  // Ouvrir un dossier : sa fiche s'affiche sur sa page, la liste est mémorisée.
  const openTicket = (ticket: Ticket) => {
    memoriserListe();
    ouvrirDossier(ticket.id);
  };

  const unassignedCount = tableauDeBord.nonAssignes.length;
  const unassignedLabel = libelleNombre(unassignedCount, tableauDeBord.nonAssignesTronque);
  // Ne pas compter 'A_TRAITER' comme filtre actif car c'est le défaut
  const activeFiltersCount = compterFiltresActifs(filters);

  const handleAutoAssign = async () => {
    setIsAutoAssigning(true);
    await autoAssignTickets();
    setIsAutoAssigning(false);
  };

  const resetFilters = () => {
    setRechercheSaisie('');
    setFilters(FILTRES_PAR_DEFAUT);
  };

  // Sous 1024 px : cartes au lieu du tableau, filtres repliés derrière un bouton.
  const etroit = useEcranEtroit();
  const [filtresOuverts, setFiltresOuverts] = useState(false);

  const champRecherche = (
    <Input placeholder="Référence, titre, n° de série, e-mail, société…" value={rechercheSaisie} onChange={e => setRechercheSaisie(e.target.value)} icon={<Search className="w-4 h-4" />} className={etroit ? 'w-full' : 'w-full sm:w-72'} />
  );
  // « Fermé » n'est plus proposé : les dossiers fermés sont dans l'onglet Archives.
  const filtreStatut = (
    <Select value={filters.status} aria-label="Statut" onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} options={[
        { value: 'A_TRAITER', label: 'À Traiter' },
        { value: '', label: 'Tous (hors archives)' },
        ...Object.entries(STATUS_LABELS).filter(([value]) => value !== TicketStatus.CLOSED).map(([value, label]) => ({ value, label }))
      ]} />
  );
  const filtrePriorite = (
    <Select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} options={Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label }))} placeholder="Toutes priorités" />
  );
  // Filtre « Spam possible » : les dossiers nés d'un e-mail jugé indésirable
  // par Microsoft, en attente de décision.
  const filtreSpam = (
    <button
      type="button"
      onClick={() => setFilters(f => ({ ...f, spamPossible: !f.spamPossible }))}
      aria-pressed={!!filters.spamPossible}
      className={`flex items-center gap-2 px-3 py-2 max-lg:min-h-10 rounded-lg border text-sm transition-colors ${
        filters.spamPossible
          ? 'bg-[#FBE9E7] border-[#F2C4BF] text-[#8A1C16]'
          : 'bg-white border-trait text-encre hover:bg-brume'
      }`}
    >
      <AlertTriangle className="w-4 h-4" />
      Spam possible
    </button>
  );
  const choixTri = (
    <label className="flex items-center gap-2 text-sm text-graphite">
      <span className="whitespace-nowrap">Trier par</span>
      <Select value={tri} onChange={e => { setTri(e.target.value as TriDossiers); setCurrentPage(1); }} options={TRIS_DOSSIERS.map(t => ({ value: t.value, label: t.label }))} />
    </label>
  );
  const boutonPlusDeFiltres = (
    <button
      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
      aria-expanded={showAdvancedFilters}
      className={`flex items-center gap-2 px-3 py-2 max-lg:min-h-10 rounded-lg border text-sm transition-colors ${
        showAdvancedFilters || activeFiltersCount > 0
          ? 'bg-blue-50 border-blue-200 text-blue-700'
          : 'bg-white border-trait text-encre hover:bg-brume'
      }`}
    >
      <Filter className="w-4 h-4" />
      Plus de filtres
      {activeFiltersCount > 0 && (
        <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFiltersCount}</span>
      )}
    </button>
  );
  const boutonReinitialiser = activeFiltersCount > 0 && (
    <Button variant="ghost" size="sm" onClick={resetFilters}>
      <X className="w-4 h-4 mr-1" />Réinitialiser
    </Button>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 max-sm:p-3 border-b border-trait bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 max-sm:gap-3">
          <div>
            <h1 className="text-xl font-bold text-encre">Dossiers SAV</h1>
            <p className="text-sm text-graphite"><span className="tnum">{pageDossiers.total}</span> dossier{pageDossiers.total > 1 ? 's' : ''} {activeFiltersCount > 0 && <span className="text-blue-600">({activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''} actif{activeFiltersCount > 1 ? 's' : ''})</span>}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {unassignedCount > 0 && (
              <Button variant="secondary" size="sm" onClick={handleAutoAssign} disabled={isAutoAssigning} className="bg-yellow-100 text-orange-700 border-orange-200">
                {isAutoAssigning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Auto-assigner ({unassignedLabel})
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={ouvrirCreation}>
              <Plus className="w-4 h-4 mr-2" />Nouveau
            </Button>
          </div>
        </div>

        {/* Puces de statut : le même filtre que la liste « Statut », en couleur,
            avec le nombre de dossiers. Les nombres viennent des statistiques :
            globales pour l'encadrement ; un agent ne voit que les siens dans
            les statistiques, alors que la liste lui montre aussi les dossiers
            sans conseiller : pas de nombre pour lui. */}
        <div className="mt-3 space-y-1.5">
          <PucesStatut
            valeur={filters.status}
            onChange={v => setFilters(f => ({ ...f, status: v }))}
            compteurs={estEncadrement(moi?.role) ? stats?.byStatus : null}
            noteCompteurs="Nombre total de dossiers dans ce statut, sans les autres filtres"
            onVoirArchives={() => setCurrentView(VUE_ARCHIVES)}
          />
          <LegendeStatuts />
        </div>

        {/* Filtres principaux. Sous 1024 px, la recherche reste visible et le
            reste se replie derrière le bouton « Filtres ». */}
        {etroit ? (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">{champRecherche}</div>
              <button
                onClick={() => setFiltresOuverts(o => !o)}
                aria-expanded={filtresOuverts}
                aria-controls="filtres-liste-dossiers"
                className={`flex-shrink-0 flex items-center gap-2 px-3 min-h-11 rounded-lg border text-sm font-medium transition-colors ${
                  filtresOuverts || activeFiltersCount > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-trait text-encre hover:bg-brume'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filtres
                {activeFiltersCount > 0 && (
                  <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFiltersCount}</span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${filtresOuverts ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {filtresOuverts && (
              <div id="filtres-liste-dossiers" className="grid grid-cols-1 sm:grid-cols-2 gap-2 animate-fade-in">
                {filtreStatut}
                {filtrePriorite}
                <div className="sm:col-span-2">{filtreSpam}</div>
                <div className="sm:col-span-2">{choixTri}</div>
                <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                  {boutonPlusDeFiltres}
                  {boutonReinitialiser}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3 mt-4">
            {champRecherche}
            {filtreStatut}
            {filtrePriorite}
            {filtreSpam}
            {choixTri}
            {boutonPlusDeFiltres}
            {boutonReinitialiser}
          </div>
        )}

        {/* Filtres avancés */}
        {showAdvancedFilters && (
          <div className="mt-4 p-4 max-sm:mt-3 max-sm:p-3 bg-brume rounded-lg border border-trait animate-fade-in">
            <div className="flex flex-wrap items-center gap-4 max-sm:gap-3">
              <div className="max-sm:w-full">
                <label className="block text-xs font-medium text-graphite mb-1">Agent assigné</label>
                {/* Sans choix, tout le personnel (agent compris) reçoit tous les dossiers, sans conseiller compris. */}
                <Select value={filters.assignedToId} onChange={e => setFilters(f => ({ ...f, assignedToId: e.target.value }))} options={users.filter(u => u.role !== UserRole.CUSTOMER).map(u => ({ value: u.id, label: u.displayName }))} placeholder="Tous agents" />
              </div>
              <div className="max-sm:w-full">
                <label className="block text-xs font-medium text-graphite mb-1">Type de problème</label>
                <Select value={filters.issueType} onChange={e => setFilters(f => ({ ...f, issueType: e.target.value }))} options={Object.entries(ISSUE_TYPE_LABELS).map(([value, label]) => ({ value, label }))} placeholder="Tous types" />
              </div>
              <div className="max-sm:w-full">
                <label className="block text-xs font-medium text-graphite mb-1">Période de création</label>
                <DateRangeFilter
                  dateFrom={filters.dateFrom}
                  dateTo={filters.dateTo}
                  onChange={(from, to) => setFilters(f => ({ ...f, dateFrom: from, dateTo: to }))}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Résumé de la dernière action groupée */}
      {bulkResult && (
        <div role="status" className={`mx-4 mt-2 mb-2 p-3 rounded-lg border flex items-start gap-2 text-sm ${bulkResult.ko > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
          {bulkResult.ko > 0 ? <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span className="flex-1">
            {bulkResult.libelle} : {bulkResult.ok} réussi{bulkResult.ok > 1 ? 's' : ''}, {bulkResult.ko} échec{bulkResult.ko > 1 ? 's' : ''}.
            {bulkResult.ko > 0 && ` Les dossiers en échec restent sélectionnés.${bulkResult.erreur ? ` Première erreur : ${bulkResult.erreur}` : ''}`}
          </span>
          <button onClick={() => setBulkResult(null)} aria-label="Masquer le résumé" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Barre d'actions groupées */}
      {selectedTicketIds.size > 0 && (
        <div className="relative mx-4 mb-2 max-sm:mx-3 p-3 bg-blue-600 rounded-lg shadow-flottant flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-white">
            <span className="font-medium">{selectedTicketIds.size} ticket{selectedTicketIds.size > 1 ? 's' : ''} sélectionné{selectedTicketIds.size > 1 ? 's' : ''}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Dropdown Assigner */}
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                onClick={() => { setShowBulkAssignDropdown(!showBulkAssignDropdown); setShowBulkStatusDropdown(false); setShowBulkPriorityDropdown(false); }}
                disabled={isBulkUpdating}
              >
                <UserPlus className="w-4 h-4 mr-1" />Assigner
              </Button>
              {showBulkAssignDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-flottant border z-50 py-1 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => handleBulkAssign(null)}
                    className="w-full px-3 py-2 max-lg:py-3 text-left text-sm hover:bg-brume text-graphite"
                  >
                    Non assigné
                  </button>
                  {agents.filter(estAssignable).map(agent => (
                    <button
                      key={agent.id}
                      onClick={() => handleBulkAssign(agent.id)}
                      className="w-full px-3 py-2 max-lg:py-3 text-left text-sm hover:bg-brume flex items-center gap-2"
                    >
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-600">
                        {agent.displayName.charAt(0)}
                      </div>
                      <span className="text-encre">{agent.displayName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dropdown Statut */}
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                onClick={() => { setShowBulkStatusDropdown(!showBulkStatusDropdown); setShowBulkAssignDropdown(false); setShowBulkPriorityDropdown(false); }}
                disabled={isBulkUpdating}
              >
                <CheckCircle className="w-4 h-4 mr-1" />Statut
              </Button>
              {showBulkStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-white rounded-lg shadow-flottant border z-50 py-1">
                  {(Object.keys(STATUS_LABELS) as TicketStatus[]).filter(status => status !== TicketStatus.CLOSED).map(status => (
                    <button
                      key={status}
                      onClick={() => handleBulkStatus(status)}
                      className="w-full px-3 py-2 max-lg:py-3 text-left text-sm hover:bg-brume flex items-center gap-2"
                    >
                      <BadgeStatut status={status} />
                    </button>
                  ))}
                  {/* « Fermé » devient « Archiver » : confirmation avec « Sans prévenir les clients ». */}
                  <button
                    onClick={() => handleBulkStatus(TicketStatus.CLOSED)}
                    className="w-full px-3 py-2 max-lg:py-3 text-left text-sm font-semibold text-encre hover:bg-brume flex items-center gap-2 border-t border-trait"
                  >
                    <Archive className="w-4 h-4 text-graphite" aria-hidden="true" />Archiver
                  </button>
                </div>
              )}
            </div>

            {/* Dropdown Priorité */}
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                onClick={() => { setShowBulkPriorityDropdown(!showBulkPriorityDropdown); setShowBulkAssignDropdown(false); setShowBulkStatusDropdown(false); }}
                disabled={isBulkUpdating}
              >
                <AlertTriangle className="w-4 h-4 mr-1" />Priorité
              </Button>
              {showBulkPriorityDropdown && (
                <div className="absolute top-full left-0 mt-1 w-36 bg-white rounded-lg shadow-flottant border z-50 py-1">
                  {Object.entries(PRIORITY_LABELS).map(([priority, label]) => (
                    <button
                      key={priority}
                      onClick={() => handleBulkPriority(priority as TicketPriority)}
                      className="w-full px-3 py-2 max-lg:py-3 text-left text-sm hover:bg-brume flex items-center gap-2"
                    >
                      <Badge className={getPriorityColor(priority as TicketPriority)}>{label}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Bouton fermer */}
            <button
              onClick={clearSelection}
              aria-label="Annuler la sélection"
              className={`ml-2 p-1.5 hover:bg-white/20 rounded-full transition-colors inline-flex items-center justify-center ${CIBLE_TACTILE}`}
              disabled={isBulkUpdating}
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          {isBulkUpdating && (
            <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>
      )}

      <FenetreArchivage
        ouvert={archivageGroupeOuvert}
        nombre={selectedTicketIds.size}
        onFermer={() => setArchivageGroupeOuvert(false)}
        onConfirmer={handleBulkArchive}
      />

      <div ref={zoneDefilement} onScroll={noterDefilement} className="flex-1 overflow-auto p-4 max-sm:p-3">
        <Card>
          {etroit ? (
            // Téléphone et tablette : une carte par dossier, avec l'essentiel
            // (référence, titre, statut, priorité, client, conseiller,
            // échéance) ; toute la carte ouvre le dossier.
            <div data-testid="cartes-dossiers">
              {paginatedTickets.length > 0 && (
                <label className="flex items-center gap-3 px-3 min-h-11 border-b border-trait text-sm text-graphite cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={selectedTicketIds.size === paginatedTickets.length}
                    onChange={toggleSelectAll}
                    className="w-5 h-5 text-blue-600 border-trait rounded focus:ring-blue-500 cursor-pointer"
                  />
                  Tout sélectionner sur cette page
                </label>
              )}
              <ul className="divide-y divide-trait" aria-label="Dossiers">
                {paginatedTickets.map(t => {
                  const assignee = t.assignedToId
                    ? (users.find(u => u.id === t.assignedToId) || (t.assignedTo?.id === t.assignedToId ? t.assignedTo : undefined))
                    : undefined;
                  const unreadCount = unreadByTicket.get(t.id) || 0;
                  const isSelected = selectedTicketIds.has(t.id);
                  const clientName = t.customer?.displayName || t.companyName || t.contactName;
                  const reference = t.ticketRef || t.ticketNumber;
                  return (
                    <li key={t.id} className={`${getStatusRail(t.status)} flex items-stretch ${isSelected ? 'bg-sapin-50' : ''} ${unreadCount > 0 && !isSelected ? 'bg-sapin-50/40' : ''}`}>
                      <label className="flex items-start justify-center w-11 pt-3.5 flex-shrink-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => basculerSelection(t.id)}
                          aria-label={`Sélectionner le dossier ${reference}`}
                          className="w-5 h-5 text-blue-600 border-trait rounded focus:ring-blue-500 cursor-pointer"
                        />
                      </label>
                      <button type="button" onClick={() => openTicket(t)} className="flex-1 min-w-0 flex items-center gap-2 py-3 pr-3 text-left hover:bg-brume active:bg-brume transition-colors">
                        <span className="flex-1 min-w-0 block">
                          <span className="flex items-center gap-2">
                            <span className="ref text-sm">{reference}</span>
                            {unreadCount > 0 && (
                              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] font-semibold rounded-full" aria-label={`${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`}>
                                <Bell className="w-3 h-3" />
                                {unreadCount}
                              </span>
                            )}
                            <span className="ml-auto flex-shrink-0"><BadgeStatut status={t.status} /></span>
                          </span>
                          <span className="block text-sm font-medium text-encre line-clamp-2 mt-1 break-words">{t.title}</span>
                          <span className="block text-xs text-graphite mt-0.5 truncate">{clientName || 'Client non renseigné'} · {ISSUE_TYPE_LABELS[t.issueType]}</span>
                          <MentionDomaineConfirme dossier={t} className="mt-0.5 max-w-full" />
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-2">
                            <BadgeSpamPossible dossier={t} />
                            <BadgeDomaineAConfirmer dossier={t} />
                            <Badge className={getPriorityColor(t.priority)}>{PRIORITY_LABELS[t.priority]}</Badge>
                            {t.slaDeadline
                              ? <SLATimer deadline={t.slaDeadline} breached={t.slaBreached || false} compact />
                              : <span className="text-xs text-graphite/70">Pas de SLA</span>}
                            {assignee ? (
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-[9px] font-bold text-white">{getInitials(assignee.displayName, assignee.email)}</span>
                                </span>
                                <span className="text-xs text-encre truncate">{assignee.displayName}</span>
                              </span>
                            ) : (
                              <Badge className="bg-[#FBF1E1] text-[#7A4A00]">Non assigné</Badge>
                            )}
                          </span>
                        </span>
                        <ChevronRight className="w-5 h-5 text-graphite/50 flex-shrink-0" aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white border-b border-trait">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={paginatedTickets.length > 0 && selectedTicketIds.size === paginatedTickets.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-blue-600 border-trait rounded focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Ticket</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Priorité</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">SLA</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Assigné à</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-graphite">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-trait">
                {paginatedTickets.map(t => {
                  const assignee = t.assignedToId
                    ? (users.find(u => u.id === t.assignedToId) || (t.assignedTo?.id === t.assignedToId ? t.assignedTo : undefined))
                    : undefined;
                  const unreadCount = unreadByTicket.get(t.id) || 0;
                  const isSelected = selectedTicketIds.has(t.id);
                  const clientName = t.customer?.displayName || t.companyName || t.contactName;
                  const clientEmail = t.customer?.email || t.contactEmail;

                  return (
                    <tr
                      key={t.id}
                      className={`${getStatusRail(t.status)} hover:bg-brume transition-colors cursor-pointer ${isSelected ? 'bg-sapin-50' : ''} ${unreadCount > 0 && !isSelected ? 'bg-sapin-50/40' : ''}`}
                      onClick={() => openTicket(t)}
                    >
                      <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          onClick={(e) => toggleTicketSelection(t.id, e)}
                          className="w-4 h-4 text-blue-600 border-trait rounded focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="ref text-sm">{t.ticketRef || t.ticketNumber}</span>
                          <BadgeSpamPossible dossier={t} />
                          <BadgeDomaineAConfirmer dossier={t} />
                          {unreadCount > 0 && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] font-semibold rounded-full">
                              <Bell className="w-3 h-3" />
                              {unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-encre line-clamp-1 max-w-xs mt-1">{t.title}</p>
                        <p className="text-xs text-graphite/70">{ISSUE_TYPE_LABELS[t.issueType]} • {formatDateTime(t.createdAt)}</p>
                      </td>
                      <td className="px-4 py-3">
                        {clientName ? (
                          <div>
                            <p className="text-sm font-medium text-encre">{clientName}</p>
                            {clientEmail && <p className="text-xs text-graphite truncate max-w-[200px]">{clientEmail}</p>}
                            <MentionDomaineConfirme dossier={t} className="mt-0.5" />
                          </div>
                        ) : (
                          <div>
                            <span className="block text-sm text-graphite/70">-</span>
                            <MentionDomaineConfirme dossier={t} className="mt-0.5" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3"><BadgeStatut status={t.status} /></td>
                      <td className="px-4 py-3"><Badge className={getPriorityColor(t.priority)}>{PRIORITY_LABELS[t.priority]}</Badge></td>
                      <td className="px-4 py-3">
                        {t.slaDeadline ? (
                          <SLATimer deadline={t.slaDeadline} breached={t.slaBreached || false} compact />
                        ) : (
                          <span className="text-xs text-graphite/70">Pas de SLA</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {assignee ? (
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-blue-400 rounded-full flex items-center justify-center">
                              <span className="text-[10px] font-bold text-white">{getInitials(assignee.displayName, assignee.email)}</span>
                            </div>
                            <span className="text-sm text-encre">{assignee.displayName}</span>
                          </div>
                        ) : (
                          <Badge className="bg-[#FBF1E1] text-[#7A4A00]">Non assigné</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openTicket(t)}
                            className="p-1.5 text-graphite/70 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ouvrir"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1.5 text-graphite/70 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Statistiques"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          {isLoadingFiltered && paginatedTickets.length === 0 && (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-blue-500 mx-auto mb-3 animate-spin" />
              <p className="text-graphite">Chargement des dossiers…</p>
            </div>
          )}
          {erreurListe && (
            <div role="alert" className="p-6 text-center text-red-700">
              <p className="text-sm">La liste n'a pas pu être chargée : {erreurListe}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => chargerPage(false)}>Réessayer</Button>
            </div>
          )}
          {!isLoadingFiltered && !erreurListe && paginatedTickets.length === 0 && (
            <div className="p-12 text-center">
              <TicketIcon className="w-12 h-12 text-trait mx-auto mb-3" />
              <p className="text-graphite">Aucun dossier trouvé</p>
              <p className="text-sm text-graphite/70 mt-1">Essayez de modifier vos filtres</p>
            </div>
          )}
          {pageDossiers.total > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.max(1, totalPages)}
              totalItems={pageDossiers.total}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

// ============================================
// CLIENTS LIST VIEW
// ============================================

interface ClientWithTickets {
  id: string;
  email: string;
  phone: string | null;
  displayName: string;
  createdAt: string;
  lastSeenAt: string | null;
  isActive: boolean;
  prestashopCustomerId?: number | null;
  totalTickets: number;
  openTickets: number;
  lastTicket: {
    id: string;
    ticketNumber: number;
    title: string;
    status: string;
    priority: string;
    createdAt: string;
  } | null;
  recentTickets: Array<{
    id: string;
    ticketNumber: number;
    title: string;
    status: string;
    priority: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

/**
 * Lien manuel d'un compte client avec son compte klygroupe.com, quand le
 * rattachement automatique a été refusé (ancien compte qui porte des dossiers,
 * sans commande sur la boutique). Le serveur vérifie dans la boutique que le
 * compte existe, est actif et porte la même adresse ; réservé aux
 * administrateurs (PUT /admin/users/:id).
 */
const LienCompteBoutique: React.FC<{ client: ClientWithTickets; onRelie: (id: number) => void }> = ({ client, onRelie }) => {
  const { hasPermission } = useAuth();
  const [valeur, setValeur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  if (client.prestashopCustomerId) {
    return (
      <div className="p-4 bg-brume rounded-lg">
        <p className="text-xs text-graphite mb-1">Compte klygroupe.com</p>
        <p className="font-medium text-encre">Relié au compte n° {client.prestashopCustomerId}</p>
      </div>
    );
  }

  const relier = async () => {
    const saisie = valeur.trim();
    const id = Number(saisie);
    if (!/^\d+$/.test(saisie) || !Number.isSafeInteger(id) || id <= 0) {
      setErreur('Saisissez le numéro du compte client sur klygroupe.com (id_customer).');
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      await AdminApi.updateUser(client.id, { prestashopCustomerId: id });
      setValeur('');
      onRelie(id);
    } catch (error) {
      setErreur(error instanceof Error ? error.message : 'Lien impossible.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="p-4 bg-brume rounded-lg border border-trait space-y-2">
      <p className="text-sm font-semibold text-encre">Compte klygroupe.com : non relié</p>
      <p className="text-xs text-graphite">
        Tant que ce compte n'est pas relié à son compte klygroupe.com, le client peut se voir refuser l'accès à son espace SAV.
        Après vérification de son identité, saisissez le numéro de son compte sur la boutique (id_customer) : le compte doit être actif et porter la même adresse e-mail.
      </p>
      {hasPermission('*') ? (
        <div className="flex flex-wrap gap-2">
          <Input
            value={valeur}
            onChange={(e) => setValeur(e.target.value)}
            placeholder="N° de compte klygroupe.com"
          />
          <Button variant="primary" size="sm" onClick={relier} disabled={envoi || !valeur.trim()}>
            {envoi ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Relier'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-graphite">Seul un administrateur peut relier ce compte.</p>
      )}
      {erreur && <p className="text-xs text-red-600" role="alert">{erreur}</p>}
    </div>
  );
};

const ClientsListView: React.FC = () => {
  const { ouvrirDossier } = useAdmin();
  const [clients, setClients] = useState<ClientWithTickets[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [selectedClient, setSelectedClient] = useState<ClientWithTickets | null>(null);
  // Sous 1024 px : une carte par client au lieu du tableau.
  const etroit = useEcranEtroit();

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const result = await AdminApi.getClientsWithTickets({
        search: searchTerm || undefined,
        page,
        limit: 20,
      });
      setClients(result.clients);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, page]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const formatDate = (date: string) => formatDateParis(date);

  const formatDateTime = (date: string) => formatDateHeure(date);

  return (
    <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-encre">Liste des clients</h1>
          <p className="text-graphite">Clients ayant ouvert un ou plusieurs tickets SAV</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1">
            {pagination.total} client{pagination.total > 1 ? 's' : ''}
          </Badge>
          <Button variant="secondary" onClick={loadClients} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="p-4 max-sm:p-3">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-graphite/70" />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Rechercher un client"
              className="w-full pl-10 pr-4 py-2.5 max-sm:text-base border border-trait rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
        </div>
      </Card>

      {/* Clients list */}
      <Card>
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : clients.length === 0 ? (
          <div className="p-12 text-center">
            <UserCircle className="w-12 h-12 text-trait mx-auto mb-3" />
            <p className="text-graphite">Aucun client trouvé</p>
          </div>
        ) : etroit ? (
          <ul className="divide-y divide-trait" aria-label="Clients" data-testid="cartes-clients">
            {clients.map((client) => (
              <li key={client.id} className="p-4 max-sm:p-3 flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-purple-600">{client.displayName.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-encre truncate">{client.displayName}</p>
                  <a href={`mailto:${client.email}`} className="block py-2.5 text-sm text-blue-600 hover:underline truncate">{client.email}</a>
                  {client.phone && (
                    <a href={`tel:${client.phone}`} className="inline-flex items-center gap-1.5 py-2.5 text-sm text-graphite hover:text-encre">
                      <Phone className="w-3.5 h-3.5" />{client.phone}
                    </a>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge className="bg-blue-100 text-blue-700">{client.totalTickets} total</Badge>
                    {client.openTickets > 0 && (
                      <Badge className="bg-amber-100 text-amber-700">{client.openTickets} ouvert{client.openTickets > 1 ? 's' : ''}</Badge>
                    )}
                    <span className="text-xs text-graphite/70">Inscrit le {formatDate(client.createdAt)}</span>
                  </div>
                  {client.lastTicket && (
                    <p className="text-xs text-graphite mt-1.5 flex items-center gap-1.5 min-w-0">
                      <span className="flex-shrink-0">Dernier : <span className="font-medium text-encre">#{client.lastTicket.ticketNumber}</span></span>
                      <BadgeStatut status={client.lastTicket.status} className="flex-shrink-0" />
                      <span className="truncate">{client.lastTicket.title}</span>
                    </p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedClient(client)} aria-label={`Voir la fiche de ${client.displayName}`}>
                  <Eye className="w-4 h-4" />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-brume border-b border-trait">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-graphite">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-graphite">Contact</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-graphite">Tickets</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-graphite">Dernier ticket</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-graphite">Inscrit le</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-graphite">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-trait">
                {clients.map((client) => (
                  <tr key={client.id} className="hover:bg-brume transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-purple-600">
                            {client.displayName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-encre">{client.displayName}</p>
                          <p className="text-xs text-graphite">ID: {client.id.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-graphite/70" />
                          <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline">{client.email}</a>
                        </div>
                        {client.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-graphite/70" />
                            <a href={`tel:${client.phone}`} className="text-graphite hover:text-encre">{client.phone}</a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Badge className="bg-blue-100 text-blue-700">{client.totalTickets} total</Badge>
                        {client.openTickets > 0 && (
                          <Badge className="bg-amber-100 text-amber-700">{client.openTickets} ouvert{client.openTickets > 1 ? 's' : ''}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {client.lastTicket ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Hash className="w-4 h-4 text-graphite/70" />
                            <span className="text-sm font-medium text-encre">#{client.lastTicket.ticketNumber}</span>
                            <BadgeStatut status={client.lastTicket.status} />
                          </div>
                          <p className="text-xs text-graphite truncate max-w-[200px]">{client.lastTicket.title}</p>
                        </div>
                      ) : (
                        <span className="text-graphite/70 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-graphite">{formatDate(client.createdAt)}</div>
                      {client.lastSeenAt && (
                        <div className="text-xs text-graphite/70">Vu le {formatDateTime(client.lastSeenAt)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedClient(client)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-trait">
            <p className="text-sm text-graphite">
              Page {page} sur {pagination.totalPages} ({pagination.total} clients)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Page précédente"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
                aria-label="Page suivante"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Autres clients de klygroupe.com pour la même recherche (16/10/2026) */}
      <AutresClientsBoutique
        recherche={searchTerm}
        exclureIds={clients.map(c => c.id)}
        onDossierCree={(dossier) => { ouvrirDossier(dossier.id); loadClients(); }}
      />

      {/* Client detail modal */}
      {selectedClient && (
        <Fenetre>
        <div className={`fixed inset-0 bg-encre/50 z-50 flex items-center justify-center p-4 ${FENETRE_FOND_MOBILE}`} onClick={() => setSelectedClient(null)}>
          <div role="dialog" aria-modal="true" aria-label={`Fiche de ${selectedClient.displayName}`} className={`bg-white rounded-card shadow-flottant w-full max-w-2xl max-h-[90vh] overflow-hidden ${FENETRE_MOBILE}`} onClick={e => e.stopPropagation()}>
            <div className="p-6 max-sm:p-4 border-b border-trait flex items-center justify-between gap-3">
              <div className="flex items-center gap-4 max-sm:gap-3 min-w-0">
                <div className="w-14 h-14 max-sm:w-11 max-sm:h-11 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-purple-600">
                    {selectedClient.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl max-sm:text-lg font-bold text-encre break-words">{selectedClient.displayName}</h2>
                  <p className="text-graphite break-all">{selectedClient.email}</p>
                </div>
              </div>
              <button onClick={() => setSelectedClient(null)} aria-label="Fermer" className={`p-2 hover:bg-brume rounded-full flex-shrink-0 inline-flex items-center justify-center ${CIBLE_TACTILE}`}>
                <X className="w-5 h-5 text-graphite/70" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6 max-sm:p-4 max-sm:max-h-none max-sm:space-y-4">
              {/* Infos contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-sm:gap-3">
                <div className="p-4 bg-brume rounded-lg">
                  <p className="text-xs text-graphite mb-1">Email</p>
                  <p className="font-medium text-encre break-all">{selectedClient.email}</p>
                </div>
                <div className="p-4 bg-brume rounded-lg">
                  <p className="text-xs text-graphite mb-1">Téléphone</p>
                  <p className="font-medium text-encre">{selectedClient.phone || '-'}</p>
                </div>
                <div className="p-4 bg-brume rounded-lg">
                  <p className="text-xs text-graphite mb-1">Inscrit le</p>
                  <p className="font-medium text-encre">{formatDateTime(selectedClient.createdAt)}</p>
                </div>
                <div className="p-4 bg-brume rounded-lg">
                  <p className="text-xs text-graphite mb-1">Dernière activité</p>
                  <p className="font-medium text-encre">{selectedClient.lastSeenAt ? formatDateTime(selectedClient.lastSeenAt) : '-'}</p>
                </div>
              </div>

              <LienCompteBoutique
                key={selectedClient.id}
                client={selectedClient}
                onRelie={(id) => {
                  setSelectedClient((c) => (c && c.id === selectedClient.id ? { ...c, prestashopCustomerId: id } : c));
                  setClients((liste) => liste.map((c) => (c.id === selectedClient.id ? { ...c, prestashopCustomerId: id } : c)));
                }}
              />

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-3xl font-bold text-blue-600">{selectedClient.totalTickets}</p>
                  <p className="text-sm text-blue-600">Total tickets</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-3xl font-bold text-amber-600">{selectedClient.openTickets}</p>
                  <p className="text-sm text-amber-600">Tickets ouverts</p>
                </div>
              </div>

              {/* Recent tickets */}
              <div>
                <h3 className="font-semibold text-encre mb-3">Tickets récents</h3>
                <div className="space-y-2">
                  {selectedClient.recentTickets.length === 0 ? (
                    <p className="text-graphite/70 text-center py-4">Aucun ticket</p>
                  ) : (
                    selectedClient.recentTickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        className="p-3 bg-brume rounded-lg hover:bg-brume transition-colors cursor-pointer"
                        onClick={() => {
                          setSelectedClient(null);
                          ouvrirDossier(ticket.id);
                        }}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <span className="text-sm font-medium text-encre">#{ticket.ticketNumber}</span>
                            <BadgeStatut status={ticket.status} />
                            <Badge className={getPriorityColor(ticket.priority as TicketPriority)}>
                              {PRIORITY_LABELS[ticket.priority as TicketPriority] || ticket.priority}
                            </Badge>
                          </div>
                          <span className="text-xs text-graphite/70">{formatDate(ticket.createdAt)}</span>
                        </div>
                        <p className="text-sm text-graphite mt-1 truncate">{ticket.title}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </Fenetre>
      )}
    </div>
  );
};

// ============================================
// OTHER VIEWS (Team, Automation, Analytics, Settings)
// ============================================

const TeamView: React.FC = () => {
  const { users, refreshData } = useAdmin();
  const { hasPermission } = useAuth();
  // Filter staff users (not CUSTOMER) - check both enum and string formats
  // Comptes techniques (assistant IA, standard, boîte e-mail) : personne ne
  // s'y connecte ; ils sont listés à part, sans modification possible.
  const personnel = users.filter(u => u.role !== UserRole.CUSTOMER && u.role !== 'CUSTOMER');
  const staffUsers = personnel.filter(u => !estCompteTechnique(u));
  const comptesSysteme = personnel.filter(u => estCompteTechnique(u));
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ displayName: '', email: '', role: 'AGENT', phone: '', isActive: true, password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const openEditModal = (user: UserType) => {
    setFormData({
      displayName: user.displayName || '',
      email: user.email || '',
      role: user.role || 'AGENT',
      phone: user.phone || '',
      isActive: user.isActive !== false,
      password: '',
    });
    setShowPassword(false);
    setEditingUser(user);
  };

  const openCreateModal = () => {
    setFormData({ displayName: '', email: '', role: 'AGENT', phone: '', isActive: true, password: '' });
    setShowPassword(false);
    setShowCreateModal(true);
  };

  // Compte client : né de la première connexion klygroupe.com (ou, depuis le
  // 16/10/2026, du choix d'un client de la boutique dans un dossier), jamais
  // créé ni promu ici, sans mot de passe propre au SAV. Seule l'activation se règle.
  const estCompteClient = (u: UserType | null) => !!u && (u.role === UserRole.CUSTOMER || u.role === 'CUSTOMER');
  const editionClient = estCompteClient(editingUser);

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      if (editingUser && editionClient) {
        await AdminApi.updateUser(editingUser.id, { isActive: formData.isActive });
      } else if (editingUser) {
        await AdminApi.updateUser(editingUser.id, {
          displayName: formData.displayName,
          role: formData.role,
          phone: formData.phone || undefined,
          isActive: formData.isActive,
          password: formData.password || undefined,
        });
      } else {
        await AdminApi.createUser({
          email: formData.email,
          displayName: formData.displayName,
          role: formData.role,
          phone: formData.phone || undefined,
          password: formData.password || undefined,
        });
      }
      await refreshData();
      setEditingUser(null);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Erreur sauvegarde utilisateur:', error);
      alert(error instanceof Error ? `Enregistrement impossible : ${error.message}` : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    try {
      await AdminApi.deleteUser(userId);
      await refreshData();
      setEditingUser(null);
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-encre">Équipe</h1>
          <p className="text-graphite">{staffUsers.length} membre{staffUsers.length > 1 ? 's' : ''}</p>
        </div>
        {hasPermission('*') && (
          <Button variant="primary" onClick={openCreateModal}>
            <UserPlus className="w-4 h-4 mr-2" />Ajouter
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {staffUsers.map(u => (
          <Card
            key={u.id}
            className="p-5 max-sm:p-4 transition-shadow cursor-pointer hover:border-blue-200"
            onClick={() => hasPermission('*') && openEditModal(u)}
          >
            <div className="flex items-start gap-4 max-sm:gap-3">
              <div className="w-14 h-14 max-sm:w-12 max-sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-blue-600">{getInitials(u.displayName, u.email)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-encre truncate">{u.displayName}</h3>
                  <div className={`w-2.5 h-2.5 rounded-full ${u.isActive !== false ? 'bg-green-500' : 'bg-gray-300'}`} title={u.isActive !== false ? 'Actif' : 'Inactif'} />
                </div>
                <p className="text-sm text-graphite truncate">{u.email}</p>
                <Badge className={`mt-2 ${ROLE_CONFIG[u.role]?.color}`}>{ROLE_CONFIG[u.role]?.icon}{ROLE_CONFIG[u.role]?.label}</Badge>
              </div>
              {hasPermission('*') && (
                <button aria-label={`Modifier ${u.displayName}`} className={`p-1.5 text-graphite/70 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex items-center justify-center flex-shrink-0 ${CIBLE_TACTILE}`}>
                  <Edit3 className="w-4 h-4" />
                </button>
              )}
            </div>
            {u._count?.assignedTickets !== undefined && (
              <div className="mt-4 pt-4 border-t border-trait flex items-center justify-between">
                <span className="text-sm text-graphite">Tickets assignés</span>
                <span className="text-lg font-bold text-encre">{u._count.assignedTickets}</span>
              </div>
            )}
          </Card>
        ))}
      </div>

      {comptesSysteme.length > 0 && (
        <section aria-labelledby="titre-comptes-systeme" className="space-y-3">
          <div>
            <h2 id="titre-comptes-systeme" className="text-base font-semibold text-encre">Comptes système</h2>
            <p className="text-sm text-graphite">Créés par le SAV pour signer les écritures automatiques. Personne ne s'y connecte : ils sont désactivés, sans mot de passe, et ne reçoivent aucun dossier.</p>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {comptesSysteme.map(u => (
              <li key={u.id} className="surface p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-brume rounded-lg flex items-center justify-center flex-shrink-0">
                  <Settings className="w-5 h-5 text-graphite" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-encre truncate">{u.displayName}</h3>
                    <Badge className="bg-brume text-graphite border border-trait">Compte système</Badge>
                  </div>
                  <p className="text-sm text-graphite mt-1">{ROLE_COMPTE_TECHNIQUE[(u.email || '').trim().toLowerCase()] ?? 'Compte technique du SAV.'}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Modal Édition/Création Utilisateur */}
      {(editingUser || showCreateModal) && (
        <Fenetre>
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${FENETRE_FOND_MOBILE}`}>
          <div className="absolute inset-0 bg-encre/50" onClick={() => { setEditingUser(null); setShowCreateModal(false); }} />
          <div role="dialog" aria-modal="true" aria-label={editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'} className={`relative bg-white rounded-card shadow-flottant w-full max-w-md p-6 max-sm:p-4 animate-in zoom-in-95 fade-in ${FENETRE_MOBILE}`}>
            <div className="flex items-center justify-between mb-6 max-sm:mb-4">
              <h2 className="text-lg font-bold text-encre">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h2>
              <button onClick={() => { setEditingUser(null); setShowCreateModal(false); }} aria-label="Fermer" className={`p-2 text-graphite/70 hover:text-graphite rounded-lg inline-flex items-center justify-center ${CIBLE_TACTILE}`}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-encre mb-1">Nom complet</label>
                <Input
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="Jean Dupont"
                  disabled={editionClient}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jean.dupont@kly.fr"
                  disabled={!!editingUser}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-1">Téléphone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                  disabled={editionClient}
                />
              </div>

              {editionClient ? (
                <div className="p-3 bg-brume rounded-lg border border-trait">
                  <p className="text-sm font-semibold text-encre">Compte klygroupe.com</p>
                  <p className="mt-0.5 text-xs text-graphite">Compte client créé à sa première connexion sur klygroupe.com. Ni le rôle ni le mot de passe ne se gèrent ici ; seule l'activation peut être modifiée.</p>
                </div>
              ) : (
              <>
              <div>
                <label className="block text-sm font-medium text-encre mb-1">Rôle</label>
                {/* Pas de rôle client : les comptes clients naissent sur klygroupe.com. */}
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  options={[
                    { value: 'AGENT', label: 'Agent' },
                    { value: 'SUPERVISOR', label: 'Superviseur' },
                    { value: 'ADMIN', label: 'Administrateur' },
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-1">
                  {editingUser ? 'Nouveau mot de passe' : 'Mot de passe'}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? 'Laisser vide pour ne pas modifier' : 'Mot de passe'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-graphite/70 hover:text-graphite max-lg:right-1 max-lg:p-2.5"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
                {editingUser && (
                  <p className="mt-1 text-xs text-graphite">Laissez vide pour conserver le mot de passe actuel</p>
                )}
              </div>
              </>
              )}

              {editingUser && (
                <div className="flex items-center gap-3 p-3 bg-brume rounded-lg">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 max-lg:w-5 max-lg:h-5 rounded border-trait text-blue-600"
                  />
                  <label htmlFor="isActive" className="text-sm text-encre">Compte actif</label>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6 max-sm:gap-2">
              {editingUser && (
                <Button
                  variant="secondary"
                  onClick={() => handleDeleteUser(editingUser.id)}
                  className="text-red-600 hover:bg-red-50 border-red-200"
                  aria-label="Supprimer l'utilisateur"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button variant="secondary" onClick={() => { setEditingUser(null); setShowCreateModal(false); }} className="flex-1">
                Annuler
              </Button>
              <Button variant="primary" onClick={handleSaveUser} disabled={saving || !formData.displayName || !formData.email} className="flex-1">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingUser ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </div>
        </div>
        </Fenetre>
      )}
    </div>
  );
};

const AutomationView: React.FC = () => {
  const [rules, setRules] = useState<Array<{
    id: string;
    name: string;
    description?: string;
    trigger: string;
    conditions: Array<{ field: string; operator: string; value: unknown }>;
    actions: Array<{ type: string; params?: Record<string, unknown> }>;
    isActive: boolean;
    priority: number;
    _count?: { executions: number };
  }>>([]);
  const [stats, setStats] = useState<{
    activeRules: number;
    todayExecutions: number;
    autoAssignCount: number;
    notificationCount: number;
  }>({ activeRules: 0, todayExecutions: 0, autoAssignCount: 0, notificationCount: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger: 'TICKET_CREATED',
    conditions: [] as Array<{ field: string; operator: string; value: string }>,
    actions: [] as Array<{ type: string }>,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Déclencheurs et actions réellement exécutés par le serveur.
  const [catalogue, setCatalogue] = useState<CatalogueAutomatisation>(CATALOGUE_PAR_DEFAUT);
  // Message du serveur (ex. 400 REGLE_NON_PRISE_EN_CHARGE), affiché tel quel.
  const [erreurRegle, setErreurRegle] = useState<string | null>(null);
  const modeles = useMemo(() => modelesDisponibles(catalogue), [catalogue]);

  // Charger les règles et stats au montage
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [rulesData, statsData] = await Promise.all([
        AdminApi.getAutomationRulesEtCatalogue(true),
        AdminApi.getAutomationStats(),
      ]);
      setRules(rulesData.regles);
      setStats(statsData);
      // Catalogue : meta.catalogue, sinon la route dédiée, sinon la liste connue.
      setCatalogue(lireCatalogue(rulesData.catalogue ?? await AdminApi.getAutomationCatalogue()));
    } catch (error) {
      console.error('Erreur chargement automatisation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRule = async (id: string) => {
    try {
      const updated = await AdminApi.toggleAutomationRule(id);
      setRules(rules.map(r => r.id === id ? { ...r, isActive: updated.isActive } : r));
      // Mettre à jour les stats
      const newStats = await AdminApi.getAutomationStats();
      setStats(newStats);
    } catch (error) {
      console.error('Erreur toggle règle:', error);
      setErreurRegle(error instanceof Error ? error.message : 'Modification impossible');
    }
  };

  const deleteRule = async (id: string) => {
    if (!confirm('Supprimer cette règle ?')) return;
    try {
      await AdminApi.deleteAutomationRule(id);
      setRules(rules.filter(r => r.id !== id));
      const newStats = await AdminApi.getAutomationStats();
      setStats(newStats);
    } catch (error) {
      console.error('Erreur suppression règle:', error);
    }
  };

  const addFromTemplate = async (template: ModeleAutomatisation) => {
    setErreurRegle(null);
    try {
      // Convertir les conditions et actions du template
      const conditions = template.conditions.map(c => {
        const parts = c.split(' ');
        return { field: parts[0], operator: 'eq', value: parts[2] || '' };
      });
      const actions = template.actions.map(a => ({ type: a }));

      const newRule = await AdminApi.createAutomationRule({
        name: template.name,
        description: template.description,
        trigger: template.trigger,
        conditions,
        actions,
        isActive: false,
      });
      setRules([...rules, newRule]);
      setShowTemplates(false);
    } catch (error) {
      console.error('Erreur création règle depuis template:', error);
      setErreurRegle(error instanceof Error ? error.message : 'Création impossible');
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.name || newRule.actions.length === 0) {
      alert('Veuillez remplir le nom et ajouter au moins une action');
      return;
    }
    setIsSubmitting(true);
    setErreurRegle(null);
    try {
      const created = await AdminApi.createAutomationRule({
        name: newRule.name,
        description: newRule.description,
        trigger: newRule.trigger,
        conditions: newRule.conditions,
        actions: newRule.actions,
        isActive: true,
      });
      setRules([...rules, created]);
      setShowCreateModal(false);
      setNewRule({ name: '', description: '', trigger: 'TICKET_CREATED', conditions: [], actions: [] });
      const newStats = await AdminApi.getAutomationStats();
      setStats(newStats);
    } catch (error) {
      console.error('Erreur création règle:', error);
      setErreurRegle(error instanceof Error ? error.message : 'Création impossible');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCondition = (c: { field: string; operator: string; value: unknown }) => {
    const opLabels: Record<string, string> = { eq: '=', neq: '≠', gt: '>', lt: '<', gte: '≥', lte: '≤', contains: 'contient', in: 'dans' };
    return `${c.field} ${opLabels[c.operator] || c.operator} ${c.value}`;
  };

  const getActionLabel = (action: { type: string }) => {
    return libelleCatalogue(catalogue, action.type, ACTION_LABELS[action.type]?.label || action.type);
  };

  const getActionColor = (action: { type: string }) => {
    const color = ACTION_LABELS[action.type]?.color;
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200',
      red: 'bg-red-50 text-red-700 border-red-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      orange: 'bg-orange-50 text-orange-700 border-orange-200',
    };
    return colorMap[color || ''] || 'bg-brume text-encre border-trait';
  };

  // Libellé d'un déclencheur (le code brut s'il est inconnu).
  const libelleDeclencheur = (code: string) => libelleCatalogue(catalogue, code, TRIGGER_LABELS[code] || code);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-encre">Automatisation</h1>
          <p className="text-graphite">{rules.length} règle{rules.length > 1 ? 's' : ''} • {stats.activeRules} active{stats.activeRules > 1 ? 's' : ''}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setShowTemplates(!showTemplates)}>
            <Sparkles className="w-4 h-4 mr-2" />Templates
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />Nouvelle règle
          </Button>
        </div>
      </div>

      {/* Templates dropdown */}
      {showTemplates && (
        <Card className="p-4 bg-purple-50 border-purple-200">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-encre">Templates prédéfinis</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modeles.length === 0 && <p className="text-sm text-graphite">Aucun modèle compatible avec les automatisations prises en charge.</p>}
            {modeles.map(t => (
              <div key={t.id} className="bg-white rounded-lg p-4 border border-purple-200 hover:border-purple-400 transition-colors cursor-pointer" onClick={() => addFromTemplate(t)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-medium text-encre">{t.name}</h4>
                    <p className="text-xs text-graphite mt-1">{t.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{libelleDeclencheur(t.trigger)}</span>
                    </div>
                  </div>
                  <Plus className="w-5 h-5 text-purple-400" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {erreurRegle && (
        <div role="alert" className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{erreurRegle}</span>
          <button onClick={() => setErreurRegle(null)} aria-label="Masquer" className="opacity-70 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-sm:gap-3">
        <Card className="p-4 bg-green-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Activity className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{stats.activeRules}</p>
              <p className="text-xs text-green-600">Règles actives</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{stats.todayExecutions}</p>
              <p className="text-xs text-blue-600">Exécutions aujourd'hui</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{stats.autoAssignCount}</p>
              <p className="text-xs text-purple-600">Auto-assignations</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Bell className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-700">{stats.notificationCount}</p>
              <p className="text-xs text-orange-600">Notifications envoyées</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Rules list */}
      <div className="grid gap-4">
        {rules.map(r => {
          // Règle hors catalogue : le serveur ne l'exécute pas, elle s'affiche inactive.
          const nonPriseEnCharge = regleHorsCatalogue(r, catalogue);
          const active = r.isActive && !nonPriseEnCharge;
          return (
          <Card key={r.id} className={`p-5 max-sm:p-4 transition-all ${active ? 'border-l-4 border-l-green-500' : 'opacity-75'}`}>
            {/* Téléphone : interrupteur et suppression passent sous la règle. */}
            <div className="flex items-start justify-between max-sm:flex-col max-sm:gap-3">
              <div className="flex items-start gap-4 max-sm:gap-3 min-w-0">
                <div className={`p-3 max-sm:p-2 rounded-lg flex-shrink-0 ${active ? 'bg-green-100' : 'bg-brume'}`}>
                  <Workflow className={`w-6 h-6 ${active ? 'text-green-600' : 'text-graphite/70'}`} />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-encre break-words">{r.name}</h3>
                    {active && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>}
                    {nonPriseEnCharge && <span className="text-xs bg-[#FBF1E1] text-[#7A4A00] px-2 py-0.5 rounded-full">inactive : déclencheur ou action non pris en charge</span>}
                    {r._count?.executions !== undefined && (
                      <span className="text-xs text-graphite/70">{r._count.executions} exécution{r._count.executions > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  {r.description && <p className="text-xs text-graphite/70 mt-0.5">{r.description}</p>}
                  <p className="text-sm text-graphite mt-1">
                    Déclencheur : <code className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{libelleDeclencheur(r.trigger)}</code>
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="text-xs text-graphite/70">SI</span>
                    {r.conditions.length > 0 ? r.conditions.map((c, i) => (
                      <span key={i} className="text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded-lg border border-yellow-200">{formatCondition(c)}</span>
                    )) : <span className="text-xs text-graphite/70 italic">Toujours</span>}
                    <span className="text-graphite/70">→</span>
                    <span className="text-xs text-graphite/70">ALORS</span>
                    {r.actions.map((a, i) => (
                      <span key={i} className={`text-xs px-2 py-1 rounded-lg border ${getActionColor(a)}`}>
                        {getActionLabel(a)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 max-sm:self-end">
                <button
                  onClick={() => toggleRule(r.id)}
                  disabled={nonPriseEnCharge && !r.isActive}
                  role="switch"
                  aria-checked={active}
                  aria-label={`Règle « ${r.name} »`}
                  className={`relative w-12 h-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${ZONE_TACTILE} ${active ? 'bg-green-500' : 'bg-gray-300'}`}
                  title={nonPriseEnCharge ? (r.isActive ? 'Désactiver (non prise en charge)' : 'Non prise en charge : activation impossible') : r.isActive ? 'Désactiver' : 'Activer'}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${active ? 'translate-x-6' : ''}`} />
                </button>
                <Button variant="ghost" size="xs" onClick={() => deleteRule(r.id)} className="text-red-500 hover:bg-red-50" title="Supprimer" aria-label={`Supprimer la règle « ${r.name} »`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </Card>
          );
        })}
      </div>

      {rules.length === 0 && (
        <Card className="p-12 text-center">
          <Workflow className="w-12 h-12 text-trait mx-auto mb-3" />
          <p className="text-graphite">Aucune règle d'automatisation</p>
          <p className="text-sm text-graphite/70 mt-1">Créez votre première règle ou utilisez un template</p>
          <Button variant="primary" className="mt-4" onClick={() => setShowTemplates(true)}>
            <Sparkles className="w-4 h-4 mr-2" />Voir les templates
          </Button>
        </Card>
      )}

      {/* Modal de création */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Nouvelle règle d'automatisation" size="lg">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-encre mb-1">Nom de la règle</label>
            <Input value={newRule.name} onChange={e => setNewRule({ ...newRule, name: e.target.value })} placeholder="Ex: Auto-assignation tickets urgents" />
          </div>
          <div>
            <label className="block text-sm font-medium text-encre mb-1">Description (optionnel)</label>
            <Input value={newRule.description} onChange={e => setNewRule({ ...newRule, description: e.target.value })} placeholder="Description de la règle" />
          </div>
          <div>
            <label className="block text-sm font-medium text-encre mb-1">Déclencheur</label>
            <Select
              value={newRule.trigger}
              onChange={e => setNewRule({ ...newRule, trigger: e.target.value })}
              options={catalogue.declencheurs.map(value => ({ value, label: libelleDeclencheur(value) }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-encre mb-2">Actions</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {newRule.actions.map((a, i) => (
                <span key={i} className={`text-xs px-2 py-1 rounded-lg border flex items-center gap-1 ${getActionColor(a)}`}>
                  {getActionLabel(a)}
                  <button onClick={() => setNewRule({ ...newRule, actions: newRule.actions.filter((_, idx) => idx !== i) })} aria-label={`Retirer l'action ${getActionLabel(a)}`} className="ml-1 text-graphite/70 hover:text-red-500 max-lg:px-2 max-lg:-my-1 max-lg:text-base">×</button>
                </span>
              ))}
            </div>
            <Select
              value=""
              onChange={e => {
                if (e.target.value && !newRule.actions.find(a => a.type === e.target.value)) {
                  setNewRule({ ...newRule, actions: [...newRule.actions, { type: e.target.value }] });
                }
              }}
              options={catalogue.actions.map(value => ({ value, label: libelleCatalogue(catalogue, value, ACTION_LABELS[value]?.label || value) }))}
              placeholder="Ajouter une action..."
            />
          </div>
          {erreurRegle && <p role="alert" className="text-sm text-red-700">{erreurRegle}</p>}
          <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button variant="primary" onClick={handleCreateRule} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer la règle
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const FILTRES_ANALYSE = {};

const AnalyticsView: React.FC = () => {
  const { users } = useAdmin();
  // Ses propres données : tous les dossiers (pages de 100 parcourues, borne de 2 000).
  const { dossiers: tickets } = useTousLesDossiers(true, FILTRES_ANALYSE);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '1y' | 'all'>('30d');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterAgent, setFilterAgent] = useState<string>('all');
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Filtrer les tickets selon les critères
  const filteredTickets = useMemo(() => {
    let filtered = [...tickets];

    // Filtre par date
    const now = new Date();
    const dateFilters: Record<string, number> = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365,
    };
    if (dateRange !== 'all' && dateFilters[dateRange]) {
      const cutoff = new Date(now.getTime() - dateFilters[dateRange] * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(t => new Date(t.createdAt) >= cutoff);
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    // Filtre par priorité
    if (filterPriority !== 'all') {
      filtered = filtered.filter(t => t.priority === filterPriority);
    }

    // Filtre par type
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.issueType === filterType);
    }

    // Filtre par agent
    if (filterAgent !== 'all') {
      filtered = filtered.filter(t => t.assignedToId === filterAgent);
    }

    return filtered;
  }, [tickets, dateRange, filterStatus, filterPriority, filterType, filterAgent]);

  // Calculer les stats filtrées
  const filteredStats = useMemo(() => {
    const byStatus: Record<string, number> = {};
    const byPriority: Record<string, number> = {};
    const byIssueType: Record<string, number> = {};
    const byAgent: Record<string, number> = {};
    let slaBreached = 0;
    let totalResolutionTime = 0;
    let resolvedCount = 0;

    filteredTickets.forEach(t => {
      byStatus[t.status] = (byStatus[t.status] || 0) + 1;
      byPriority[t.priority] = (byPriority[t.priority] || 0) + 1;
      byIssueType[t.issueType] = (byIssueType[t.issueType] || 0) + 1;
      if (t.assignedToId) {
        byAgent[t.assignedToId] = (byAgent[t.assignedToId] || 0) + 1;
      }
      if (t.slaBreached) slaBreached++;
      if (t.status === 'RESOLVED' || t.status === 'CLOSED') {
        const created = new Date(t.createdAt).getTime();
        const updated = new Date(t.updatedAt).getTime();
        totalResolutionTime += (updated - created) / (1000 * 60); // minutes
        resolvedCount++;
      }
    });

    return {
      total: filteredTickets.length,
      byStatus,
      byPriority,
      byIssueType,
      byAgent,
      slaBreached,
      avgResolutionTime: resolvedCount > 0 ? totalResolutionTime / resolvedCount : 0,
      resolvedCount,
    };
  }, [filteredTickets]);

  // Agents pour le filtre
  const agents = users.filter(u => ['AGENT', 'SUPERVISOR', 'ADMIN'].includes(u.role as string));

  // Export CSV
  const exportCSV = () => {
    const headers = ['N° Ticket', 'Titre', 'Statut', 'Priorité', 'Type', 'Client', 'Agent', 'Créé le', 'SLA dépassé'];
    const rows = filteredTickets.map(t => [
      t.ticketNumber || t.id,
      `"${(t.title || '').replace(/"/g, '""')}"`,
      STATUS_LABELS[t.status] || t.status,
      PRIORITY_LABELS[t.priority] || t.priority,
      ISSUE_TYPE_LABELS[t.issueType] || t.issueType,
      `"${(t.customer?.displayName || t.contactName || t.companyName || '').replace(/"/g, '""')}"`,
      `"${(t.assignedTo?.displayName || 'Non assigné').replace(/"/g, '""')}"`,
      formatDateParis(t.createdAt),
      t.slaBreached ? 'Oui' : 'Non',
    ]);

    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_tickets_${jourParis()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  // Export JSON
  const exportJSON = () => {
    const data = {
      exportDate: new Date().toISOString(),
      filters: { dateRange, filterStatus, filterPriority, filterType, filterAgent },
      summary: filteredStats,
      tickets: filteredTickets.map(t => ({
        ticketNumber: t.ticketNumber,
        title: t.title,
        status: t.status,
        priority: t.priority,
        issueType: t.issueType,
        customer: t.customer?.displayName || t.contactName || t.companyName,
        agent: t.assignedTo?.displayName,
        createdAt: t.createdAt,
        slaBreached: t.slaBreached,
      })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_tickets_${jourParis()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  // Export rapport texte
  const exportReport = () => {
    const slaRate = filteredStats.total > 0 ? Math.round(((filteredStats.total - filteredStats.slaBreached) / filteredStats.total) * 100) : 0;
    const avgTime = filteredStats.avgResolutionTime > 0 ? `${Math.round(filteredStats.avgResolutionTime / 60)}h ${Math.round(filteredStats.avgResolutionTime % 60)}m` : 'N/A';

    let report = `RAPPORT ANALYTICS SAV PRO\n`;
    report += `${'='.repeat(50)}\n`;
    report += `Date d'export: ${formatDateTime(new Date())}\n`;
    report += `Période: ${dateRange === 'all' ? 'Toutes les données' : `${dateRange.replace('d', ' jours').replace('y', ' an')}`}\n\n`;

    report += `RÉSUMÉ\n${'-'.repeat(30)}\n`;
    report += `Total tickets: ${filteredStats.total}\n`;
    report += `Taux SLA: ${slaRate}%\n`;
    report += `SLA dépassés: ${filteredStats.slaBreached}\n`;
    report += `Temps moyen résolution: ${avgTime}\n`;
    report += `Tickets résolus: ${filteredStats.resolvedCount}\n\n`;

    report += `PAR STATUT\n${'-'.repeat(30)}\n`;
    Object.entries(STATUS_LABELS).forEach(([status, label]) => {
      const count = filteredStats.byStatus[status] || 0;
      const pct = filteredStats.total > 0 ? Math.round((count / filteredStats.total) * 100) : 0;
      report += `${label}: ${count} (${pct}%)\n`;
    });

    report += `\nPAR PRIORITÉ\n${'-'.repeat(30)}\n`;
    Object.entries(PRIORITY_LABELS).forEach(([priority, label]) => {
      const count = filteredStats.byPriority[priority] || 0;
      const pct = filteredStats.total > 0 ? Math.round((count / filteredStats.total) * 100) : 0;
      report += `${label}: ${count} (${pct}%)\n`;
    });

    report += `\nPAR TYPE\n${'-'.repeat(30)}\n`;
    Object.entries(ISSUE_TYPE_LABELS).forEach(([type, label]) => {
      const count = filteredStats.byIssueType[type] || 0;
      const pct = filteredStats.total > 0 ? Math.round((count / filteredStats.total) * 100) : 0;
      report += `${label}: ${count} (${pct}%)\n`;
    });

    if (Object.keys(filteredStats.byAgent).length > 0) {
      report += `\nPAR AGENT\n${'-'.repeat(30)}\n`;
      Object.entries(filteredStats.byAgent).forEach(([agentId, count]) => {
        const agent = users.find(u => u.id === agentId);
        const pct = filteredStats.total > 0 ? Math.round((count / filteredStats.total) * 100) : 0;
        report += `${agent?.displayName || 'Inconnu'}: ${count} (${pct}%)\n`;
      });
    }

    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapport_analytics_${jourParis()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const slaRate = filteredStats.total > 0 ? Math.round(((filteredStats.total - filteredStats.slaBreached) / filteredStats.total) * 100) : 0;

  return (
    <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-encre">Analytics</h1>
          <p className="text-graphite">Statistiques et métriques • {filteredStats.total} tickets</p>
        </div>
        <div className="relative">
          <Button variant="secondary" onClick={() => setShowExportMenu(!showExportMenu)}>
            <Download className="w-4 h-4 mr-2" />Exporter
            <ChevronDown className="w-4 h-4 ml-2" />
          </Button>
          {showExportMenu && (
            <div className="absolute right-0 max-sm:right-auto max-sm:left-0 top-full mt-2 w-48 bg-white rounded-lg shadow-flottant border border-trait py-2 z-50">
              <button onClick={exportCSV} className="w-full px-4 py-2 max-lg:py-3 text-left text-sm hover:bg-brume flex items-center gap-2">
                <FileText className="w-4 h-4 text-green-600" />
                Export CSV (Excel)
              </button>
              <button onClick={exportJSON} className="w-full px-4 py-2 max-lg:py-3 text-left text-sm hover:bg-brume flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Export JSON
              </button>
              <button onClick={exportReport} className="w-full px-4 py-2 max-lg:py-3 text-left text-sm hover:bg-brume flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600" />
                Rapport texte
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filtres */}
      <Card className="p-4 max-sm:p-3">
        <div className="flex flex-wrap items-center gap-4 max-sm:gap-2">
          <div className="flex items-center gap-2 max-sm:w-full">
            <Filter className="w-4 h-4 text-graphite" />
            <span className="text-sm font-medium text-encre">Filtres:</span>
          </div>

          {/* Période */}
          <select
            value={dateRange}
            onChange={e => setDateRange(e.target.value as typeof dateRange)}
            className="text-sm max-sm:text-base border border-trait rounded-lg px-3 py-1.5 max-lg:min-h-10 max-sm:flex-1 max-sm:min-w-[45%] bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">7 derniers jours</option>
            <option value="30d">30 derniers jours</option>
            <option value="90d">90 derniers jours</option>
            <option value="1y">1 an</option>
            <option value="all">Tout</option>
          </select>

          {/* Statut */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="text-sm max-sm:text-base border border-trait rounded-lg px-3 py-1.5 max-lg:min-h-10 max-sm:flex-1 max-sm:min-w-[45%] bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Tous les statuts</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {/* Priorité */}
          <select
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
            className="text-sm max-sm:text-base border border-trait rounded-lg px-3 py-1.5 max-lg:min-h-10 max-sm:flex-1 max-sm:min-w-[45%] bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Toutes priorités</option>
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {/* Type */}
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="text-sm max-sm:text-base border border-trait rounded-lg px-3 py-1.5 max-lg:min-h-10 max-sm:flex-1 max-sm:min-w-[45%] bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Tous les types</option>
            {Object.entries(ISSUE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          {/* Agent */}
          <select
            value={filterAgent}
            onChange={e => setFilterAgent(e.target.value)}
            className="text-sm max-sm:text-base border border-trait rounded-lg px-3 py-1.5 max-lg:min-h-10 max-sm:flex-1 max-sm:min-w-[45%] bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Tous les agents</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>{agent.displayName}</option>
            ))}
          </select>

          {/* Reset */}
          {(dateRange !== '30d' || filterStatus !== 'all' || filterPriority !== 'all' || filterType !== 'all' || filterAgent !== 'all') && (
            <button
              onClick={() => {
                setDateRange('30d');
                setFilterStatus('all');
                setFilterPriority('all');
                setFilterType('all');
                setFilterAgent('all');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 max-lg:min-h-10"
            >
              <X className="w-4 h-4" />
              Réinitialiser
            </button>
          )}
        </div>
      </Card>

      {/* KPIs filtrés */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-sm:gap-3">
        <KPICard title="Total filtré" value={filteredStats.total} icon={<TicketIcon className="w-6 h-6" />} color="purple" subtitle={dateRange === 'all' ? 'Historique complet' : `${dateRange.replace('d', ' jours').replace('y', ' an')}`} />
        <KPICard title="Taux SLA" value={`${slaRate}%`} icon={<Target className="w-6 h-6" />} color="green" subtitle="Respect des délais" />
        <KPICard title="Temps moyen" value={filteredStats.avgResolutionTime > 0 ? `${Math.round(filteredStats.avgResolutionTime / 60)}h` : 'N/A'} icon={<Timer className="w-6 h-6" />} color="blue" subtitle="Résolution" />
        <KPICard title="SLA dépassés" value={filteredStats.slaBreached} icon={<AlertTriangle className="w-6 h-6" />} color="red" subtitle="Hors délai" />
      </div>

      {/* Graphiques visuels - Donut Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Donut - Par statut */}
        <Card className="p-6 max-sm:p-4">
          <h3 className="font-semibold text-encre mb-4 text-center">Répartition par statut</h3>
          <DonutChart
            data={[
              { label: 'Ouvert', value: filteredStats.byStatus['OPEN'] || 0, color: COULEUR_STATUT[TicketStatus.OPEN] },
              { label: 'En cours', value: filteredStats.byStatus['IN_PROGRESS'] || 0, color: COULEUR_STATUT[TicketStatus.IN_PROGRESS] },
              { label: 'Attente', value: filteredStats.byStatus['WAITING_CUSTOMER'] || 0, color: COULEUR_STATUT[TicketStatus.WAITING_CUSTOMER] },
              { label: 'Résolu', value: filteredStats.byStatus['RESOLVED'] || 0, color: COULEUR_STATUT[TicketStatus.RESOLVED] },
              { label: 'Fermé', value: filteredStats.byStatus['CLOSED'] || 0, color: COULEUR_STATUT[TicketStatus.CLOSED] },
              { label: 'Escaladé', value: filteredStats.byStatus['ESCALATED'] || 0, color: COULEUR_STATUT[TicketStatus.ESCALATED] },
              { label: 'Réouvert', value: filteredStats.byStatus['REOPENED'] || 0, color: COULEUR_STATUT[TicketStatus.REOPENED] },
            ].filter(d => d.value > 0)}
            centerValue={filteredStats.total}
            centerLabel="tickets"
          />
        </Card>

        {/* Donut - Par priorité */}
        <Card className="p-6 max-sm:p-4">
          <h3 className="font-semibold text-encre mb-4 text-center">Répartition par priorité</h3>
          <DonutChart
            data={[
              { label: 'Basse', value: filteredStats.byPriority['LOW'] || 0, color: '#9ca3af' },
              { label: 'Moyenne', value: filteredStats.byPriority['MEDIUM'] || 0, color: '#3b82f6' },
              { label: 'Haute', value: filteredStats.byPriority['HIGH'] || 0, color: '#f97316' },
              { label: 'Urgente', value: filteredStats.byPriority['URGENT'] || 0, color: '#ef4444' },
            ].filter(d => d.value > 0)}
            centerValue={`${slaRate}%`}
            centerLabel="SLA OK"
          />
        </Card>

        {/* Donut - Par type */}
        <Card className="p-6 max-sm:p-4">
          <h3 className="font-semibold text-encre mb-4 text-center">Répartition par type</h3>
          <DonutChart
            data={[
              { label: 'Technique', value: filteredStats.byIssueType['TECHNICAL'] || 0, color: '#8b5cf6' },
              { label: 'Livraison', value: filteredStats.byIssueType['DELIVERY'] || 0, color: '#3b82f6' },
              { label: 'Facturation', value: filteredStats.byIssueType['BILLING'] || 0, color: '#22c55e' },
              { label: 'Autre', value: filteredStats.byIssueType['OTHER'] || 0, color: '#6b7280' },
            ].filter(d => d.value > 0)}
            centerValue={filteredStats.resolvedCount}
            centerLabel="résolus"
          />
        </Card>
      </div>

      {/* Graphiques barres et tendances */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - Priorité */}
        <Card className="p-6 max-sm:p-4">
          <h3 className="font-semibold text-encre mb-4">Tickets par priorité</h3>
          <div className="flex justify-center">
            <VerticalBarChart
              data={[
                { label: 'Basse', value: filteredStats.byPriority['LOW'] || 0, color: '#9ca3af' },
                { label: 'Moyenne', value: filteredStats.byPriority['MEDIUM'] || 0, color: '#3b82f6' },
                { label: 'Haute', value: filteredStats.byPriority['HIGH'] || 0, color: '#f97316' },
                { label: 'Urgente', value: filteredStats.byPriority['URGENT'] || 0, color: '#ef4444' },
              ]}
              width={320}
              height={200}
            />
          </div>
        </Card>

        {/* Tendance tickets par jour */}
        <Card className="p-6 max-sm:p-4">
          <h3 className="font-semibold text-encre mb-4">Tendance des tickets (7 derniers jours)</h3>
          <div className="flex justify-center">
            <AreaLineChart
              data={(() => {
                // Jours de Paris, aujourd'hui compris.
                const days = [];
                const aujourdhui = jourParis();
                for (let i = 6; i >= 0; i--) {
                  const jour = decalerJour(aujourdhui, -i);
                  const dayStr = formatJour(jour, { weekday: 'short' });
                  const count = filteredTickets.filter(t => jourParis(t.createdAt) === jour).length;
                  days.push({ label: dayStr, value: count });
                }
                return days;
              })()}
              width={350}
              height={180}
              color="#8b5cf6"
              gradientId="trendGradient"
            />
          </div>
        </Card>

        {/* Performance agents - Horizontal bars */}
        <Card className="p-6 max-sm:p-4">
          <h3 className="font-semibold text-encre mb-4">Charge par agent</h3>
          {agents.length > 0 ? (
            <HorizontalBarChart
              data={agents.slice(0, 5).map((agent, i) => ({
                label: agent.displayName,
                value: filteredStats.byAgent[agent.id] || 0,
                color: ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444'][i % 5],
                percent: filteredStats.total > 0 ? Math.round(((filteredStats.byAgent[agent.id] || 0) / filteredStats.total) * 100) : 0,
              }))}
            />
          ) : (
            <p className="text-graphite text-sm text-center py-8">Aucun agent trouvé</p>
          )}
        </Card>

        {/* Types de problèmes - Horizontal bars */}
        <Card className="p-6 max-sm:p-4">
          <h3 className="font-semibold text-encre mb-4">Types de problèmes</h3>
          <HorizontalBarChart
            data={[
              { label: 'Technique', value: filteredStats.byIssueType['TECHNICAL'] || 0, color: '#8b5cf6', percent: filteredStats.total > 0 ? Math.round(((filteredStats.byIssueType['TECHNICAL'] || 0) / filteredStats.total) * 100) : 0 },
              { label: 'Livraison', value: filteredStats.byIssueType['DELIVERY'] || 0, color: '#3b82f6', percent: filteredStats.total > 0 ? Math.round(((filteredStats.byIssueType['DELIVERY'] || 0) / filteredStats.total) * 100) : 0 },
              { label: 'Facturation', value: filteredStats.byIssueType['BILLING'] || 0, color: '#22c55e', percent: filteredStats.total > 0 ? Math.round(((filteredStats.byIssueType['BILLING'] || 0) / filteredStats.total) * 100) : 0 },
              { label: 'Autre', value: filteredStats.byIssueType['OTHER'] || 0, color: '#6b7280', percent: filteredStats.total > 0 ? Math.round(((filteredStats.byIssueType['OTHER'] || 0) / filteredStats.total) * 100) : 0 },
            ]}
          />
        </Card>
      </div>

      {/* Métriques détaillées */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-sm:gap-3">
        <Card className="p-4 bg-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Tickets ouverts</p>
              <p className="text-2xl font-bold text-blue-900">{(filteredStats.byStatus['OPEN'] || 0) + (filteredStats.byStatus['REOPENED'] || 0)}</p>
            </div>
            <Sparkline data={[3, 5, 4, 7, 6, 8, (filteredStats.byStatus['OPEN'] || 0)]} color="#3b82f6" />
          </div>
        </Card>
        <Card className="p-4 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 font-medium">En traitement</p>
              <p className="text-2xl font-bold text-amber-900">{filteredStats.byStatus['IN_PROGRESS'] || 0}</p>
            </div>
            <Sparkline data={[2, 4, 3, 5, 4, 6, (filteredStats.byStatus['IN_PROGRESS'] || 0)]} color="#f59e0b" />
          </div>
        </Card>
        <Card className="p-4 bg-green-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Résolus</p>
              <p className="text-2xl font-bold text-green-900">{filteredStats.resolvedCount}</p>
            </div>
            <Sparkline data={[5, 7, 6, 8, 9, 7, filteredStats.resolvedCount]} color="#22c55e" />
          </div>
        </Card>
        <Card className="p-4 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">SLA dépassés</p>
              <p className="text-2xl font-bold text-red-900">{filteredStats.slaBreached}</p>
            </div>
            <Sparkline data={[1, 2, 1, 3, 2, 1, filteredStats.slaBreached]} color="#ef4444" />
          </div>
        </Card>
      </div>

      {/* Tableau récapitulatif */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-trait flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-encre">Détail des tickets filtrés</h3>
          <span className="text-sm text-graphite">{filteredStats.total} résultats</span>
        </div>
        <DefilementHorizontal libelle="Détail des tickets filtrés">
          <table className="w-full">
            <thead className="bg-brume">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-graphite uppercase">N°</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-graphite uppercase">Titre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-graphite uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-graphite uppercase">Priorité</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-graphite uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-graphite uppercase">Agent</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-graphite uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-trait">
              {filteredTickets.slice(0, 10).map(t => (
                <tr key={t.id} className="hover:bg-brume">
                  <td className="px-4 py-3 text-sm font-medium text-encre">#{t.ticketRef || t.ticketNumber || t.id.slice(0, 6)}</td>
                  <td className="px-4 py-3 text-sm text-encre max-w-[200px] truncate">{t.title}</td>
                  <td className="px-4 py-3"><BadgeStatut status={t.status} /></td>
                  <td className="px-4 py-3"><Badge className={getPriorityColor(t.priority)}>{PRIORITY_LABELS[t.priority]}</Badge></td>
                  <td className="px-4 py-3 text-sm text-graphite">{ISSUE_TYPE_LABELS[t.issueType]}</td>
                  <td className="px-4 py-3 text-sm text-graphite">{t.assignedTo?.displayName || '-'}</td>
                  <td className="px-4 py-3 text-sm text-graphite">{formatDateParis(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </DefilementHorizontal>
          {filteredTickets.length > 10 && (
            <div className="p-4 text-center text-sm text-graphite bg-brume">
              + {filteredTickets.length - 10} autres tickets • Exportez pour voir la liste complète
            </div>
          )}
          {filteredTickets.length === 0 && (
            <div className="p-8 text-center text-graphite">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-trait" />
              <p>Aucun ticket ne correspond aux filtres sélectionnés</p>
            </div>
          )}
      </Card>
    </div>
  );
};

// ============================================
// GENERAL SETTINGS TAB (Language & Theme)
// ============================================

// Theme preview styles - uses THEME_STYLES for consistency
const getThemePreview = (key: AppTheme) => ({
  sidebar: THEME_STYLES[key].sidebarBg,
  logo: THEME_STYLES[key].logoBg,
});

const GeneralSettingsTab: React.FC = () => {
  const { appTheme, setAppTheme, appLanguage, setAppLanguage, t } = useAdmin();

  return (
    <div className="space-y-8">
      <h2 className="text-lg font-semibold text-encre">{t('settings.general')}</h2>

      {/* Language Selection */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-encre mb-1">{t('settings.language')}</h3>
          <p className="text-xs text-graphite mb-3">{t('settings.languageDesc')}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          {([
            { code: 'fr' as AppLanguage, name: 'Français', flag: '🇫🇷' },
            { code: 'en' as AppLanguage, name: 'English', flag: '🇬🇧' },
          ]).map(({ code, name, flag }) => (
            <button
              key={code}
              onClick={() => setAppLanguage(code)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-2
                ${appLanguage === code
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-trait hover:border-trait hover:bg-brume'
                }`}
            >
              <span className="text-3xl">{flag}</span>
              <span className={`text-sm font-medium ${appLanguage === code ? 'text-blue-700' : 'text-encre'}`}>
                {name}
              </span>
              {appLanguage === code && (
                <CheckCircle className="w-4 h-4 text-blue-500" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Theme Selection */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-encre mb-1">{t('settings.theme')}</h3>
          <p className="text-xs text-graphite mb-3">{t('settings.themeDesc')}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-sm:gap-3">
          {(['classic', 'blue', 'green'] as AppTheme[]).map((key) => {
            const themeInfo = THEME_STYLES[key];
            const preview = getThemePreview(key);
            return (
              <button
                key={key}
                onClick={() => setAppTheme(key)}
                className={`p-4 rounded-lg border-2 transition-all duration-200
                  ${appTheme === key
                    ? 'border-blue-500 shadow-flottant scale-105'
                    : 'border-trait hover:border-trait'
                  }`}
              >
                {/* Theme preview with inline styles */}
                <div
                  className={`h-16 rounded-lg mb-3 flex items-center justify-center ${themeInfo.isLight ? 'border border-trait' : ''}`}
                  style={{ background: preview.sidebar }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shadow-flottant"
                    style={{ background: preview.logo }}
                  >
                    <Wrench className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className={`text-sm font-medium ${appTheme === key ? 'text-blue-700' : 'text-encre'}`}>
                  {themeInfo.name}
                </span>
                <span className="block text-xs text-graphite mt-1">{themeInfo.description}</span>
                {appTheme === key && (
                  <div className="flex justify-center mt-2">
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Company Info (read-only for now) */}
      <div className="space-y-4 pt-4 border-t border-trait">
        <h3 className="text-sm font-semibold text-encre">Informations entreprise</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-graphite">Nom de l'entreprise</label>
            <Input value="KLY GROUPE" className="mt-1" readOnly />
          </div>
          <div>
            <label className="text-xs font-medium text-graphite">Email support</label>
            <Input value="support@kly.fr" className="mt-1" readOnly />
          </div>
        </div>
      </div>

      <div className="text-xs text-graphite/70">
        Les changements de langue et de thème sont appliqués immédiatement et sauvegardés automatiquement.
      </div>
    </div>
  );
};

// ============================================
// VUE: Gestion des Marques (Base de connaissances)
// ============================================

const BrandsView: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    folderUrl: '',
    websiteUrl: '',
    isActive: true,
  });

  // Charger les marques
  const loadBrands = useCallback(async () => {
    try {
      setLoading(true);
      const data = await AdminApi.getBrands();
      setBrands(data);
    } catch (error) {
      console.error('Erreur chargement marques:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Ouvrir modal création
  const handleCreate = () => {
    setEditingBrand(null);
    setFormData({
      name: '',
      description: '',
      logoUrl: '',
      folderUrl: '',
      websiteUrl: '',
      isActive: true,
    });
    setShowModal(true);
  };

  // Ouvrir modal édition
  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      description: brand.description || '',
      logoUrl: brand.logoUrl || '',
      folderUrl: brand.folderUrl || '',
      websiteUrl: brand.websiteUrl || '',
      isActive: brand.isActive,
    });
    setShowModal(true);
  };

  // Sauvegarder (création ou mise à jour)
  const handleSave = async () => {
    if (!formData.name.trim()) return;

    setSaving(true);
    try {
      if (editingBrand) {
        await AdminApi.updateBrand(editingBrand.id, formData);
      } else {
        await AdminApi.createBrand(formData);
      }
      await loadBrands();
      setShowModal(false);
    } catch (error) {
      console.error('Erreur sauvegarde marque:', error);
    } finally {
      setSaving(false);
    }
  };

  // Supprimer une marque
  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer cette marque ?')) return;

    setDeleting(id);
    try {
      await AdminApi.deleteBrand(id);
      await loadBrands();
    } catch (error) {
      console.error('Erreur suppression marque:', error);
    } finally {
      setDeleting(null);
    }
  };

  // Toggle actif/inactif
  const handleToggleActive = async (brand: Brand) => {
    try {
      await AdminApi.updateBrand(brand.id, { isActive: !brand.isActive });
      await loadBrands();
    } catch (error) {
      console.error('Erreur toggle marque:', error);
    }
  };

  return (
    <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-encre">Gestion des marques</h1>
          <p className="text-graphite mt-1">Base de connaissances - Fiches techniques par marque</p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une marque
        </Button>
      </div>

      {/* Liste des marques */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : brands.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-trait">
          <FolderOpen className="w-12 h-12 mx-auto text-trait mb-4" />
          <h3 className="text-lg font-medium text-encre">Aucune marque</h3>
          <p className="text-graphite mt-1">Commencez par ajouter une marque</p>
          <Button variant="primary" className="mt-4" onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter une marque
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((brand) => (
            <div
              key={brand.id}
              className={`bg-white rounded-lg border p-5 shadow-flottant transition-all ${
                brand.isActive ? 'border-trait' : 'border-trait opacity-60'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={brand.name}
                      className="w-12 h-12 object-contain rounded-lg border border-trait"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {brand.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-encre">{brand.name}</h3>
                    {!brand.isActive && (
                      <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                        Inactif
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(brand)}
                    className={`p-1.5 text-graphite/70 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex items-center justify-center ${CIBLE_TACTILE}`}
                    title="Modifier"
                    aria-label={`Modifier ${brand.name}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(brand.id)}
                    disabled={deleting === brand.id}
                    className={`p-1.5 text-graphite/70 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 inline-flex items-center justify-center ${CIBLE_TACTILE}`}
                    title="Supprimer"
                    aria-label={`Supprimer ${brand.name}`}
                  >
                    {deleting === brand.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {brand.description && (
                <p className="text-sm text-graphite mb-3 line-clamp-2">{brand.description}</p>
              )}

              <div className="flex flex-wrap gap-2">
                {brand.folderUrl && (
                  <a
                    href={brand.folderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    <FolderOpen className="w-4 h-4" />
                    Fiches techniques
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {brand.websiteUrl && (
                  <a
                    href={brand.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brume text-encre rounded-lg text-sm font-medium hover:bg-brume transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Site web
                  </a>
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-trait flex items-center justify-between">
                <span className="text-xs text-graphite/70">
                  Mis à jour le {formatDateParis(brand.updatedAt)}
                </span>
                <button
                  onClick={() => handleToggleActive(brand)}
                  className={`text-xs font-medium max-lg:min-h-10 max-lg:px-2 max-lg:-mr-2 ${
                    brand.isActive
                      ? 'text-green-600 hover:text-green-700'
                      : 'text-graphite hover:text-encre'
                  }`}
                >
                  {brand.isActive ? 'Actif' : 'Activer'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal création/édition */}
      {showModal && (
        <Fenetre>
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${FENETRE_FOND_MOBILE}`}>
          <div className="absolute inset-0 bg-encre/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div role="dialog" aria-modal="true" aria-label={editingBrand ? 'Modifier la marque' : 'Ajouter une marque'} className={`relative bg-white rounded-card shadow-flottant w-full max-w-lg overflow-hidden ${FENETRE_MOBILE}`}>
            <div className="px-6 py-4 max-sm:px-4 max-sm:py-3 border-b border-trait flex items-center justify-between">
              <h2 className="text-lg font-semibold text-encre">
                {editingBrand ? 'Modifier la marque' : 'Ajouter une marque'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                aria-label="Fermer"
                className={`p-2 text-graphite/70 hover:text-graphite hover:bg-brume rounded-lg transition-colors inline-flex items-center justify-center ${CIBLE_TACTILE}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 max-sm:p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-encre mb-1">
                  Nom de la marque *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 max-sm:text-base border border-trait rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Bosch, Siemens..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2.5 max-sm:text-base border border-trait rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  placeholder="Description optionnelle..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-1">
                  <FolderOpen className="w-4 h-4 inline mr-1" />
                  Lien dossier fiches techniques
                </label>
                <input
                  type="url"
                  value={formData.folderUrl}
                  onChange={(e) => setFormData({ ...formData, folderUrl: e.target.value })}
                  className="w-full px-4 py-2.5 max-sm:text-base border border-trait rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://sharepoint.com/... ou https://drive.google.com/..."
                />
                <p className="text-xs text-graphite mt-1">
                  Lien vers le dossier SharePoint, Google Drive ou autre contenant les fiches techniques
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-1">
                  URL du logo
                </label>
                <input
                  type="url"
                  value={formData.logoUrl}
                  onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  className="w-full px-4 py-2.5 max-sm:text-base border border-trait rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-encre mb-1">
                  Site web officiel
                </label>
                <input
                  type="url"
                  value={formData.websiteUrl}
                  onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                  className="w-full px-4 py-2.5 max-sm:text-base border border-trait rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://www.marque.com"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded border-trait focus:ring-blue-500"
                />
                <label htmlFor="isActive" className="text-sm text-encre">
                  Marque active (visible pour les utilisateurs)
                </label>
              </div>
            </div>

            <div className="px-6 py-4 max-sm:px-4 border-t border-trait flex flex-wrap justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingBrand ? 'Mettre à jour' : 'Créer'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        </Fenetre>
      )}
    </div>
  );
};

const SettingsView: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { user, refreshUser } = useAuth();
  const { refreshData } = useAdmin();

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    displayName: user?.displayName || '',
    phone: user?.phone || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const token = localStorage.getItem('kly_admin_access_token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000/api'}/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ displayName: profileForm.displayName, phone: profileForm.phone }),
      });
      if (!response.ok) throw new Error('Erreur lors de la mise à jour');
      // Refresh user data from backend
      await refreshUser();
      setProfileMessage({ type: 'success', text: 'Profil mis à jour avec succès' });
      refreshData();
    } catch (err) {
      setProfileMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erreur' });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 8 caractères' });
      return;
    }
    setPasswordSaving(true);
    setPasswordMessage(null);
    try {
      // POST /auth/change-password : les autres sessions sont révoquées et
      // les nouveaux jetons enregistrés ; le temps réel reprend avec eux.
      await changerMonMotDePasse(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordMessage({ type: 'success', text: 'Mot de passe changé. Vos autres sessions sont déconnectées.' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erreur lors du changement de mot de passe' });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4">
      <h1 className="text-xl font-bold text-encre">Préférences et SLA</h1>

      <div className="flex flex-col lg:flex-row gap-6">
        <Card className="w-full lg:w-64 p-4 max-lg:p-2 h-fit overflow-x-auto">
          <nav className="flex lg:flex-col gap-1 lg:gap-0 lg:space-y-1 min-w-max lg:min-w-0">
            {[
              { id: 'profile', label: 'Mon profil', icon: <User className="w-4 h-4" /> },
              { id: 'security', label: 'Sécurité', icon: <Shield className="w-4 h-4" /> },
              { id: 'general', label: 'Général', icon: <Settings className="w-4 h-4" /> },
              { id: 'sla', label: 'SLA', icon: <Timer className="w-4 h-4" /> },
              { id: 'reponses', label: 'Réponses types', icon: <MessageSquare className="w-4 h-4" /> },
            ].map(i => (
              <button key={i.id} onClick={() => setActiveTab(i.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === i.id ? 'bg-blue-50 text-blue-600' : 'text-graphite hover:bg-brume'}`}>
                {i.icon}{i.label}
              </button>
            ))}
          </nav>
        </Card>

        <Card className="flex-1 max-lg:min-w-0 p-6 max-sm:p-4">
          {activeTab === 'profile' && (
            <div className="space-y-6 max-w-lg">
              <h2 className="text-lg font-semibold">Mon profil</h2>

              {/* Avatar section */}
              <div className="flex items-center gap-4 max-sm:gap-3 p-4 max-sm:p-3 bg-brume rounded-lg">
                <div className="w-16 h-16 max-sm:w-12 max-sm:h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                  {user?.displayName?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="font-medium text-encre">{user?.displayName}</p>
                  <p className="text-sm text-graphite break-all">{user?.email}</p>
                  <Badge className="mt-1 bg-blue-100 text-blue-700">{ROLE_CONFIG[user?.role || '']?.label || user?.role}</Badge>
                </div>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-encre">Nom d'affichage</label>
                  <Input
                    value={profileForm.displayName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, displayName: e.target.value }))}
                    className="mt-2"
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-encre">Email</label>
                  <Input value={user?.email || ''} disabled readOnly className="mt-2 bg-brume text-graphite" />
                  <p className="text-xs text-graphite/70 mt-1">L'email ne peut pas être modifié</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-encre">Téléphone</label>
                  <Input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-2"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>

                {profileMessage && (
                  <div className={`p-3 rounded-lg flex items-center gap-2 ${profileMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {profileMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    <span className="text-sm">{profileMessage.text}</span>
                  </div>
                )}

                <Button type="submit" variant="primary" disabled={profileSaving}>
                  {profileSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6 max-w-lg">
              <h2 className="text-lg font-semibold">Sécurité</h2>

              <form onSubmit={handlePasswordSubmit} className="space-y-4 p-4 max-sm:p-3 bg-brume rounded-lg">
                <h3 className="font-medium text-encre flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Changer le mot de passe
                </h3>

                <div>
                  <label className="text-sm font-medium text-encre">Mot de passe actuel</label>
                  <div className="relative mt-2">
                    <Input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      placeholder="Entrez votre mot de passe actuel"
                    />
                    <button type="button" onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-graphite/70 hover:text-graphite max-lg:right-1 max-lg:p-2.5">
                      {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-encre">Nouveau mot de passe</label>
                  <div className="relative mt-2">
                    <Input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      placeholder="Minimum 8 caractères"
                    />
                    <button type="button" onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-graphite/70 hover:text-graphite max-lg:right-1 max-lg:p-2.5">
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-encre">Confirmer le mot de passe</label>
                  <div className="relative mt-2">
                    <Input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Répétez le nouveau mot de passe"
                    />
                    <button type="button" onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-graphite/70 hover:text-graphite max-lg:right-1 max-lg:p-2.5">
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {passwordMessage && (
                  <div className={`p-3 rounded-lg flex items-center gap-2 ${passwordMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {passwordMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    <span className="text-sm">{passwordMessage.text}</span>
                  </div>
                )}

                <Button type="submit" variant="primary" disabled={passwordSaving || !passwordForm.currentPassword || !passwordForm.newPassword}>
                  {passwordSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                  Changer le mot de passe
                </Button>
              </form>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2">Conseils de sécurité</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Utilisez un mot de passe unique d'au moins 8 caractères</li>
                  <li>• Combinez lettres, chiffres et caractères spéciaux</li>
                  <li>• Ne partagez jamais votre mot de passe</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <GeneralSettingsTab />
          )}

          {/* Délais lus et enregistrés en base (GET/PUT /admin/sla). L'onglet
              Notifications a été retiré : ses cases n'étaient reliées à rien. */}
          {activeTab === 'sla' && (
            <SlaSettingsTab />
          )}

          {activeTab === 'reponses' && (
            <GestionReponsesTypes />
          )}
        </Card>
      </div>
    </div>
  );
};

const PlaceholderView: React.FC<{ title: string }> = ({ title }) => (
  <div className="p-6 flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <div className="w-20 h-20 bg-brume rounded-card flex items-center justify-center mx-auto mb-4">
        <Cog className="w-10 h-10 text-graphite/70" />
      </div>
      <h2 className="text-xl font-semibold text-encre">{title}</h2>
      <p className="text-graphite mt-2">Cette fonctionnalité sera bientôt disponible</p>
    </div>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

const AdminV2: React.FC = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // App state
  const [vueChoisie, setVueChoisie] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fiche d'un dossier : page /dossiers/:id dans la mise en page de l'admin.
  // Les autres écrans restent à l'adresse racine, choisis par la barre latérale.
  const navigate = useNavigate();
  const location = useLocation();
  const surPageDossier = matchPath('/dossiers/:id', location.pathname) !== null;
  // Liste d'où vient la fiche : « Dossiers » ou « Archives » (Retour y ramène).
  const listeDOrigine = (location.state as { vue?: string } | null)?.vue === VUE_ARCHIVES ? VUE_ARCHIVES : 'tickets';
  // Sur la page d'un dossier, la barre latérale montre sa liste comme active.
  const currentView = surPageDossier ? listeDOrigine : vueChoisie;
  // État de la liste quittée pour une fiche (filtres, recherche, page).
  const memoireListe = useRef<EtatListeMemorise | null>(null);
  // Idem pour l'onglet Archives (recherche, période, page).
  const memoireArchives = useRef<EtatArchives | null>(null);

  // Ouverte depuis une liste, la fiche le note dans l'historique : « Retour aux
  // dossiers » revient alors en arrière au lieu d'empiler une nouvelle entrée.
  const ouvrirDossier = useCallback((id: string) => {
    const depuisListe = location.pathname === '/' && (vueChoisie === 'tickets' || vueChoisie === VUE_ARCHIVES);
    navigate(`/dossiers/${encodeURIComponent(id)}`, depuisListe ? { state: { depuisListe: true, vue: vueChoisie } } : undefined);
  }, [navigate, location.pathname, vueChoisie]);

  // Changer d'écran (barre latérale, liens) : quitte la page d'un dossier et
  // repart d'une liste neuve, comme avant.
  const setCurrentView = useCallback((vue: string) => {
    memoireListe.current = null;
    memoireArchives.current = null;
    setVueChoisie(vue);
    if (location.pathname !== '/') navigate('/');
  }, [location.pathname, navigate]);

  // « Retour aux dossiers » : la liste, avec les filtres, la recherche et la page quittés.
  // L'écran est fixé à la liste dans tous les cas : après un rechargement, ou
  // si un autre écran a été choisi entre-temps, l'entrée précédente « / »
  // afficherait sinon l'écran retenu en mémoire (vue d'ensemble).
  const retourALaListe = useCallback(() => {
    setVueChoisie((location.state as { vue?: string } | null)?.vue === VUE_ARCHIVES ? VUE_ARCHIVES : 'tickets');
    if ((location.state as { depuisListe?: boolean } | null)?.depuisListe) {
      navigate(-1);
      return;
    }
    navigate('/', { replace: true });
  }, [navigate, location.state]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  // App settings (persisted in localStorage)
  const [appTheme, setAppTheme] = useState<AppTheme>(() => {
    const saved = localStorage.getItem('kly_app_theme');
    return (saved as AppTheme) || 'blue';
  });
  const [appLanguage, setAppLanguage] = useState<AppLanguage>(() => {
    const saved = localStorage.getItem('kly_app_language');
    return (saved as AppLanguage) || 'fr';
  });

  // Persist theme and language changes
  useEffect(() => {
    localStorage.setItem('kly_app_theme', appTheme);
  }, [appTheme]);

  useEffect(() => {
    localStorage.setItem('kly_app_language', appLanguage);
  }, [appLanguage]);

  // Translation function
  const t = useCallback((key: string): string => {
    return TRANSLATIONS[appLanguage]?.[key] || TRANSLATIONS.fr[key] || key;
  }, [appLanguage]);

  // Data state
  const [tableauDeBord, setTableauDeBord] = useState<TableauDeBord>({ nonAssignes: [], nonAssignesTronque: false, derniers: [], charge: false });
  const [versionDossiers, setVersionDossiers] = useState(0);
  const [creationOuverte, setCreationOuverte] = useState(false);
  const [users, setUsers] = useState<UserType[]>([]);
  const [agents, setAgents] = useState<UserType[]>([]);
  const [pendingTicketFilter, setPendingTicketFilter] = useState<string | null>(null);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Notification state
  const seenNotifIds = useRef<Set<string>>(new Set());
  const [humanTakeoverAlert, setHumanTakeoverAlert] = useState<{ ticketId: string; ticketNumber: string; customerName: string } | null>(null);

  // Computed: unread notifications count
  const unreadNotifications = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);

  // Computed: map of ticketId -> unread count
  const unreadByTicket = useMemo(() => {
    const map = new Map<string, number>();
    notifications.filter(n => !n.isRead && n.ticketId).forEach(n => {
      const current = map.get(n.ticketId!) || 0;
      map.set(n.ticketId!, current + 1);
    });
    return map;
  }, [notifications]);

  const hasUnreadForTicket = useCallback((ticketId: string) => unreadByTicket.has(ticketId), [unreadByTicket]);

  const markNotificationsAsRead = useCallback(async (ids: string[]) => {
    try {
      await AdminApi.markNotificationsAsRead(ids);
      setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, isRead: true } : n));
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  }, []);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await AdminApi.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, []);

  const clearHumanTakeoverAlert = useCallback(() => { setHumanTakeoverAlert(null); }, []);

  // Check existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (TokenStorage.isAuthenticated()) {
        try {
          const user = await AdminApi.getCurrentUser();
          if (user.role !== UserRole.CUSTOMER) {
            setCurrentUser(user);
            setIsAuthenticated(true);
          } else {
            TokenStorage.clear();
          }
        } catch (error) {
          // Mot de passe à changer : la session est valable, l'écran plein s'affiche.
          const enregistre = TokenStorage.getUser();
          if (error instanceof ApiError && error.code === CODE_MOT_DE_PASSE_A_CHANGER && enregistre && enregistre.role !== UserRole.CUSTOMER) {
            setCurrentUser(enregistre);
            setIsAuthenticated(true);
          } else {
            console.error('Session invalide:', error);
            TokenStorage.clear();
          }
        }
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  // Après le changement de mot de passe obligatoire : nouveaux jetons, on reprend.
  useEffect(() => surRepriseApresMotDePasse(() => {
    adminSocketService.reconnecter();
    AdminApi.getCurrentUser().then(setCurrentUser).catch(() => {});
    refreshDataRef.current();
  }), []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshData();
    }
  }, [isAuthenticated]);

  // Socket connection for real-time updates
  useEffect(() => {
    if (!isAuthenticated) return;

    // Connect socket
    adminSocketService.connect();

    // Handle new notifications
    const handleNewNotification = (data: unknown) => {
      const incoming = data as Record<string, unknown>;
      const notifId = incoming.id as string;

      // Avoid duplicates
      if (seenNotifIds.current.has(notifId)) {
        console.log('[AdminSocket] Duplicate notification ignored:', notifId);
        return;
      }

      console.log('[AdminSocket] New notification received:', notifId, incoming);
      seenNotifIds.current.add(notifId);

      const notif: NotificationType = {
        id: notifId,
        type: incoming.type as NotificationType['type'],
        ticketId: incoming.ticketId as string | undefined,
        title: incoming.title as string | undefined,
        body: incoming.body as string | undefined,
        isRead: false,
        createdAt: incoming.createdAt as string || new Date().toISOString(),
      };

      setNotifications(prev => [notif, ...prev]);

      // Play notification sound
      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch {}
    };

    // Handle GPT to human transfer
    const handleHumanTakeover = (data: unknown) => {
      const incoming = data as { ticketId: string; ticketNumber: string; customerName?: string; message?: string };
      console.log('[AdminSocket] Human takeover alert:', incoming);

      setHumanTakeoverAlert({
        ticketId: incoming.ticketId,
        ticketNumber: incoming.ticketNumber || 'N/A',
        customerName: incoming.customerName || 'Client',
      });

      // Play alert sound
      try {
        const audio = new Audio('/alert.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch {}
    };

    // Un dossier a changé (création, modification, message, e-mail, fusion) :
    // le tableau de bord et la liste se rechargent, regroupés (≈ 1,5 s). La
    // fiche ouverte écoute le même événement pour se relire.
    // Les dossiers sans agent (lecture coûteuse) suivent leur propre limiteur.
    const rechargement = creerRegroupeur(DELAI_REGROUPEMENT_MS, () => {
      chargerTableauDeBordRef.current({ nonAssignes: false });
      setVersionDossiers(v => v + 1);
    });
    const rechargementNonAssignes = creerLimiteur(DELAI_NON_ASSIGNES_MS, () => {
      chargerNonAssignes()
        .then(r => setTableauDeBord(prev => ({ ...prev, nonAssignes: r.dossiers, nonAssignesTronque: r.tronque })))
        .catch(err => console.error('Erreur chargement non assignés:', err));
    });
    const handleDossierModifie = (data: unknown) => {
      const decision = decisionDossierModifie(data, null);
      if (decision.rechargerListes) rechargement.declencher();
      if (decision.rechargerNonAssignes) rechargementNonAssignes.declencher();
    };
    // Reconnexion après une coupure : les événements manqués sont perdus, on relit tout.
    const desabonnerReconnexion = adminSocketService.surReconnexion(() => {
      chargerTableauDeBordRef.current({ nonAssignes: true });
      setVersionDossiers(v => v + 1);
    });

    adminSocketService.on('notification', handleNewNotification);
    adminSocketService.on('admin:human_takeover', handleHumanTakeover);
    adminSocketService.on('dossier:modifie', handleDossierModifie);

    return () => {
      rechargement.annuler();
      rechargementNonAssignes.annuler();
      desabonnerReconnexion();
      adminSocketService.off('notification', handleNewNotification);
      adminSocketService.off('admin:human_takeover', handleHumanTakeover);
      adminSocketService.off('dossier:modifie', handleDossierModifie);
    };
  }, [isAuthenticated]);

  // Tableau de bord : ses propres requêtes (statistiques, dossiers non
  // assignés, derniers dossiers), indépendantes de la page de la liste.
  // Les dossiers sans agent (lecture de plusieurs pages) ne sont relus que
  // si on le demande : au chargement, à la reconnexion, et au plus une fois
  // par fenêtre de DELAI_NON_ASSIGNES_MS sur les événements qui les changent.
  const chargerTableauDeBord = useCallback(async (options: { nonAssignes?: boolean } = { nonAssignes: true }) => {
    const [statsResult, nonAssignesResult, derniersResult] = await Promise.allSettled([
      AdminApi.getTicketStats(),
      options.nonAssignes ? chargerNonAssignes() : Promise.resolve(null),
      chargerDerniersDossiers(5),
    ]);
    if (statsResult.status === 'fulfilled') setStats(statsResult.value);
    else console.error('Erreur chargement stats:', statsResult.reason);
    setTableauDeBord(prev => {
      const na = nonAssignesResult.status === 'fulfilled' ? nonAssignesResult.value : null;
      return {
        nonAssignes: na ? na.dossiers : prev.nonAssignes,
        nonAssignesTronque: na ? na.tronque : prev.nonAssignesTronque,
        derniers: derniersResult.status === 'fulfilled' ? derniersResult.value : prev.derniers,
        charge: true,
      };
    });
    if (nonAssignesResult.status === 'rejected') console.error('Erreur chargement non assignés:', nonAssignesResult.reason);
    if (derniersResult.status === 'rejected') console.error('Erreur chargement derniers dossiers:', derniersResult.reason);
  }, []);
  const chargerTableauDeBordRef = useRef(chargerTableauDeBord);
  chargerTableauDeBordRef.current = chargerTableauDeBord;

  const refreshData = useCallback(async () => {
    setDataLoading(true);
    try {
      // Use Promise.allSettled to handle individual failures gracefully
      // Personnel : rôles staff explicitement demandés (sans filtre, le backend
      // renvoie les 20 comptes les plus récents, clients compris).
      // Affectables : GET /admin/agents (personnel actif, avec sa charge).
      const [, usersResult, agentsResult, notifsResult] = await Promise.allSettled([
        chargerTableauDeBord(),
        AdminApi.getStaffUsers(),
        AdminApi.getAgents(),
        AdminApi.getNotifications(),
      ]);
      // La liste affichée recharge sa page.
      setVersionDossiers(v => v + 1);

      // Handle users
      if (usersResult.status === 'fulfilled') {
        setUsers(usersResult.value || []);
      } else {
        console.error('Erreur chargement users:', usersResult.reason);
      }

      // Handle agents (comptes techniques et inactifs retirés)
      if (agentsResult.status === 'fulfilled') {
        setAgents((agentsResult.value || []).filter(estAssignable));
      } else {
        console.error('Erreur chargement agents:', agentsResult.reason);
      }

      // Handle notifications
      if (notifsResult.status === 'fulfilled') {
        const notifs = notifsResult.value || [];
        seenNotifIds.current = new Set(notifs.map((n: NotificationType) => n.id));
        setNotifications(notifs);
      } else {
        console.error('Erreur chargement notifications:', notifsResult.reason);
        setNotifications([]);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setDataLoading(false);
    }
  }, [chargerTableauDeBord]);
  const refreshDataRef = useRef(refreshData);
  refreshDataRef.current = refreshData;

  const autoAssignTickets = useCallback(async () => {
    // Liste à jour des dossiers sans agent, lue au serveur (pas la page affichée).
    const unassigned = await chargerNonAssignes().then(r => r.dossiers).catch(() => tableauDeBord.nonAssignes);
    // Agents et superviseurs actifs, hors compte de l'IA et standard téléphonique.
    const candidats = agentsPourAutoAssignation(agents);

    if (unassigned.length === 0 || candidats.length === 0) return;

    // Chaque dossier va au moins chargé (dossiers actifs), charge mise à jour au fil du lot.
    const choix = repartirSurLeMoinsCharge(candidats, unassigned.length);
    for (let i = 0; i < unassigned.length; i++) {
      const ticket = unassigned[i];
      try {
        await AdminApi.updateTicket(ticket.id, { assignedToId: choix[i].id, status: TicketStatus.IN_PROGRESS });
      } catch (error) {
        console.error('Erreur assignation ticket:', ticket.id, error);
      }
    }

    // Refresh data after assignment
    await refreshData();
  }, [tableauDeBord, agents, refreshData]);

  const handleLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await AdminApi.login(email, password);
      if (response.user.role === UserRole.CUSTOMER) {
        TokenStorage.clear();
        throw new ApiError('Accès réservé au personnel autorisé.', 403, 'ACCESS_DENIED');
      }
      setCurrentUser(response.user);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      return false;
    }
  };

  const handleLogout = async () => {
    try {
      await AdminApi.logout();
    } catch (error) {
      console.error('Erreur logout:', error);
    }
    // Disconnect socket
    adminSocketService.disconnect();
    oublierChangementMotDePasse();
    seenNotifIds.current.clear();
    memoireListe.current = null;

    setCurrentUser(null);
    setIsAuthenticated(false);
    setTableauDeBord({ nonAssignes: [], nonAssignesTronque: false, derniers: [], charge: false });
    setUsers([]);
    setAgents([]);
    setStats(null);
    setNotifications([]);
    setHumanTakeoverAlert(null);
  };

  const handleRefreshUser = async () => {
    try {
      const user = await AdminApi.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Erreur refresh user:', error);
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!currentUser) return false;
    const permissions = ROLE_CONFIG[currentUser.role]?.permissions || [];
    return permissions.includes('*') || permissions.includes(permission);
  };

  const renderView = () => {
    switch (currentView) {
      case 'dashboard': return <DashboardView />;
      case 'tickets': return <TicketsView />;
      case VUE_ARCHIVES: return (
        <ArchivesView
          onOuvrirDossier={ouvrirDossier}
          version={versionDossiers}
          memoire={memoireArchives}
          surChangement={refreshData}
          onVoirDossiers={() => setCurrentView('tickets')}
        />
      );
      case 'clients': return <ClientsListView />;
      case 'team': return <TeamView />;
      case 'automation': return <AutomationView />;
      case 'analytics': return <AnalyticsView />;
      case 'brands': return <BrandsView />;
      case 'connecteurs': return <ConnecteursView onOuvrirDossier={ouvrirDossier} onCourrierEcarte={() => setCurrentView('courrier-ecarte')} />;
      case 'courrier-ecarte': return <CourrierEcarteView onOuvrirDossier={ouvrirDossier} onBoitesMail={hasPermission('settings') ? () => setCurrentView('connecteurs') : undefined} />;
      case 'telephonie': return <TelephonieView onOuvrirDossier={ouvrirDossier} onParametres={() => setCurrentView('parametres')} />;
      case 'parametres': return <ParametresView onOuvrirDossier={ouvrirDossier} />;
      case 'settings': return <SettingsView />;
      default: return <PlaceholderView title={currentView.charAt(0).toUpperCase() + currentView.slice(1)} />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brume">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-500 rounded-card flex items-center justify-center mx-auto mb-4 shadow-flottant">
            <Wrench className="w-8 h-8 text-white" />
          </div>
          <p className="text-graphite font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPageV2 onLogin={handleLogin} />;
  }

  return (
    <AuthContext.Provider value={{ user: currentUser, isAuthenticated, login: handleLogin, logout: handleLogout, hasPermission, refreshUser: handleRefreshUser }}>
      <AdminContext.Provider value={{
        currentView, setCurrentView,
        ouvrirDossier, memoireListe,
        sidebarOpen, setSidebarOpen,
        sidebarCollapsed, setSidebarCollapsed,
        notifications,
        unreadNotifications,
        unreadByTicket,
        hasUnreadForTicket,
        markNotificationsAsRead,
        markAllNotificationsAsRead,
        humanTakeoverAlert,
        clearHumanTakeoverAlert,
        showAIAssistant, setShowAIAssistant,
        tableauDeBord,
        versionDossiers,
        ouvrirCreation: () => setCreationOuverte(true),
        users,
        agents,
        pendingTicketFilter, setPendingTicketFilter,
        stats,
        refreshData,
        isLoading: dataLoading,
        autoAssignTickets,
        appTheme, setAppTheme,
        appLanguage, setAppLanguage,
        t
      }}>
        {/* 100dvh quand le navigateur la connaît : sur téléphone, 100vh passe
            sous la barre d'adresse et cache le bas de l'écran. */}
        <div className="flex h-screen supports-[height:100dvh]:h-dvh bg-papier">
          <Sidebar />
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <Header />
            <main className="flex-1 overflow-auto">
              <Routes>
                {/* Fiche d'un dossier, adressable : rechargement et lien partagé la rouvrent. */}
                <Route path="dossiers/:id" element={<div className="anim-page"><PageDossier Fiche={FicheDossier} surRetour={retourALaListe} /></div>} />
                <Route path="*" element={<div key={currentView} className="anim-page">{renderView()}</div>} />
              </Routes>
            </main>
          </div>
          <AIAssistant isOpen={showAIAssistant} onClose={() => setShowAIAssistant(false)} />

          {/* Nouveau dossier (bouton « Nouveau » de la liste et du tableau de bord) */}
          {creationOuverte && (
            <NouveauDossierModal
              onClose={() => setCreationOuverte(false)}
              onCree={(dossier) => {
                setCreationOuverte(false);
                ouvrirDossier(dossier.id);
                chargerTableauDeBord();
                setVersionDossiers(v => v + 1);
              }}
            />
          )}

          {/* Human Takeover Alert Modal */}
          {humanTakeoverAlert && (
            <div role="dialog" aria-modal="true" aria-label="Prise en charge requise" className="fixed inset-0 z-[100] flex items-center justify-center max-sm:p-4">
              <div className="absolute inset-0 bg-encre/50 backdrop-blur-sm animate-in fade-in" onClick={clearHumanTakeoverAlert} />
              <div className="relative bg-white rounded-card shadow-flottant w-full max-w-md p-6 max-sm:p-4 animate-in zoom-in-95 fade-in">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-encre">Prise en charge requise</h3>
                    <p className="text-sm text-graphite">L'IA transfère ce dossier</p>
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-200">
                  <p className="text-sm text-orange-800">
                    <strong>{humanTakeoverAlert.customerName}</strong> a besoin d'une assistance humaine sur le dossier <strong>#{humanTakeoverAlert.ticketNumber}</strong>.
                  </p>
                  <p className="text-xs text-orange-600 mt-2">L'assistant IA a déterminé qu'un opérateur doit prendre le relais.</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="primary"
                    className="flex-1 bg-orange-500"
                    onClick={() => {
                      // La page du dossier le demande au serveur et dit s'il
                      // n'est pas accessible.
                      ouvrirDossier(humanTakeoverAlert.ticketId);
                      clearHumanTakeoverAlert();
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Prendre en charge
                  </Button>
                  <Button variant="secondary" onClick={clearHumanTakeoverAlert}>
                    Plus tard
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AdminContext.Provider>
    </AuthContext.Provider>
  );
};

export default AdminV2;

/**
 * Écrans exposés aux tests de composants (tests/unit/ecrans-dossiers.test.tsx) :
 * ils se rendent avec les contextes ci-dessous, sans monter toute l'application.
 */
export const pourTests = { AuthContext, AdminContext, TicketsView, FicheDossier, SettingsView, ClientsListView, DashboardView, Sidebar };
